import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Extension } from '@tiptap/core';
import Color from '@tiptap/extension-color';
import ImageExtension from '@tiptap/extension-image';
import LinkExtension from '@tiptap/extension-link';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import { EditorContent, useEditor } from '@tiptap/react';
import {
    DndContext,
    DragEndEvent,
    DragMoveEvent,
    DragStartEvent,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    arrayMove,
    horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
// import type { Trade } from '../../types'; // Uncomment if using your shared types
import {
    AlignCenter,
    AlignLeft,
    AlignRight,
    Bold,
    Plus,
    Edit2,
    Edit3,
    X as XIcon,
    Eye,
    Trash2,

    Info,
    Italic,
    Calendar,
    Clock,
    ChevronDown,
    DollarSign,
    BarChart3,
    Image as ImageIcon,
    Link,
    List,
    ListOrdered,
    Redo,
    Strikethrough,
    Tag,
    Settings,
    Underline,
    Undo,
    ArrowLeft,
    ArrowUp,
    ArrowDown,
    ArrowRight,
    ArrowUpRight,
    ArrowDownRight,
    TrendingUp,
    TrendingDown,
    Layers2
} from 'lucide-react';
import { Download } from 'lucide-react';
import { currentRuntime, isFeatureEnabled } from '../../config/runtime';
import { readPrivateTradingJson, removePrivateTradingValue, writePrivateTradingJson } from '../../lib/storage/privateTradingStorage';

import { loadImportedTrades as loadImportedTradesAuth, saveImportedTrades as saveImportedTradesAuth } from '../../utils/importedTradesStorage';
import { getNyDayStartMs } from '../../utils/formatters';
import TradeReplayChart, { isDemoTrade } from './TradeReplayChart';
import { contractSymbolToYyyymm, inferInstrumentType, sanitizeSymbolForChart } from './tradeDetailsUtils';
import RemovableTag from '../ui/RemovableTag';
import TradeExcursionWidget from '../widgets/TradeExcursionWidget';
import TradeInsightCard from '../widgets/TradeInsightCard';
import { getVisibleTradeTags } from '../../lib/tradeTags';
import TooltipSimple from '../ui/TooltipSimple';
import type { AssetClass } from '../../lib/time/SessionResolver.ts';
import { normalizeTimestampToMs } from '../../lib/time/normalizeTimestampToMs';
import { formatChartTime } from '../../lib/chart/displayOptions';
import { formatDateTimeInTimezone } from '../../lib/time/displayTimezone';
import { formatDateTimeInputInTimezone } from '../../lib/time/displayTimezone';
import { resolveSourceTimestamp } from '../../lib/time/sourceTimestamp';
import { useDisplayTimezone } from '../app/DisplayTimezoneProvider';
import CreateCustomEntryModal, { type ManualEntryPayload } from '../ui/CreateCustomEntryModal';
import { listCsvSourceAccounts } from '../../lib/import-engine/csvImportPersistenceSync';
import { loadPersistedCsvTrades } from '../../lib/import-engine/persistedCsvTradeRead';
import { updateManualTrade, updateTradeInitialRisk } from '../../lib/trades/manualTradeApi';
import { getTradeDetailsCompanyName, resolveTradeDetailsCompanyName } from '../../lib/trades/tradeDetailsCompanyName';
import { computeDirectionalTicks, getContractMultiplier, getForexQuoteCurrency, resolveFuturesTickSize } from '../../constants/contractMultipliers';
import { formatPriceForSymbol } from '../../lib/chart/priceFormat';
import { parseTradeQuantityValue } from '../../lib/forex/quantity';
import { resolveExecutionRows, type ExecutionRowAction } from '../../lib/trades/resolveExecutionRows';
import { normalizeTradeStatus } from '../../lib/trades/authoritativeTradeData';
import { buildTradeDetailsDeterministicSummary } from '../../lib/trades/tradeDetailsDeterministicAdapter';
import { computeDeterministicPositionState } from '../../lib/trades/deterministicPrimitives';
import type { MetricBar, MetricExecution } from '../../lib/trades/deterministicTradeMetrics';
import { getExecutionStructure, hasCoherentTradeInsightBars, inferTradeInsightTimestampResolutionMs } from '../../lib/trades/tradeInsight';
import {
    getReplayBlockedMessage,
    mapSecTypeToLaunchAssetClass,
    parseReplayLaunchControlsStatus,
    fetchReplayLaunchControlsStatus,
    type ReplayLaunchControlsStatus,
} from '../../lib/replay/launchControls';
import { useReplayChartVisibility } from '../../lib/replayChartVisibility';
import { formatTradeDetailsPnlDisplay } from './tradeDetailsPnl';
import * as pnlMode from '../../utils/pnlMode';
import { useDemoTradesOverride } from '../../demo/demoMode';
import { generateDashboardDemoTrades } from '../../demo/demoData';
import { normalizeTradeForDetails } from '../../lib/trades/tradeDetailsNormalization';
import { resolveTradeDetailsFinancialSummary } from '../../lib/trades/tradeDetailsFinancialSummary';
import { computeInitialRiskFromStop, type InitialRiskMethod, type StopRiskAssetClass } from './initialRisk';

/* eslint-disable @typescript-eslint/no-explicit-any */

interface Trade {
    id?: string;
    date?: Date | string | number;
    symbol: string;
    side?: string;
    volume?: number;
    pnl?: number;
    duration?: number;
    commission?: number;
    commissionAndFees?: number;
    fees?: number;
    notes?: string;
    tags?: string[];
    entryTime?: number | string;
    openTime?: number | string;
    timestamp?: number | string;
    openTimestampMs?: number;
    closeTimestampMs?: number | null;
}

// --- MOCKS FOR EXTERNAL DEPENDENCIES ---

export type ImportedTradeRecord = {
                                    storageId: string;
                                    key?: string | number;
                                    importBatchId?: string;
                                    importedAt?: number;
                                    timestamp: number;
                                    openTimestampMs?: number;
                                    closeTimestampMs?: number | null;
                                    symbol: string;
                                    company?: string;
                                    price?: number;
                                    avgPrice?: number;
                                    volume?: number;
                                    shares?: number;
                                    execSide?: 'buy' | 'sell';
                                    side?: 'Long' | 'Short' | string;
                                    pnl?: number;
                                    fees?: number;
                                    commission?: number;
                                    commissionAndFees?: number;
                                    durationMinutes?: number;
                                    notes?: string;
                                    action?: ExecutionRowAction;
                                    positionAfter?: number;
                                    closedQty?: number;
                                    rowResolvedQty?: number;
                                    rowRealizedGross?: number;
                                    rowRealizedNet?: number;
                                    rowFees?: number;
                                    fees_present?: boolean;
                                    commission_present?: boolean;
                                    commissionAndFees_present?: boolean;
                                };

type TradeRecord = Partial<Trade> & Record<string, unknown> & {
    key?: string | number;
    id?: string | number;
    storageId?: string;
    tradeId?: string | number;
    symbol?: string;
    side?: string;
    timestamp?: number | string;
    volume?: number;
    price?: number;
    avgPrice?: number;
    action?: string;
    position?: number;
    positionAfter?: number;
    durationMinutes?: number;
    commission?: number;
    commissionAndFees?: number;
    fees?: number;
    notes?: string;
    tags?: string[];
    openTimestampMs?: number | null;
    closeTimestampMs?: number | null;
    openTime?: number | string | null;
    closeTime?: number | string | null;
    rowRealizedGross?: number;
    rowRealizedNet?: number;
    row_realized_gross?: number;
    row_realized_net?: number;
    deterministicMfe?: number;
    deterministicMae?: number;
    initialRisk?: number;
    initial_risk?: number;
    riskAmount?: number;
    secType?: string;
    sec_type?: string;
    instrumentType?: string;
    assetClass?: string;
    fees_present?: boolean;
    commission_present?: boolean;
    commissionAndFees_present?: boolean;
    rowFees?: number;
    row_fees?: number;
    commissionsAndFees?: number;
    closeDate?: unknown;
    exitDate?: unknown;
    exitTimestampMs?: number | null;
};

type TableColumn = {
    id: string;
    label: string;
    locked?: boolean;
    badge?: { text: string; tooltip: string };
};

type ExecutionForLabel = {
    time: string;
    symbol: string;
    quantity: number;
    key?: string | number;
    rowIndex?: number;
};

type LabeledExecution = ExecutionForLabel & {
    positionAfter: number;
    label: 'OPEN LONG' | 'OPEN SHORT' | 'ADDING' | 'SUBTRACTING' | 'CLOSED LONG' | 'CLOSED SHORT';
};

                                let globalTrades: ImportedTradeRecord[] = [];
                                const globalDailyNotes: Record<string, string> = {};
                                const globalDailyTags: Record<string, string[]> = {};

                                const listeners = new Set<() => void>();

                                function notify() {
                                    listeners.forEach((listener) => listener());
                                }

                                function useImportedTradesRecords() {
                                    const [trades, setTrades] = useState(globalTrades);

                                    useEffect(() => {
                                        const onChange = () => setTrades([...globalTrades]);
                                        listeners.add(onChange);
                                        return () => {
                                            listeners.delete(onChange);
                                        };
                                    }, []);

                                    return trades;
                                }

                                function saveImportedTrades(newTrades: ImportedTradeRecord[], options?: { append: boolean }) {
                                    if (options?.append) {
                                        const existingIds = new Set(globalTrades.map((trade) => trade.storageId));
                                        const uniqueNewTrades = newTrades.filter((trade) => !existingIds.has(trade.storageId));
                                        globalTrades = [...globalTrades, ...uniqueNewTrades];
                                    } else {
                                        globalTrades = newTrades;
                                    }

                                    notify();
                                    return { success: true, added: newTrades.length, error: undefined };
                                }

                                function deleteImportedTrade(storageId: string) {
                                    globalTrades = globalTrades.filter((trade) => trade.storageId !== storageId);
                                    notify();
                                }

                                function updateImportedTrade(updatedTrade: ImportedTradeRecord) {
                                    globalTrades = globalTrades.map((trade) => trade.storageId === updatedTrade.storageId ? updatedTrade : trade);
                                    notify();
                                }

                                function useDailyNotes(date: Date | null) {
                                    const getKey = (d: Date) => getStartOfDayLocal(d).toString();
                                    const [note, setNote] = useState('');

                                    useEffect(() => {
                                        if (!date) return;
                                        const key = getKey(date);
                                        setNote(globalDailyNotes[key] || '');
                                    }, [date]);

                                    const saveNote = (newText: string) => {
                                        if (!date) return;
                                        const key = getKey(date);
                                        globalDailyNotes[key] = newText;
                                        setNote(newText);
                                    };

                                    return { note, saveNote };
                                }

                                function useDailyTags(date: Date | null) {
                                    const getKey = (d: Date) => getStartOfDayLocal(d).toString();
                                    const [tags, setTags] = useState<string[]>([]);

                                    useEffect(() => {
                                        if (!date) return;
                                        const key = getKey(date);
                                        setTags(globalDailyTags[key] || []);
                                    }, [date]);

                                    const addTag = (tag: string) => {
                                        if (!date) return;
                                        const key = getKey(date);
                                        const current = globalDailyTags[key] || [];
                                        if (current.includes(tag)) return;
                                        const next = [...current, tag];
                                        globalDailyTags[key] = next;
                                        setTags(next);
                                    };

                                    const removeTag = (tag: string) => {
                                        if (!date) return;
                                        const key = getKey(date);
                                        const current = globalDailyTags[key] || [];
                                        const next = current.filter((value) => value !== tag);
                                        globalDailyTags[key] = next;
                                        setTags(next);
                                    };

                                    return { tags, addTag, removeTag };
                                }

// --- END MOCKS ---

const FALLBACK_DATE_LABEL = 'Monday, Dec 15, 2025';
const FORCE_NOTES_FIRST = false;

const isDarkTheme = () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

function getStartOfDayLocal(d: Date) {
    return getNyDayStartMs(d);
}

function pnlOfTrade(t: ImportedTradeRecord) {
    return Number.isFinite(t.pnl) ? Number(t.pnl) : 0;
}

const MISSING_VALUE = String.fromCharCode(0x2014);

function fmtMoney(amount: number) {
    const safe = Number.isFinite(amount) ? Number(amount) : 0;
    return safe.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function fmtTime24hWithSeconds(ms: number | string | null | undefined, tzMode: string) {
    const numericMs = Number(ms);
    if (!Number.isFinite(numericMs)) return MISSING_VALUE;
    const dt = new Date(numericMs);
    if (!Number.isFinite(dt.getTime())) return MISSING_VALUE;
    return formatDateTimeInTimezone(numericMs, tzMode).timeStr;
}

type MarketCategory = 'STOCKS' | 'FUTURES' | 'FOREX' | 'CRYPTO';

function normalizeMarketCategory(raw: unknown): MarketCategory | undefined {
    const v = String(raw ?? '').trim().toUpperCase();
    if (!v) return undefined;
    if (['STOCK', 'STOCKS', 'EQUITY', 'EQUITIES', 'STK'].includes(v)) return 'STOCKS';
    if (['FUTURE', 'FUTURES', 'FUT', 'DERIVATIVE', 'DERIVATIVES'].includes(v)) return 'FUTURES';
    if (['FOREX', 'FX', 'CURRENCY', 'CURRENCIES'].includes(v)) return 'FOREX';
    if (['CRYPTO', 'CRYPTOCURRENCY', 'CRYPTOCURRENCIES', 'DIGITAL_ASSET', 'DIGITAL_ASSETS'].includes(v)) return 'CRYPTO';
    return undefined;
}

function detectMarketCategory(input: {
    symbol?: unknown;
    market?: unknown;
    marketType?: unknown;
    assetClass?: unknown;
    category?: unknown;
    instrumentType?: unknown;
    instrument?: unknown;
    securityType?: unknown;
}): MarketCategory {
    const sym = String(input.symbol || '').trim().toUpperCase();
    const compact = sym.replace(/[\s._-]/g, '');
    const noVenue = compact.includes(':') ? compact.split(':').pop() || compact : compact;
    const isFxPair = /^[A-Z]{3}\/[A-Z]{3}$/.test(noVenue);
    const slashParts = noVenue.split('/').filter(Boolean);
    const normalizedContract = !isFxPair && slashParts.length > 0 ? slashParts[0] : noVenue;
    const futuresRoots = new Set(['ES', 'MES', 'NQ', 'MNQ', 'RTY', 'M2K', 'YM', 'MYM', 'CL', 'GC', 'SI', 'HG', 'NG', 'ZB', 'ZN', 'ZF', 'ZT', '6A', '6B', '6C', '6E', '6J', '6N', '6S']);
    const cryptoRoots = new Set(['BTC', 'ETH', 'LTC', 'XRP', 'DOGE', 'SOL', 'ADA', 'BNB']);
    const fxCodes = new Set(['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD', 'SEK', 'NOK', 'DKK', 'HKD', 'SGD', 'CNH', 'CNY', 'MXN', 'ZAR', 'TRY', 'PLN', 'HUF', 'CZK', 'ILS', 'AED', 'SAR']);
    const explicitStrong =
        normalizeMarketCategory(input.assetClass) ??
        normalizeMarketCategory(input.category) ??
        normalizeMarketCategory(input.instrumentType) ??
        normalizeMarketCategory(input.instrument) ??
        normalizeMarketCategory(input.securityType);
    if (explicitStrong) return explicitStrong;

    if (sym) {
        if (sym.includes(':')) {
            if (/CME|NYMEX|CBOT|COMEX/i.test(sym)) return 'FUTURES';
            if (/NASDAQ|NYSE|AMEX/i.test(sym)) return 'STOCKS';
        }
        if (futuresRoots.has(normalizedContract)) return 'FUTURES';
        if (/^(ES|MES|NQ|MNQ|RTY|M2K|YM|MYM|CL|GC|SI|HG|NG|ZB|ZN|ZF|ZT|6A|6B|6C|6E|6J|6N|6S)[FGHJKMNQUVXZ]\d{1,2}$/.test(normalizedContract)) return 'FUTURES';
        if (/^[A-Z]{1,6}[FGHJKMNQUVXZ]\d{1,4}$/.test(normalizedContract)) return 'FUTURES';
        if (/^\d[A-Z]([FGHJKMNQUVXZ]\d{1,2})?$/.test(normalizedContract)) return 'FUTURES';
        if (cryptoRoots.has(normalizedContract) || /^(BTC|ETH|LTC|XRP|DOGE|SOL|ADA|BNB)\b/.test(normalizedContract)) return 'CRYPTO';
        if (isFxPair) {
            const parts = noVenue.split('/').filter(Boolean);
            const baseCode = parts[0] ?? '';
            const quoteCode = parts[1] ?? '';
            if (cryptoRoots.has(baseCode) || cryptoRoots.has(quoteCode)) return 'CRYPTO';
            if (explicitStrong && explicitStrong !== 'FOREX') return explicitStrong;
            return 'FOREX';
        }
        if (/^[A-Z]{6}$/.test(normalizedContract)) {
            const base = normalizedContract.slice(0, 3);
            const quote = normalizedContract.slice(3);
            if (cryptoRoots.has(base) || cryptoRoots.has(quote)) return 'CRYPTO';
            if (fxCodes.has(base) && fxCodes.has(quote)) return 'FOREX';
            if (quote === 'USD') return 'FOREX';
        }
    }

    const explicitWeak =
        normalizeMarketCategory(input.marketType) ??
        normalizeMarketCategory(input.market);
    if (explicitWeak) return explicitWeak;
    return 'STOCKS';
}

function getTradeMarketCategory(trade: any): MarketCategory {
    return detectMarketCategory({
        symbol: trade?.symbol,
        market: trade?.market,
        marketType: trade?.marketType,
        assetClass: trade?.assetClass,
        category: trade?.category,
        instrumentType: trade?.instrumentType,
        instrument: trade?.instrument,
        securityType: trade?.securityType,
    });
}

function formatTradePriceValue(value: unknown, trade: any): string {
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value ?? '');
    const market = getTradeMarketCategory(trade);
    const secType = market === 'FUTURES' ? 'FUTURES' : market === 'FOREX' ? 'CASH' : market === 'CRYPTO' ? 'CRYPTO' : undefined;
    return formatPriceForSymbol(num, String(trade?.symbol ?? ''), secType);
}

function fmtDurationMinutes(durationMinutes?: number) {
    if (!Number.isFinite(durationMinutes)) return MISSING_VALUE;
    const mins = Math.max(0, Math.round(Number(durationMinutes)));
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
}

type ExecutionPnlMode = 'NET' | 'GROSS';

function resolveExecutionCosts(row: any): number {
    const directRowFees = Number(
        row?.rowFees
        ?? row?.row_fees
        ?? row?.commissionAndFees
        ?? row?.commissionsAndFees
    );
    if (Number.isFinite(directRowFees)) return directRowFees;

    const fees = Number(row?.fees);
    const commission = Number(row?.commission);
    return (Number.isFinite(fees) ? fees : 0) + (Number.isFinite(commission) ? commission : 0);
}

function resolveExecutionPnlForMode(row: any, mode: ExecutionPnlMode): number {
    const directGross = Number(
        row?.rowRealizedGross
        ?? row?.row_realized_gross
        ?? row?.realizedGross
        ?? row?.realized_gross
        ?? row?.grossPnl
    );
    const directNet = Number(
        row?.rowRealizedNet
        ?? row?.row_realized_net
        ?? row?.realizedNet
        ?? row?.realized_net
        ?? row?.netPnl
        ?? row?.realizedPnl
        ?? row?.pnl
    );

    const fees = resolveExecutionCosts(row);
    const hasGross = Number.isFinite(directGross);
    const hasNet = Number.isFinite(directNet);

    const gross = hasGross ? directGross : (hasNet ? directNet + fees : NaN);
    const net = hasNet ? directNet : (hasGross ? directGross - fees : NaN);

    return mode === 'GROSS' ? gross : net;
}

function computePeakOpenPosition(rows: any[]): number {
    if (!Array.isArray(rows) || rows.length === 0) return 0;

    const explicitPositionAfter = rows
        .map((row) => Number(row?.positionAfter ?? row?.position_after ?? row?.netPositionAfter))
        .filter((value) => Number.isFinite(value));

    if (explicitPositionAfter.length > 0) {
        return explicitPositionAfter.reduce((maxAbs, value) => Math.max(maxAbs, Math.abs(value)), 0);
    }

    const chronologicRows = [...rows].sort((a, b) => {
        const aTs = Number(a?.timestamp ?? a?.openTimestampMs ?? a?.time ?? 0);
        const bTs = Number(b?.timestamp ?? b?.openTimestampMs ?? b?.time ?? 0);
        return (Number.isFinite(aTs) ? aTs : 0) - (Number.isFinite(bTs) ? bTs : 0);
    });

    let runningPosition = 0;
    let peakAbsPosition = 0;
    for (const row of chronologicRows) {
        const signedQty = Number(row?.volume ?? row?.signedQty ?? row?.qty ?? row?.quantity ?? row?.shares ?? 0);
        if (!Number.isFinite(signedQty) || signedQty === 0) continue;
        runningPosition += signedQty;
        peakAbsPosition = Math.max(peakAbsPosition, Math.abs(runningPosition));
    }

    return peakAbsPosition;
}

function getTradeInsightRetention(trade: any, mode: 'NET' | 'GROSS'): number | null {
    const normalized = normalizeTradeForDetails(trade);
    const pnl = mode === 'GROSS' ? normalized.grossPnl : normalized.netPnl;
    const mfe = mode === 'GROSS' ? normalized.mfeGross : normalized.mfeNet;
    if (pnl == null || mfe == null || !Number.isFinite(pnl) || !Number.isFinite(mfe) || mfe <= 0) return null;
    return pnl / mfe;
}

function getTradeInsightComparableId(trade: any): string {
    return String(trade?.id ?? trade?.storageId ?? trade?.tradeId ?? '');
}

function getTradeInsightTextKey(...values: unknown[]): string | null {
    for (const value of values) {
        const text = String(value ?? '').trim().toLowerCase();
        if (text) return text;
    }
    return null;
}

function getTradeInsightOwnerScope(trade: any): string | null {
    const user = getTradeInsightTextKey(trade?.userId, trade?.ownerId, trade?.accountUserId, trade?.user?.id);
    const tenant = getTradeInsightTextKey(trade?.tenantId, trade?.workspaceId, trade?.demoTenantId, trade?.tenant?.id);
    if (user || tenant) return `${user ?? '*'}::${tenant ?? '*'}`;
    return null;
}

function getTradeInsightSetupKey(trade: any): string | null {
    return getTradeInsightTextKey(trade?.setup, trade?.strategy, trade?.playbook, Array.isArray(trade?.tags) ? trade.tags[0] : null);
}

function getTradeInsightDirectionKey(trade: any): string | null {
    return normalizeTradeForDetails(trade).side;
}

function getTradeInsightInstrumentKey(trade: any): string | null {
    return normalizeTradeForDetails(trade).assetClass;
}

function isTradeInsightClosedTrade(trade: any): boolean {
    return normalizeTradeStatus(trade) === 'closed' && !(trade?.deleted || trade?.isDeleted || trade?.deletedAt);
}

function isSameTradeInsightScope(current: any, candidate: any): boolean {
    const currentScope = getTradeInsightOwnerScope(current);
    const candidateScope = getTradeInsightOwnerScope(candidate);
    if (currentScope != null || candidateScope != null) return currentScope === candidateScope;
    const currentDemo = Boolean(current?.isDemo || current?.sourceType === 'demo' || current?.demoTenantId);
    const candidateDemo = Boolean(candidate?.isDemo || candidate?.sourceType === 'demo' || candidate?.demoTenantId);
    return currentDemo === candidateDemo;
}

function isSameTradeInsightProfile(current: any, candidate: any): boolean {
    const setup = getTradeInsightSetupKey(current);
    const candidateSetup = getTradeInsightSetupKey(candidate);
    const side = getTradeInsightDirectionKey(current);
    const candidateSide = getTradeInsightDirectionKey(candidate);
    const instrument = getTradeInsightInstrumentKey(current);
    const candidateInstrument = getTradeInsightInstrumentKey(candidate);
    return setup != null
        && candidateSetup === setup
        && side != null
        && candidateSide === side
        && instrument != null
        && candidateInstrument === instrument;
}

function getTradeInsightComparableSource(current: any, source: any[]): any[] {
    const currentId = getTradeInsightComparableId(current);
    return source
        .filter((candidate) => getTradeInsightComparableId(candidate) !== currentId)
        .filter(isTradeInsightClosedTrade)
        .filter((candidate) => isSameTradeInsightScope(current, candidate));
}

const ALL_COLUMNS: TableColumn[] = [
    { id: 'time', label: 'Time' },
    { id: 'price', label: 'Price' },
    { id: 'volume', label: 'Qty' },
    { id: 'side', label: 'Side' },
    { id: 'action', label: 'Action' },
    { id: 'position', label: 'Pos After' },
    { id: 'duration', label: 'Duration' },
    { id: 'commissionAndFees', label: 'Commissions & Fees' },
    { id: 'pnl', label: 'Realized P&L', badge: { text: 'NET', tooltip: 'Net realized P&L after commissions & fees.' } },
    { id: 'controls', label: 'Controls' },
];

const DEFAULT_COLUMNS = ALL_COLUMNS.map((c) => c.id);

const TooltipIcon = ({ text, offsetX }: { text?: string; offsetX?: number }) => {
    if (!text) return null;
    const dark = isDarkTheme();
    return (
        <TooltipSimple text={text} offsetX={offsetX}>
            <div className="cursor-help flex items-center justify-center" aria-label={text}>
                <Info className={dark ? "w-3.5 h-3.5 text-zinc-500 hover:text-zinc-400 transition-colors" : "w-3.5 h-3.5 text-zinc-400 hover:text-zinc-500 transition-colors"} />
            </div>
        </TooltipSimple>
    );
};

const InstantTooltip = ({ children, content }: { children: React.ReactNode; content: string }) => (
    <TooltipSimple text={content}>
        <div className="flex items-center justify-center" aria-label={content}>
            {children}
        </div>
    </TooltipSimple>
);

const TagsModal = ({ isOpen, onClose, tags, onAdd, onRemove }: { isOpen: boolean; onClose: () => void; tags: string[]; onAdd: (t: string) => void; onRemove: (t: string) => void }) => {
    const [inputValue, setInputValue] = useState('');

    const handleAdd = (e?: React.FormEvent) => {
        e?.preventDefault();
        const trimmed = inputValue.trim();
        if (trimmed) {
            onAdd(trimmed);
            setInputValue('');
        }
    };

    const suggestions = ['Trend Day', 'Choppy', 'FOMC', 'Reversal', 'Breakout', 'Range Bound', 'High Volatility', 'Low Volume', 'Long Term'];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-sm bg-white dark:bg-[#1a1a1c] p-6 rounded-2xl shadow-2xl border border-zinc-200 dark:border-zinc-800">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">Manage Daily Tags</h3>
                </div>

                <form onSubmit={handleAdd} className="flex gap-2 mb-4">
                    <input
                        className="flex-1 px-3 py-2 rounded-lg bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 text-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                        placeholder="Add a tag..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        autoFocus
                    />
                    <button type="submit" className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
                        <Plus className="w-5 h-5" />
                    </button>
                </form>

                <div className="mb-4">
                    <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-2">Active Tags</div>
                    <div className="flex flex-wrap gap-2 min-h-[40px]">
                        {tags.length === 0 ? (
                            <span className="text-sm text-zinc-400 italic">No tags selected</span>
                        ) : (
                            tags.map((tag: string) => (
                                <RemovableTag key={tag} label={tag} onRemove={() => onRemove(tag)} />
                            ))
                        )}
                    </div>
                </div>

                <div>
                    <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-2">Suggestions</div>
                    <div className="flex flex-wrap gap-2">
                        {suggestions.map((s: string) => {
                            const isLongTerm = s === 'Long Term';
                            return (
                                <button
                                    key={s}
                                    onClick={() => onAdd(s)}
                                    disabled={tags.includes(s)}
                                    className={`px-2 py-1 rounded-md text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isLongTerm ? 'bg-yellow-200 text-yellow-900 hover:bg-yellow-300 dark:bg-yellow-600/20 dark:text-yellow-300' : 'bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-zinc-600 dark:text-zinc-300'}`}
                                >
                                    + {s}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md border border-zinc-800 bg-zinc-900 px-4 py-2 text-sm font-medium text-zinc-300 hover:bg-zinc-800 hover:text-white transition-all"
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            const toRemove = Array.isArray(tags) ? [...tags] : [];
                            toRemove.forEach((t) => onRemove(t));
                            setInputValue('');
                        }}
                        disabled={!Array.isArray(tags) || tags.length === 0}
                        className={`px-3 py-2 text-xs font-semibold rounded-lg transition-colors ${Array.isArray(tags) && tags.length > 0 ? 'bg-zinc-100 text-zinc-900 dark:bg-zinc-700 dark:text-white' : 'bg-zinc-900 border border-zinc-700 text-zinc-500 opacity-50 cursor-not-allowed'}`}
                    >
                        Clear
                    </button>

                    <button
                        type="button"
                        onClick={() => { handleAdd(); onClose(); }}
                        className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-500"
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
};
// --- Trade Efficiency Gauge Component ---
const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

const TradeEfficiencyGauge = ({
        mae,
        pnl,
        mfe,
        modeLabel,
}: {
        mae: number;
        pnl: number;
        mfe: number;
        modeLabel: 'Gross' | 'Net';
}) => {
        const radius = 100;
        const stroke = 16;
        const circumference = Math.PI * radius;
        const denominator = Math.max(Math.abs(mae) + Math.abs(mfe), Math.abs(pnl), 1);
        const percent = clamp(Math.round((Math.abs(pnl) / denominator) * 100), 0, 100);
        const offset = circumference * (1 - percent / 100);
        const dark = isDarkTheme();
        const zeroTone = dark ? 'text-zinc-200' : 'text-zinc-700';
        const pnlToneClass = getSignedToneClass(pnl, 'text-emerald-500', 'text-red-500', zeroTone);
        const maeToneClass = getSignedToneClass(mae, 'text-emerald-500', 'text-red-500', zeroTone);
        const mfeToneClass = getSignedToneClass(mfe, 'text-yellow-500', 'text-red-500', zeroTone);
        const pnlDisplay = formatTopCurrency(pnl, true);
        const maeDisplay = formatTopCurrency(mae, true);
        const mfeDisplay = formatTopCurrency(mfe, true);

        return (
                <div className="relative bg-white dark:bg-[#1a1a1c] border border-zinc-200 dark:border-[#242428] rounded-xl p-4 flex flex-col h-full shadow-md">
                        <div className="flex-1 flex items-center justify-center">
                                <svg viewBox="0 0 260 170" className="w-full max-w-[260px] h-auto">
                                        <defs>
                                                <linearGradient id="efficiencyGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                                        <stop offset="0%" stopColor="#ef4444" />
                                                        <stop offset="50%" stopColor="#f59e0b" />
                                                        <stop offset="100%" stopColor="#10b981" />
                                                </linearGradient>
                                        </defs>

                                        {/* Background arc */}
                                        <path
                                                d={`M 20 130 A ${radius} ${radius} 0 0 1 240 130`}
                                                fill="none"
                                                className="stroke-zinc-200 dark:stroke-zinc-800"
                                                strokeWidth={stroke}
                                                strokeLinecap="round"
                                        />

                                        {/* Foreground arc */}
                                        <path
                                                d={`M 20 130 A ${radius} ${radius} 0 0 1 240 130`}
                                                fill="none"
                                                stroke="url(#efficiencyGradient)"
                                                strokeWidth={stroke}
                                                strokeLinecap="round"
                                                strokeDasharray={circumference}
                                                strokeDashoffset={offset}
                                        />

                                        {/* Center text */}
                                        <text x="130" y="90" textAnchor="middle" className={`font-semibold text-4xl ${pnl === 0 ? (dark ? 'fill-zinc-200' : 'fill-zinc-700') : (dark ? 'fill-white' : 'fill-zinc-900')}`}>
                                                {percent}%
                                        </text>
                                        <text x="130" y="112" textAnchor="middle" className="fill-zinc-400 dark:fill-zinc-500 text-xs">
                                                Opportunity captured
                                        </text>
                                </svg>
                        </div>

                        {/* Metrics */}
                        <div className="grid grid-cols-3 gap-4 text-center mt-2 border-t border-zinc-100 dark:border-zinc-800 pt-4">
                                <div title="Maximum Adverse Excursion — the worst unrealized loss experienced during the trade." className="cursor-help">
                                        <div className="text-xs text-zinc-500 dark:text-gray-400">MAE</div>
                                        <div className={`text-sm font-semibold ${maeToneClass}`}>
                                                {maeDisplay}
                                        </div>
                                </div>
                                <div>
                                        <div className="text-xs text-zinc-500 dark:text-gray-400">P&L ({modeLabel})</div>
                                        <div className={`text-sm font-semibold ${pnlToneClass}`}>
                                                {pnlDisplay}
                                        </div>
                                </div>
                                <div title="Maximum Favorable Excursion — the best unrealized profit reached during the trade." className="cursor-help">
                                        <div className="text-xs text-zinc-500 dark:text-gray-400">MFE ({modeLabel})</div>
                                        <div className={`text-sm font-semibold ${mfeToneClass}`}>
                                                {mfeDisplay}
                                        </div>
                                </div>
                        </div>
                </div>
        );
};

function formatTopCurrency(value: number, forceSign = false) {
    if (!Number.isFinite(value)) return MISSING_VALUE;
    const sign = value > 0 ? (forceSign ? '+' : '') : value < 0 ? '-' : '';
    return `${sign}$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// NOTE: `readFirstFiniteNumber` was removed (unused). Keep `readFirstPositiveFiniteNumber`.

function readFirstPositiveFiniteNumber(...values: unknown[]): number | null {
    for (const value of values) {
        const numeric = Number(value);
        if (Number.isFinite(numeric) && numeric > 0) return numeric;
    }
    return null;
}

function formatMovementNumber(value: number): string {
    const rounded = Math.round(value * 100) / 100;
    if (Object.is(rounded, -0)) return '0';
    return Number.isInteger(rounded)
        ? String(rounded)
        : rounded.toFixed(2).replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
}

function formatMovementValueWithUnit(value: number | null, unitLabel: string): React.ReactNode {
    if (value == null || !Number.isFinite(value)) {
        return <span className="text-zinc-400 dark:text-zinc-500">{MISSING_VALUE}</span>;
    }

    const absVal = Math.abs(Number(value));
    const unitDisplay = unitLabel === 'ticks'
        ? (absVal === 1 ? 'tick' : 'ticks')
        : unitLabel === 'pips'
            ? (absVal === 1 ? 'pip' : 'pips')
            : (absVal === 1 ? 'point' : 'points');

    return (
        <span className="inline-flex items-baseline gap-2 whitespace-nowrap">
            <span>{formatMovementNumber(value)}</span>
            <span className="text-[18px] font-semibold leading-none lowercase opacity-80">{unitDisplay}</span>
        </span>
    );
}

function getCaptureUnitLabel(secType: string): 'ticks' | 'pips' | 'points' {
    if (secType === 'FUT') return 'ticks';
    if (secType === 'CASH') return 'pips';
    return 'points';
}

const NEUTRAL_NUMERIC_TONE = 'text-zinc-700 dark:text-zinc-200';

function getSignedToneClass(value: number | null | undefined, positiveClass: string, negativeClass: string, zeroClass = NEUTRAL_NUMERIC_TONE) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric === 0) return zeroClass;
    return numeric > 0 ? positiveClass : negativeClass;
}

function getZeroNeutralToneClass(value: number | null | undefined, activeClass: string, zeroClass = NEUTRAL_NUMERIC_TONE) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric === 0) return zeroClass;
    return activeClass;
}

const TopMetricCard = ({
    title,
    value,
    subtext,
    tooltipText,
    valueClassName = 'top-metric-value',
    valueToneClassName = '',
    valueStyle,
    rightElement,
}: {
    title: string;
    value: React.ReactNode;
    subtext: React.ReactNode;
    tooltipText?: string;
    valueClassName?: string;
    valueToneClassName?: string;
    valueStyle?: React.CSSProperties;
    rightElement?: React.ReactNode;
}) => (
    <div className="relative min-w-0 [container-type:inline-size] bg-white dark:bg-[#1a1a1c] border border-zinc-200 dark:border-[#242428] rounded-xl flex flex-col h-full min-h-[140px] shadow-md">
        <div className="px-6 py-4 border-b border-zinc-200 dark:border-[#242428] flex justify-between items-center shrink-0">
            <div className="text-zinc-700 dark:text-gray-300 text-[15px] font-semibold flex items-center">
                {title} <span className="ml-1.5"><TooltipIcon text={tooltipText} /></span>
            </div>
            {rightElement ? <div>{rightElement}</div> : null}
        </div>
        <div className="px-5 pt-3 pb-3 flex-1 flex flex-col text-center">
            <div className="flex-1 flex items-center justify-center min-h-[64px]">
                <div className={`font-bold ${valueClassName} ${valueToneClassName || 'text-zinc-900 dark:text-white'}`} style={valueStyle}>
                    {value}
                </div>
            </div>
            <div className="text-[15px] text-zinc-500 dark:text-gray-400 mt-auto pt-2">{subtext}</div>
        </div>
    </div>
);

const DEFAULT_NOTES_FONT_SIZE = '14pt';
const NOTES_FONT_SIZE_OPTIONS = ['12pt', '14pt', '16pt', '18pt', '20pt', '22pt'];
const NOTES_FONT_FAMILY_OPTIONS = ['Arial', 'Georgia', 'Courier New', 'Times New Roman'];

const NotesTextStyle = TextStyle.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            fontSize: {
                default: null,
                parseHTML: (element) => (element as HTMLElement).style.fontSize || null,
                renderHTML: (attributes) => attributes.fontSize ? { style: `font-size: ${attributes.fontSize}` } : {},
            },
            fontFamily: {
                default: null,
                parseHTML: (element) => (element as HTMLElement).style.fontFamily || null,
                renderHTML: (attributes) => attributes.fontFamily ? { style: `font-family: ${attributes.fontFamily}` } : {},
            },
            textDecoration: {
                default: null,
                parseHTML: (element) => (element as HTMLElement).style.textDecoration || null,
                renderHTML: (attributes) => attributes.textDecoration ? { style: `text-decoration: ${attributes.textDecoration}` } : {},
            },
        };
    },
});

const NotesTextAlign = Extension.create({
    name: 'notesTextAlign',
    addGlobalAttributes() {
        return [
            {
                types: ['paragraph', 'heading'],
                attributes: {
                    textAlign: {
                        default: 'left',
                        parseHTML: (element) => (element as HTMLElement).style.textAlign || 'left',
                        renderHTML: (attributes) => {
                            const textAlign = String(attributes.textAlign || 'left');
                            return textAlign !== 'left' ? { style: `text-align: ${textAlign}` } : {};
                        },
                    },
                },
            },
        ];
    },
});

const TopNotesSection = ({ note, onChange }: { note: string; onChange: (value: string) => void }) => {
    const dark = isDarkTheme();
    const normalizeEditorHtml = (value: string) => String(value || '').replace(/<p><br><\/p>/gi, '').trim();
    const isMeaningfulContent = (value: string) => {
        try {
            const div = document.createElement('div');
            div.innerHTML = String(value || '');
            return Boolean((div.textContent || '').trim() || div.querySelector('img, a, ul, ol, li, blockquote'));
        } catch {
            return Boolean(String(value || '').trim());
        }
    };

    const [isExpanded, setIsExpanded] = useState(isMeaningfulContent(String(note || '')));
    const [content, setContent] = useState(String(note || ''));
    const selectionRangeRef = useRef<{ from: number; to: number } | null>(null);
    const isSyncingRef = useRef(false);
    const [toolbarState, setToolbarState] = useState({
        bold: false,
        italic: false,
        underline: false,
        strikeThrough: false,
        insertUnorderedList: false,
        insertOrderedList: false,
        justifyLeft: true,
        justifyCenter: false,
        justifyRight: false,
        fontSize: DEFAULT_NOTES_FONT_SIZE,
        fontFamily: '',
        color: '#000000',
    });

    const getToolbarBtnClass = (isActive: boolean) =>
        `p-1 rounded transition-colors ${isActive
            ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
            : dark
                ? 'text-gray-400 hover:text-white hover:bg-[#2D2D35]'
                : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100'}`;

    const toolbarDividerClass = dark ? 'border-white/20' : 'border-zinc-200';
    const toolbarSelectClass = dark
        ? 'h-8 w-full appearance-none rounded-md border border-gray-700 bg-[#1C1C22] pl-2 select-chevron-input text-sm text-gray-300 outline-none focus:border-gray-500'
        : 'h-8 w-full appearance-none rounded-md border border-zinc-300 bg-white pl-2 select-chevron-input text-sm text-zinc-700 outline-none focus:border-zinc-400';

    const editor = useEditor({
        immediatelyRender: false,
        extensions: [
            StarterKit.configure({
                blockquote: false,
                codeBlock: false,
                horizontalRule: false,
            }),
            NotesTextStyle,
            NotesTextAlign,
            Color,
            LinkExtension.configure({
                autolink: false,
                openOnClick: false,
                linkOnPaste: true,
            }),
            ImageExtension,
        ],
        content: String(note || ''),
        editorProps: {
            attributes: {
                class: `w-full min-h-[190px] h-full bg-transparent text-left ${dark ? 'text-gray-200' : 'text-zinc-800'} p-5 outline-none leading-6 overflow-y-auto [&_p]:my-0 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-6 [&_ol]:pl-6 [&_li]:my-1 [&_a]:text-blue-400 [&_a]:underline`,
                style: `direction: ltr; unicode-bidi: plaintext; font-size: ${DEFAULT_NOTES_FONT_SIZE};`,
            },
        },
        onCreate: ({ editor: editorInstance }) => {
            const { from, to } = editorInstance.state.selection;
            selectionRangeRef.current = { from, to };
            const attrs = editorInstance.getAttributes('textStyle') as { fontSize?: string; fontFamily?: string; textDecoration?: string; color?: string };
            setToolbarState({
                bold: editorInstance.isActive('bold'),
                italic: editorInstance.isActive('italic'),
                underline: String(attrs.textDecoration || '').includes('underline'),
                strikeThrough: editorInstance.isActive('strike'),
                insertUnorderedList: editorInstance.isActive('bulletList'),
                insertOrderedList: editorInstance.isActive('orderedList'),
                justifyLeft: editorInstance.isActive({ textAlign: 'left' }) || (!editorInstance.isActive({ textAlign: 'center' }) && !editorInstance.isActive({ textAlign: 'right' })),
                justifyCenter: editorInstance.isActive({ textAlign: 'center' }),
                justifyRight: editorInstance.isActive({ textAlign: 'right' }),
                fontSize: String(attrs.fontSize || DEFAULT_NOTES_FONT_SIZE),
                fontFamily: String(attrs.fontFamily || ''),
                color: String(attrs.color || '#000000'),
            });
        },
        onSelectionUpdate: ({ editor: editorInstance }) => {
            const { from, to } = editorInstance.state.selection;
            selectionRangeRef.current = { from, to };
            const attrs = editorInstance.getAttributes('textStyle') as { fontSize?: string; fontFamily?: string; textDecoration?: string; color?: string };
            setToolbarState({
                bold: editorInstance.isActive('bold'),
                italic: editorInstance.isActive('italic'),
                underline: String(attrs.textDecoration || '').includes('underline'),
                strikeThrough: editorInstance.isActive('strike'),
                insertUnorderedList: editorInstance.isActive('bulletList'),
                insertOrderedList: editorInstance.isActive('orderedList'),
                justifyLeft: editorInstance.isActive({ textAlign: 'left' }) || (!editorInstance.isActive({ textAlign: 'center' }) && !editorInstance.isActive({ textAlign: 'right' })),
                justifyCenter: editorInstance.isActive({ textAlign: 'center' }),
                justifyRight: editorInstance.isActive({ textAlign: 'right' }),
                fontSize: String(attrs.fontSize || DEFAULT_NOTES_FONT_SIZE),
                fontFamily: String(attrs.fontFamily || ''),
                color: String(attrs.color || '#000000'),
            });
        },
        onUpdate: ({ editor: editorInstance }) => {
            if (isSyncingRef.current) return;
            const nextValue = editorInstance.getHTML();
            setContent(nextValue);
            onChange(nextValue);
        },
    });

    useEffect(() => {
        const nextValue = String(note || '');
        setContent(nextValue);
        if (isMeaningfulContent(nextValue)) {
            setIsExpanded(true);
        }
        if (!editor) return;
        if (normalizeEditorHtml(editor.getHTML()) === normalizeEditorHtml(nextValue)) return;
        isSyncingRef.current = true;
        // TipTap typings expect an options object rather than a boolean for setContent.
        // Call without the second arg to preserve compatibility and avoid type errors.
        editor.commands.setContent(nextValue || '<p></p>');
        window.requestAnimationFrame(() => {
            isSyncingRef.current = false;
        });
    }, [editor, note]);

    useEffect(() => {
        if (!isExpanded || !editor) return;
        window.requestAnimationFrame(() => {
            editor.commands.focus('end');
        });
    }, [editor, isExpanded]);

    const getActiveSelection = () => {
        if (!editor) return null;
        return selectionRangeRef.current ?? { from: editor.state.selection.from, to: editor.state.selection.to };
    };

    const withStoredSelection = (run: (range: { from: number; to: number }) => void) => {
        if (!editor) return;
        const range = getActiveSelection();
        if (!range) return;
        run(range);
    };

    const handleToolbarMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const expandEditor = () => {
        setIsExpanded(true);
    };

    const handleTextStyleToggle = (type: 'bold' | 'italic' | 'strike') => {
        withStoredSelection((range) => {
            const chain = editor!.chain().focus().setTextSelection(range);
            if (type === 'bold') chain.toggleBold();
            if (type === 'italic') chain.toggleItalic();
            if (type === 'strike') chain.toggleStrike();
            chain.run();
        });
    };

    const handleUnderlineToggle = () => {
        withStoredSelection((range) => {
            const isActive = String(editor!.getAttributes('textStyle').textDecoration || '').includes('underline');
            const chain = editor!.chain().focus().setTextSelection(range);
            if (isActive) {
                chain.setMark('textStyle', { textDecoration: null }).removeEmptyTextStyle();
            } else {
                chain.setMark('textStyle', { textDecoration: 'underline' });
            }
            chain.run();
        });
    };

    const handleUndo = () => {
        editor?.chain().focus().undo().run();
    };

    const handleRedo = () => {
        editor?.chain().focus().redo().run();
    };

    const handleInsertLink = () => {
        if (!editor) return;
        const url = window.prompt('Enter link URL', editor.getAttributes('link').href || '');
        if (url == null) return;
        withStoredSelection((range) => {
            const chain = editor.chain().focus().setTextSelection(range).extendMarkRange('link');
            if (!url.trim()) {
                chain.unsetLink().run();
            } else {
                chain.setLink({ href: url.trim() }).run();
            }
        });
    };

    const handleInsertImage = () => {
        if (!editor) return;
        const url = window.prompt('Enter image URL');
        if (!url) return;
        editor.chain().focus().setImage({ src: url.trim() }).run();
    };

    const handleFontSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        if (!value || !editor) return;
        withStoredSelection((range) => {
            editor
                .chain()
                .focus()
                .setTextSelection(range)
                .setMark('textStyle', { fontSize: value })
                .setTextSelection(range)
                .run();
            selectionRangeRef.current = range;
        });
    };

    const handleFontFamilyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        if (!editor) return;
        withStoredSelection((range) => {
            const chain = editor.chain().focus().setTextSelection(range);
            if (value) {
                chain.setMark('textStyle', { fontFamily: value });
            } else {
                chain.setMark('textStyle', { fontFamily: null }).removeEmptyTextStyle();
            }
            chain.run();
        });
    };

    const handleTextColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (!value || !editor) return;
        withStoredSelection((range) => {
            editor.chain().focus().setTextSelection(range).setColor(value).run();
        });
    };

    const handleAlignChange = (alignment: 'left' | 'center' | 'right') => {
        if (!editor) return;
        const range = getActiveSelection();
        const chain = editor.chain().focus();
        if (range) {
            chain.setTextSelection(range);
        }
        chain.updateAttributes('paragraph', { textAlign: alignment });
        chain.updateAttributes('heading', { textAlign: alignment });
        chain.run();
    };

    const handleListCommand = (type: 'bullet' | 'ordered') => {
        if (!editor) return;
        withStoredSelection((range) => {
            const chain = editor.chain().focus().setTextSelection(range);
            if (type === 'bullet') {
                chain.toggleBulletList().run();
            } else {
                chain.toggleOrderedList().run();
            }
        });
    };

    const activeFontFamily = NOTES_FONT_FAMILY_OPTIONS.includes(toolbarState.fontFamily) ? toolbarState.fontFamily : '';

    if (!isExpanded && !isMeaningfulContent(content)) {
        return (
            <div
                onClick={expandEditor}
                className="bg-white dark:bg-[#1a1a1c] border border-zinc-200 dark:border-[#242428] rounded-xl flex flex-col h-full cursor-text group shadow-md"
            >
                <div className="px-6 py-4 border-b border-zinc-200 dark:border-[#242428] flex items-center gap-2">
                    <Edit2 size={16} className="text-gray-400 group-hover:text-gray-300" />
                    <span className="font-semibold text-zinc-900 dark:text-gray-200 text-[15px] group-hover:text-white transition-colors">Notes</span>
                </div>
                <div className="p-5 flex-1">
                    <p className="text-sm text-zinc-500 italic">Add trade notes, lessons, mistakes, or context...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-[#1a1a1c] border border-zinc-200 dark:border-[#242428] rounded-xl flex flex-col overflow-hidden h-full shadow-md">
            <div className="px-6 py-4 border-b border-zinc-200 dark:border-[#242428] flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Edit2 size={16} className="text-gray-400" />
                    <span className="font-semibold text-zinc-900 dark:text-gray-200 text-[15px]">Notes</span>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 p-2 border-b border-zinc-200 dark:border-[#242428] bg-zinc-50 text-zinc-500 dark:bg-[#222226] dark:text-gray-400">
                <div className={`flex gap-2 items-center px-2 border-r ${toolbarDividerClass}`}>
                    <button className={getToolbarBtnClass(toolbarState.bold)} type="button" title="Bold" onMouseDown={handleToolbarMouseDown} onClick={() => handleTextStyleToggle('bold')}><Bold size={16}/></button>
                    <button className={getToolbarBtnClass(toolbarState.italic)} type="button" title="Italic" onMouseDown={handleToolbarMouseDown} onClick={() => handleTextStyleToggle('italic')}><Italic size={16}/></button>
                    <button className={getToolbarBtnClass(toolbarState.underline)} type="button" title="Underline" onMouseDown={handleToolbarMouseDown} onClick={handleUnderlineToggle}><Underline size={16}/></button>
                    <button className={getToolbarBtnClass(toolbarState.strikeThrough)} type="button" title="Strikethrough" onMouseDown={handleToolbarMouseDown} onClick={() => handleTextStyleToggle('strike')}><Strikethrough size={16}/></button>
                </div>
                <div className={`flex gap-2 items-center px-2 border-r ${toolbarDividerClass}`}>
                    <button className={getToolbarBtnClass(false)} type="button" title="Undo" onMouseDown={handleToolbarMouseDown} onClick={handleUndo}><Undo size={16}/></button>
                    <button className={getToolbarBtnClass(false)} type="button" title="Redo" onMouseDown={handleToolbarMouseDown} onClick={handleRedo}><Redo size={16}/></button>
                </div>
                <div className={`flex gap-2 items-center px-2 border-r ${toolbarDividerClass}`}>
                    <button className={getToolbarBtnClass(Boolean(editor?.isActive('link')))} type="button" title="Insert link" onMouseDown={handleToolbarMouseDown} onClick={handleInsertLink}><Link size={16}/></button>
                    <button className={getToolbarBtnClass(false)} type="button" title="Insert image" onMouseDown={handleToolbarMouseDown} onClick={handleInsertImage}><ImageIcon size={16}/></button>
                </div>
                <div className={`flex gap-3 items-center px-2 border-r ${toolbarDividerClass} text-sm`}>
                    <div className="relative min-w-[92px]">
                        <select value={toolbarState.fontSize} className={toolbarSelectClass} onChange={handleFontSizeChange} onMouseDown={() => { selectionRangeRef.current = editor ? { from: editor.state.selection.from, to: editor.state.selection.to } : null; }} aria-label="Font size">
                            {NOTES_FONT_SIZE_OPTIONS.map((option) => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                        <ChevronDown size={14} className="pointer-events-none absolute select-chevron-icon top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-500" />
                    </div>
                    <div className="relative min-w-[106px]">
                        <select value={activeFontFamily} className={toolbarSelectClass} onChange={handleFontFamilyChange} onMouseDown={() => { selectionRangeRef.current = editor ? { from: editor.state.selection.from, to: editor.state.selection.to } : null; }} aria-label="Font family">
                            <option value="">Font</option>
                            {NOTES_FONT_FAMILY_OPTIONS.map((option) => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                        <ChevronDown size={14} className="pointer-events-none absolute select-chevron-icon top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-500" />
                    </div>
                    <label className="relative inline-flex h-8 w-8 cursor-pointer items-center justify-center overflow-hidden rounded-md border border-zinc-300 bg-white dark:border-gray-700 dark:bg-[#1C1C22]">
                        <span className="h-4 w-4 rounded-sm border border-gray-600" style={{ backgroundColor: toolbarState.color }} />
                        <input type="color" value={toolbarState.color} className="absolute inset-0 cursor-pointer opacity-0" aria-label="Text color" onChange={handleTextColorChange} />
                    </label>
                </div>
                <div className={`flex gap-2 items-center px-2 border-r ${toolbarDividerClass}`}>
                    <button className={getToolbarBtnClass(toolbarState.justifyLeft)} type="button" title="Align left" onMouseDown={handleToolbarMouseDown} onClick={() => handleAlignChange('left')}><AlignLeft size={16}/></button>
                    <button className={getToolbarBtnClass(toolbarState.justifyCenter)} type="button" title="Align center" onMouseDown={handleToolbarMouseDown} onClick={() => handleAlignChange('center')}><AlignCenter size={16}/></button>
                    <button className={getToolbarBtnClass(toolbarState.justifyRight)} type="button" title="Align right" onMouseDown={handleToolbarMouseDown} onClick={() => handleAlignChange('right')}><AlignRight size={16}/></button>
                </div>
                <div className="flex gap-2 items-center px-2">
                    <button className={getToolbarBtnClass(toolbarState.insertUnorderedList)} type="button" title="Bulleted list" onMouseDown={handleToolbarMouseDown} onClick={() => handleListCommand('bullet')}><List size={16}/></button>
                    <button className={getToolbarBtnClass(toolbarState.insertOrderedList)} type="button" title="Numbered list" onMouseDown={handleToolbarMouseDown} onClick={() => handleListCommand('ordered')}><ListOrdered size={16}/></button>
                </div>
            </div>

            <div className="relative flex-1 min-h-[190px]">
                {!isMeaningfulContent(content) ? (
                    <div className="pointer-events-none absolute inset-x-5 top-5 text-sm italic text-zinc-500 dark:text-gray-600">
                        Add trade notes, lessons, mistakes, or context...
                    </div>
                ) : null}
                <EditorContent editor={editor} />
            </div>
        </div>
    );
};

const TopTradeExcursionCard = ({
    isGross,
    pnl,
    mae,
    mfe,
    isOpen,
    availability,
    unavailableReason,
}: {
    isGross: boolean;
    pnl: number;
    mae: number | null;
    mfe: number | null;
    isOpen: boolean;
    hasCurrentMarkPrice: boolean;
    availability: 'loading' | 'available' | 'unavailable' | 'data_error';
    unavailableReason?: string | null;
}) => {
    if (isOpen) {
        return (
            <TradeExcursionWidget
                closedPnl={0}
                mae={mae}
                mfe={mfe}
                isOpen={true}
                pnlMode={isGross ? 'GROSS' : 'NET'}
                availability={availability}
                unavailableReason={unavailableReason}
                className="h-full"
            />
        );
    }

    const safePnl = Number.isFinite(Number(pnl)) ? Number(pnl) : 0;
    const safeMae = mae != null && Number.isFinite(Number(mae)) ? Number(mae) : null;
    const safeMfe = mfe != null && Number.isFinite(Number(mfe)) ? Number(mfe) : null;
    return (
        <TradeExcursionWidget
            closedPnl={safePnl}
            mae={safeMae}
            mfe={safeMfe}
            pnlMode={isGross ? 'GROSS' : 'NET'}
            availability={availability}
            unavailableReason={unavailableReason}
            className="h-full"
        />
    );
};

// --- NEW COMPONENT: Trade Details Modal (Read Only) ---
// Can also be rendered as an embedded page panel via `variant="page"`.
const TradeDetailsModal = ({
    isOpen,
    onClose,
    trade,
    variant = 'modal',
    onSetInitialRisk,
}: {
    isOpen: boolean,
    onClose: () => void,
    trade: TradeRecord,
    variant?: 'modal' | 'page',
    onSetInitialRisk?: (trade: TradeRecord, value: number) => void,
}) => {
    const displayTimeZone = useDisplayTimezone();
    if (!isOpen || !trade) return null;

    const asPage = variant === 'page';

    const mfe = Number(trade.deterministicMfe ?? 0) || 0;
    const mae = Number(trade.deterministicMae ?? 0) || 0;
    const rawPnl = Number(trade.pnl);
    const pnl = Number.isFinite(rawPnl) ? rawPnl : null;
    const initialRiskRaw = Number(trade.initialRisk ?? trade.initial_risk ?? trade.riskAmount);
    const initialRisk = Number.isFinite(initialRiskRaw) && initialRiskRaw > 0 ? initialRiskRaw : null;
    const pnlR = initialRisk != null && pnl != null ? (pnl / initialRisk) : null;
    const isWin = pnl != null ? pnl >= 0 : false;
    const dateStr = new Intl.DateTimeFormat(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: displayTimeZone }).format(Number(trade.timestamp));
    const timeStr = formatDateTimeInTimezone(Number(trade.timestamp), displayTimeZone).timeStr.slice(0, 5);
    const headerToneClass = pnl != null
        ? (isWin ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400')
        : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800/70 dark:text-zinc-300';

    const handleSetInitialRisk = () => {
        const existing = initialRisk != null ? String(initialRisk) : '';
        const input = window.prompt('Set Initial Risk (USD)', existing);
        if (input == null) return;
        const value = Number(input);
        if (!Number.isFinite(value) || value <= 0) {
            try {
                window.dispatchEvent(new CustomEvent('tt:toast', {
                    detail: {
                        message: 'Please enter a valid Initial Risk greater than 0.',
                        variant: 'error',
                        duration: 2800,
                    },
                }));
            } catch {}
            return;
        }
        onSetInitialRisk?.(trade, value);
    };

    const dark = isDarkTheme();

    const content = (
            <div className={`relative w-full ${asPage ? 'max-w-none' : 'max-w-3xl'} bg-white dark:bg-[#1a1a1c] rounded-2xl ${asPage ? 'shadow-md' : 'shadow-2xl'} border border-zinc-200 dark:border-zinc-800 flex flex-col ${asPage ? '' : 'max-h-[90vh]'} overflow-hidden`}>

                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50">
                    <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${headerToneClass}`}>
                            <DollarSign className="w-6 h-6" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-xl font-bold text-zinc-900 dark:text-white">{trade.symbol}</h3>
                                <span className={`px-2 py-0.5 text-xs font-semibold rounded uppercase ${trade.side === 'Long' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'}`}>
                                    {trade.side}
                                </span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                                <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {dateStr}</span>
                                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {timeStr}</span>
                            </div>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-sm text-zinc-500 dark:text-gray-400">Net P&L</div>
                        <div className={`text-3xl font-bold ${pnl != null ? getSignedToneClass(pnl, 'text-emerald-500', 'text-red-500') : 'text-zinc-400 dark:text-zinc-500'}`}>
                            {formatTradeDetailsPnlDisplay(pnl)}
                        </div>
                    </div>
                </div>

                {/* Content - Scrollable */}
                <div className={`p-6 ${asPage ? '' : 'overflow-y-auto custom-scrollbar'}`}>
                    <div className="w-full max-w-none min-w-0 space-y-6 px-4 md:px-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                        {/* Left Column: Visuals */}
                        <div className="space-y-6">
                            {/* Reusing Gauge for this specific trade */}
                            <div className="h-94">
                                <TradeEfficiencyGauge mae={mae} pnl={pnl ?? 0} mfe={mfe} modeLabel="Net" />
                            </div>
                        </div>

                        {/* Right Column: Stats */}
                        <div className="space-y-6">
                            {/* Execution Details */}
                            <div className="bg-zinc-50 dark:bg-zinc-900/50 rounded-xl p-5 border border-zinc-100 dark:border-zinc-800">
                                <h4 className="text-sm font-medium text-zinc-900 dark:text-white mb-4 flex items-center gap-2">
                                    <BarChart3 className="w-4 h-4 text-zinc-400" />
                                    Trade Statistics
                                </h4>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center py-2 border-b border-zinc-200/50 dark:border-zinc-700/50">
                                        <span className="text-sm text-zinc-500 dark:text-zinc-400">Volume</span>
                                        <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-200">{trade.volume}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-zinc-200/50 dark:border-zinc-700/50">
                                        <span className="text-sm text-zinc-500 dark:text-zinc-400">Duration</span>
                                        <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-200">{fmtDurationMinutes(trade.durationMinutes)}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-zinc-200/50 dark:border-zinc-700/50">
                                        <span className="text-sm text-zinc-500 dark:text-zinc-400">Fees</span>
                                        <span className={`text-sm font-semibold ${trade.fees_present ? getSignedToneClass(-Math.abs(Number(trade.fees || 0)), 'text-[#22C55E]', 'text-[#EF4444]') : 'text-zinc-400 dark:text-zinc-400'}`}>
                                            {trade.fees_present ? formatTopCurrency(-Math.abs(Number(trade.fees || 0))) : <span className="text-zinc-400">—</span>}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-zinc-200/50 dark:border-zinc-700/50">
                                        <span className="text-sm text-zinc-500 dark:text-zinc-400">Commissions</span>
                                        <span className={`text-sm font-semibold ${trade.commission_present ? getSignedToneClass(-Math.abs(Number(trade.commission || 0)), 'text-[#22C55E]', 'text-[#EF4444]') : 'text-zinc-400 dark:text-zinc-400'}`}>
                                            {trade.commission_present ? formatTopCurrency(-Math.abs(Number(trade.commission || 0))) : <span className="text-zinc-400">—</span>}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-zinc-200/50 dark:border-zinc-700/50">
                                        <span className="text-sm text-zinc-500 dark:text-zinc-400">Initial Risk</span>
                                        <span className={`text-sm font-semibold ${getZeroNeutralToneClass(initialRisk, dark ? 'text-zinc-200' : 'text-zinc-700')}`}>
                                            {initialRisk != null ? fmtMoney(initialRisk) : <span className="text-zinc-400">Not set</span>}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-zinc-200/50 dark:border-zinc-700/50">
                                        <span className="text-sm text-zinc-500 dark:text-zinc-400">P&L (R)</span>
                                        <span className={`text-sm font-semibold ${pnlR == null ? 'text-amber-600 dark:text-amber-400' : getSignedToneClass(pnlR, 'text-emerald-500', 'text-red-500')}`}>
                                            {pnlR != null ? `${pnlR.toFixed(2)}R` : (pnl == null ? MISSING_VALUE : 'Requires Initial Risk')}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-2">
                                        <span className="text-sm text-zinc-500 dark:text-zinc-400">Gross P&L</span>
                                        <span className={`text-sm font-bold ${pnl != null ? getSignedToneClass(pnl + (Number(trade.fees)||0) + (Number(trade.commission)||0), 'text-emerald-500', 'text-red-500') : 'text-zinc-400 dark:text-zinc-500'}`}>
                                            {pnl != null
                                                ? fmtMoney(pnl + (Number(trade.fees)||0) + (Number(trade.commission)||0))
                                                : <span className="text-zinc-400">—</span>}
                                        </span>
                                    </div>
                                    {initialRisk == null ? (
                                        <div className="pt-2">
                                            <button
                                                type="button"
                                                onClick={handleSetInitialRisk}
                                                className="px-3 h-8 rounded-md text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700"
                                            >
                                                Set Initial Risk
                                            </button>
                                            <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">Add Initial Risk to calculate P&L (R).</p>
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            {/* Notes Section (Read Only) */}
                            <div className="bg-zinc-50 dark:bg-zinc-900/50 rounded-xl p-5 border border-zinc-100 dark:border-zinc-800 flex-1">
                                <h4 className="text-sm font-medium text-zinc-900 dark:text-white mb-2 flex items-center gap-2">
                                    <Edit3 className="w-4 h-4 text-zinc-400" />
                                    Trade Notes
                                </h4>
                                <div className="text-sm text-zinc-600 dark:text-zinc-300 min-h-[80px] whitespace-pre-wrap">
                                    {trade.notes || "No notes for this trade."}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-zinc-100 dark:border-zinc-800 bg-white dark:bg-[#1a1a1c] flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-zinc-900 dark:text-white rounded-lg text-sm font-medium transition-colors"
                    >
                        Close Details
                    </button>
                </div>
            </div>
    );

    if (asPage) {
        return content;
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            {content}
        </div>
    );
};

// --- 1. The Popup Modal Component (User Provided) ---
// Now supports Editing
const CustomEntryModal = ({
    isOpen,
    onClose,
    onAdd,
    initialData,
    defaultSymbol,
}: {
    isOpen: boolean;
    onClose: () => void;
    onAdd: (t: ImportedTradeRecord & { id: string }) => void;
    initialData?: TradeRecord;
    defaultSymbol?: string;
}) => {
    const displayTimeZone = useDisplayTimezone();
    const resolvedSymbol = (initialData?.symbol || defaultSymbol || 'ES').toString();

    // Default to current local time in "YYYY-MM-DDTHH:mm" format for the input
    const [dateTimeStr, setDateTimeStr] = useState(() => {
        if (initialData?.timestamp) {
            return formatDateTimeInputInTimezone(Number(initialData.timestamp), displayTimeZone);
        }
        return formatDateTimeInputInTimezone(Date.now(), displayTimeZone);
    });

    // Expose the native input so we can call showPicker() where available
    const dateInputRef = useRef<HTMLInputElement | null>(null);

    const openNativePicker = () => {
        const el = dateInputRef.current;
        if (!el) return;
        // Prefer the standardized showPicker() when supported
        const picker = el as HTMLInputElement & { showPicker?: () => void };
        if (typeof picker.showPicker === 'function') {
            try { picker.showPicker(); return; } catch { /* fallthrough */ }
        }
        // Fallback: focus + synthetic click (best-effort across browsers)
        el.focus();
        try { el.click(); } catch { /* ignore */ }
    };

    // Field string values (controlled)
    const [quantityStr, setQuantityStr] = useState(() => initialData?.volume?.toString() || '');
    const [pnlStr, setPnlStr] = useState(() => initialData?.pnl?.toString() || '');
    const [durationStr, setDurationStr] = useState(() => initialData?.durationMinutes?.toString() || '');
    const [feesAndCommissionsStr, setFeesAndCommissionsStr] = useState(() => {
    const caf = typeof initialData?.commissionAndFees !== 'undefined' && initialData?.commissionAndFees !== null ? Number(initialData?.commissionAndFees) : undefined;
    if (typeof caf === 'number' && Number.isFinite(caf)) return String(caf.toFixed(2));
    const f = Number(initialData?.fees ?? NaN);
    const c = Number(initialData?.commission ?? NaN);
    if (Number.isFinite(f) || Number.isFinite(c)) {
        const sum = (Number.isFinite(f) ? f : 0) + (Number.isFinite(c) ? c : 0);
        return String(sum.toFixed(2));
    }
    return '';
});

// Active state: when false the field visually shows a "zeroed, faded" placeholder;
// on first focus the field is "activated" (cleared for typing and shown fully).
const [quantityActive, setQuantityActive] = useState<boolean>(() => !!(initialData && typeof initialData.volume !== 'undefined' && initialData.volume !== null));
const [durationActive, setDurationActive] = useState<boolean>(() => !!(initialData && typeof initialData.durationMinutes !== 'undefined' && initialData.durationMinutes !== null));
const [pnlActive, setPnlActive] = useState<boolean>(() => !!(initialData && typeof initialData.pnl !== 'undefined' && initialData.pnl !== null));
const [feesAndCommissionsActive, setFeesAndCommissionsActive] = useState<boolean>(() => {
    return (typeof initialData?.commissionAndFees !== 'undefined' && initialData?.commissionAndFees !== null) ||
        (typeof initialData?.fees !== 'undefined' && initialData?.fees !== null) ||
        (typeof initialData?.commission !== 'undefined' && initialData?.commission !== null);
});

// Duration unit (minutes/hours/days)
const [durationUnit, setDurationUnit] = useState<'m' | 'h' | 'd'>(() => {
    const dm = Number(initialData?.durationMinutes);
    if (Number.isFinite(dm) && dm > 0) {
        if (dm % 1440 === 0) return 'd';
        if (dm % 60 === 0) return 'h';
        return 'm';
    }
    return 'm';
});


    // Reset when modal opens/closes or initialData changes
    useEffect(() => {
        if (isOpen && initialData) {
             setDateTimeStr(formatDateTimeInputInTimezone(Number(initialData.timestamp), displayTimeZone));
             setQuantityStr(initialData.volume?.toString() || '');
             setPnlStr(initialData.pnl?.toString() || '');

             // derive duration string/unit from minutes
             if (Number.isFinite(Number(initialData.durationMinutes))) {
                 const dm = Number(initialData.durationMinutes);
                 if (dm % 1440 === 0) {
                     setDurationUnit('d');
                     setDurationStr(String(dm / 1440));
                 } else if (dm % 60 === 0) {
                     setDurationUnit('h');
                     setDurationStr(String(dm / 60));
                 } else {
                     setDurationUnit('m');
                     setDurationStr(String(dm));
                 }
             } else {
                 setDurationStr('');
             }

            // combined fees+commissions source
            const caf = typeof initialData.commissionAndFees !== 'undefined' && initialData.commissionAndFees !== null
                ? Number(initialData.commissionAndFees)
                : (Number(initialData.fees || 0) + Number(initialData.commission || 0));
            setFeesAndCommissionsStr(Number.isFinite(caf) && caf !== 0 ? String(Number(caf).toFixed(2)) : '');

            // Mark fields active if initial data contains values
            setQuantityActive(typeof initialData.volume !== 'undefined' && initialData.volume !== null);
            setPnlActive(typeof initialData.pnl !== 'undefined' && initialData.pnl !== null);
            setDurationActive(typeof initialData.durationMinutes !== 'undefined' && initialData.durationMinutes !== null);
            setFeesAndCommissionsActive((typeof initialData.commissionAndFees !== 'undefined' && initialData.commissionAndFees !== null) || (typeof initialData.fees !== 'undefined' && initialData.fees !== null) || (typeof initialData.commission !== 'undefined' && initialData.commission !== null));
        } else if (isOpen && !initialData) {
             setDateTimeStr(formatDateTimeInputInTimezone(Date.now(), displayTimeZone));
             setQuantityStr('');
             setPnlStr('');
             setDurationStr('');
            setFeesAndCommissionsStr('');

            // start fields in the "zeroed / faded" inactive state
            setQuantityActive(false);
            setPnlActive(false);
            setDurationActive(false);
            setFeesAndCommissionsActive(false);
        }
    }, [isOpen, initialData, displayTimeZone]);

    if (!isOpen) return null;

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const parsed = resolveSourceTimestamp(dateTimeStr, displayTimeZone);
        if (parsed.timestampMs == null) {
            window.alert(parsed.error === 'ambiguous_local_time'
              ? 'This local time occurs twice because of a daylight-saving transition. Choose a time with an explicit offset or another minute.'
              : parsed.error === 'nonexistent_local_time'
                ? 'This local time does not exist because of a daylight-saving transition.'
                : 'Enter a valid date and time.');
            return;
        }
        const timestamp = parsed.timestampMs;
        const pnl = Number(pnlStr);

        // Basic validation
        if (!Number.isFinite(pnl)) return;

        // Construct the trade object
        const newTrade: ImportedTradeRecord & { id: string } = {
            // Preserve ID if editing, else create new
            id: String(initialData?.id || initialData?.storageId || `${timestamp}_${Math.random()}`),
            storageId: String(initialData?.storageId ?? initialData?.id ?? `${timestamp}_${Math.random()}`), // Keep tracking info for imported
            timestamp,
            symbol: resolvedSymbol, // Match page header/title symbol by default
            // Volume is treated as signed quantity: + buy/add, - sell/subtract
            volume: parseTradeQuantityValue(quantityStr),
            side: pnl >= 0 ? 'Long' : 'Short',
            pnl,
            durationMinutes: Number(durationStr) || undefined,
            notes: initialData?.notes || ''
        };

        // Attach combined fees+commissions if provided
        if (String(feesAndCommissionsStr).trim() !== '') {
            const caf = Number(feesAndCommissionsStr);
            if (Number.isFinite(caf)) {
                newTrade.commissionAndFees = caf;
                // Also set fees for backward compatibility in displays
                newTrade.fees = caf;
                newTrade.commission = 0;
            }
        }

        // Convert duration input (respect unit) -> durationMinutes
        if (durationActive && String(durationStr).trim() !== '') {
            const v = Number(durationStr);
            if (Number.isFinite(v) && v >= 0) {
                const minutes = durationUnit === 'm' ? v : durationUnit === 'h' ? Math.round(v * 60) : Math.round(v * 1440);
                newTrade.durationMinutes = minutes;
            } else {
                newTrade.durationMinutes = undefined;
            }
        } else {
            newTrade.durationMinutes = undefined;
        }

        onAdd(newTrade);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

            {/* Modal Content */}
            <div className="relative w-full max-w-md bg-white dark:bg-[#1a1a1c] p-6 rounded-2xl shadow-2xl border border-zinc-200 dark:border-zinc-800 scrollable overflow-y-auto max-h-[80vh]">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">{initialData ? 'Edit Trade' : 'Manual Trade Entry'}</h3>
                    <button onClick={onClose} className="text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200">
                        <XIcon className="w-5 h-5"/>
                    </button>
                </div>

                <form onSubmit={onSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-medium text-zinc-500 mb-1">Date & Time</label>
                        <div className="relative">
                            <input
                                ref={dateInputRef}
                                type="datetime-local"
                                value={dateTimeStr}
                                onChange={(e) => setDateTimeStr(e.target.value)}
                                className="w-full px-3 py-2 rounded-lg bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 text-sm text-zinc-900 dark:text-zinc-100"
                            />

                            <button
                                type="button"
                                aria-label="Open date/time picker"
                                onClick={openNativePicker}
                                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-md text-zinc-700 dark:text-white hover:bg-zinc-100 dark:hover:bg-[#222226] transition-colors"
                            >
                                <Calendar className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-medium text-zinc-500 mb-1">Quantity</label>
                            <input
                                value={quantityActive ? quantityStr : (quantityStr || '0')}
                                onChange={(e) => setQuantityStr(e.target.value)}
                                onFocus={() => { if (!quantityActive) { setQuantityActive(true); setQuantityStr(''); } }}
                                onBlur={() => { if (quantityActive && String(quantityStr).trim() === '') setQuantityActive(false); }}
                                className={`w-full px-3 py-2 rounded-lg bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 text-sm ${quantityActive ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-400 dark:text-zinc-500'}`}
                                placeholder="1"
                                type="number"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-zinc-500 mb-1">Duration</label>
                            <div className="relative">
                                <input
                                    value={durationActive ? durationStr : (durationStr || '0')}
                                    onChange={(e) => setDurationStr(e.target.value)}
                                    onFocus={() => { if (!durationActive) { setDurationActive(true); setDurationStr(''); } }}
                                    onBlur={() => { if (durationActive && String(durationStr).trim() === '') setDurationActive(false); }}
                                    className={`w-full pr-16 px-3 py-2 rounded-lg bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 text-sm ${durationActive ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-400 dark:text-zinc-500'}`}
                                    placeholder="5"
                                    type="number"
                                />

                                <select
                                    value={durationUnit}
                                    onChange={(e) => { setDurationUnit(e.target.value as 'm'|'h'|'d'); setDurationActive(true); }}
                                    className="absolute right-1 top-1/2 -translate-y-1/2 h-8 bg-zinc-50 dark:bg-zinc-900 border-l border-zinc-200 dark:border-zinc-700 px-2 text-sm rounded-r-md text-zinc-700 dark:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-blue-500/25"
                                >
                                    <option value="m">m</option>
                                    <option value="h">h</option>
                                    <option value="d">d</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-zinc-500 mb-1">P&L ($)</label>
                        <input
                            value={pnlActive ? pnlStr : (pnlStr || '0.00')}
                            onChange={(e) => setPnlStr(e.target.value)}
                            onFocus={() => { if (!pnlActive) { setPnlActive(true); setPnlStr(''); } }}
                            onBlur={() => {
                                if (pnlActive && String(pnlStr).trim() === '') {
                                    setPnlActive(false);
                                } else if (pnlActive && String(pnlStr).trim() !== '') {
                                    const n = Number(pnlStr);
                                    if (Number.isFinite(n)) setPnlStr(n.toFixed(2));
                                }
                            }}
                            className={`w-full px-3 py-2 rounded-lg bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 text-sm font-semibold ${!pnlActive ? 'text-zinc-400 dark:text-zinc-500' : (Number(pnlStr) > 0 ? 'text-green-600' : Number(pnlStr) < 0 ? 'text-red-500' : 'text-zinc-900 dark:text-zinc-100')}`}
                            placeholder="0.00"
                            type="number"
                            step="0.01"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-zinc-500 mb-1">Fees & Commissions ($)</label>
                        <input
                            value={feesAndCommissionsActive ? feesAndCommissionsStr : (feesAndCommissionsStr || '0.00')}
                            onChange={(e) => setFeesAndCommissionsStr(e.target.value)}
                            onFocus={() => { if (!feesAndCommissionsActive) { setFeesAndCommissionsActive(true); setFeesAndCommissionsStr(''); } }}
                            onBlur={() => {
                                if (feesAndCommissionsActive && String(feesAndCommissionsStr).trim() === '') setFeesAndCommissionsActive(false);
                                else if (feesAndCommissionsActive && String(feesAndCommissionsStr).trim() !== '') {
                                    const n = Number(feesAndCommissionsStr);
                                    if (Number.isFinite(n)) setFeesAndCommissionsStr(n.toFixed(2));
                                }
                            }}
                            className={`w-full px-3 py-2 rounded-lg bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 text-sm ${feesAndCommissionsActive ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-400 dark:text-zinc-500'}`}
                            placeholder="0.00"
                            type="number"
                            step="0.01"
                        />
                    </div>
                    <button type="submit" className="w-full py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm transition-colors mt-2">
                        {initialData ? 'Update Execution' : `Add new ${resolvedSymbol} execution`}
                    </button>
                </form>
            </div>
        </div>
    );
};



// --- SUB-COMPONENTS (Defined outside to avoid re-renders and scope issues) ---


function normalizeHeaderSymbol(rawSymbol: string | undefined | null): { symbol: string; name?: string } {
    const raw = String(rawSymbol ?? '').trim();
    if (!raw) return { symbol: MISSING_VALUE };

    // Strip exchange prefix if present (e.g. CME_MINI:ES1!, NASDAQ:AAPL)
    const withoutExchange = raw.includes(':') ? (raw.split(':').pop() || raw) : raw;
    const upper = withoutExchange.toUpperCase().trim();

    // Futures contract patterns: ESZ5, CLF2026, etc.
    // Root = leading letters before month code + year.
    const m = /^([A-Z]+)([FGHJKMNQUVXZ])(\d{1,2}|\d{4})$/.exec(upper);
    const root = m ? m[1] : upper.replace(/\d+!?$/g, '').replace(/!$/g, '');

    const names: Record<string, string> = {
        // Futures / commodities
        ES: 'S&P 500 E-Mini',
        MES: 'Micro E-mini S&P 500',
        NQ: 'Nasdaq-100 E-Mini',
        MNQ: 'Micro E-mini Nasdaq-100',
        RTY: 'Russell 2000 E-Mini',
        M2K: 'Micro E-mini Russell 2000',
        YM: 'Dow Jones E-Mini',
        MYM: 'Micro E-mini Dow',
        CL: 'Crude Oil',
        GC: 'Gold',
        SI: 'Silver',

    };

    const symbol = (root || upper || raw).trim();
    return { symbol, name: names[symbol] ?? getTradeDetailsCompanyName(symbol) };
}

function formatFuturesDisplaySymbol(raw: unknown): string {
    const s = String(raw ?? '').trim().toUpperCase().replace(/^\/+/, '');
    if (!s) return '';

    const noVenue = s.includes(':') ? (s.split(':').pop() || s) : s;
    const m4 = /^([A-Z0-9]{1,8}[FGHJKMNQUVXZ])(\d{4})$/.exec(noVenue);
    if (m4) return `${m4[1]}${m4[2].slice(-2)}`;

    const m2 = /^([A-Z0-9]{1,8}[FGHJKMNQUVXZ])(\d{2})$/.exec(noVenue);
    if (m2) return `${m2[1]}${m2[2]}`;

    const m1 = /^([A-Z0-9]{1,8}[FGHJKMNQUVXZ])(\d)$/.exec(noVenue);
    if (m1) {
        const d = Number(m1[2]);
        if (Number.isFinite(d)) {
            const now = new Date();
            const currentYear = now.getFullYear();
            const base = Math.floor(currentYear / 10) * 10;
            let cand = base + d;
            if (cand < currentYear - 5) cand += 10;
            if (cand > currentYear + 5) cand -= 10;
            return `${m1[1]}${String(cand).slice(-2)}`;
        }
    }

    return noVenue;
}

// --- Company name lookup (client-side cache + live lookup via Yahoo Finance) ---
const _companyCacheKey = 'tt_company_cache_v1';
const _companyCacheTTL = 30 * 24 * 60 * 60 * 1000; // 30 days
const _companyCache = new Map<string, { name: string; ts: number }>();

function _loadCompanyCacheFromStorage() {
    try {
        const raw = localStorage.getItem(_companyCacheKey);
        if (!raw) return;
        const obj = JSON.parse(raw || '{}') as Record<string, { name: string; ts: number }>;
        for (const k of Object.keys(obj)) {
            const v = obj[k];
            if (v && v.name && Number.isFinite(v.ts)) _companyCache.set(k, v);
        }
    } catch { /* ignore */ }
}

function _saveCompanyCacheToStorage() {
    try {
        const obj: Record<string, { name: string; ts: number }> = {};
        _companyCache.forEach((v, k) => { obj[k] = v; });
        localStorage.setItem(_companyCacheKey, JSON.stringify(obj));
    } catch { /* ignore */ }
}

function getCachedCompany(symbol: string): string | null {
    if (!symbol) return null;
    const key = String(symbol).toUpperCase();
    const inMem = _companyCache.get(key);
    if (inMem && (Date.now() - inMem.ts) < _companyCacheTTL) return inMem.name;

    // lazy-load storage cache on first miss
    _loadCompanyCacheFromStorage();
    const stored = _companyCache.get(key);
    if (stored && (Date.now() - stored.ts) < _companyCacheTTL) return stored.name;
    return null;
}

function setCachedCompany(symbol: string, name: string) {
    if (!symbol || !name) return;
    const key = String(symbol).toUpperCase();
    const entry = { name, ts: Date.now() };
    _companyCache.set(key, entry);
    try { _saveCompanyCacheToStorage(); } catch { /* ignore */ }
}

async function fetchCompanyFromYahoo(symbol: string): Promise<string | null> {
    try {
        const q = encodeURIComponent(symbol);
        const url = `https://query1.finance.yahoo.com/v7/finance/quote?symbols=${q}`;
        const res = await fetch(url);
        if (!res.ok) return null;
        const json = await res.json();
        const r = json?.quoteResponse?.result?.[0];
        if (!r) return null;
        return r.longName || r.shortName || r.displayName || null;
    } catch {
        return null;
    }
}

async function lookupCompanyName(symbol: string): Promise<string | null> {
    if (!symbol) return null;
    const s = String(symbol).toUpperCase();

    // 1) static mapping via normalizeHeaderSymbol
    const mapped = normalizeHeaderSymbol(s).name;
    if (mapped) return mapped;

    // 2) cached value
    const cached = getCachedCompany(s);
    if (cached) return cached;

    // 3) remote fetch (best-effort)
    const fetched = await fetchCompanyFromYahoo(s);
    if (fetched) {
        setCachedCompany(s, fetched);
        return fetched;
    }

    return null;
}

function normalizeSecType(raw: unknown): 'STK' | 'FUT' | 'CASH' | 'CRYPTO' | null {
    const s = String(raw ?? '').trim().toUpperCase();
    if (!s) return null;

    if (s === 'STK' || s === 'STOCK' || s === 'STOCKS' || s === 'EQUITY' || s === 'EQ') return 'STK';
    if (s === 'FUT' || s === 'FUTURE' || s === 'FUTURES') return 'FUT';
    if (s === 'CASH' || s === 'FOREX' || s === 'FX' || s === 'CURRENCY') return 'CASH';
    if (s === 'CRYPTO' || s === 'CRYPTOCURRENCY') return 'CRYPTO';

    return null;
}

function instrumentTypeToSecType(instrumentType: 'stock' | 'future' | 'forex' | 'crypto'): 'STK' | 'FUT' | 'CASH' | 'CRYPTO' {
    if (instrumentType === 'future') return 'FUT';
    if (instrumentType === 'forex') return 'CASH';
    if (instrumentType === 'crypto') return 'CRYPTO';
    return 'STK';
}

function secTypeToInstrumentType(secType: 'STK' | 'FUT' | 'CASH' | 'CRYPTO'): 'stock' | 'future' | 'forex' | 'crypto' {
    if (secType === 'FUT') return 'future';
    if (secType === 'CASH') return 'forex';
    if (secType === 'CRYPTO') return 'crypto';
    return 'stock';
}

function toCanonicalUnixSec(value: unknown): number | null {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    if (n <= 0) return null;
    if (n >= 100000000000) return Math.floor(n / 1000);
    return Math.floor(n);
}

function normalizeEpochMs(v: unknown): number | null {
    if (v == null) return null;
    if (v instanceof Date) {
        const t = v.getTime();
        return Number.isFinite(t) ? t : null;
    }
    if (typeof v === 'number') {
        if (!Number.isFinite(v)) return null;
        return v < 1e12 ? v * 1000 : v;
    }
    const n = Number(v);
    if (Number.isFinite(n)) return n < 1e12 ? n * 1000 : n;
    const p = new Date(String(v)).getTime();
    return Number.isFinite(p) ? p : null;
}





const ColumnSettingsModal = ({ isOpen, onClose, visibleColumns, setVisibleColumns }: {
    isOpen: boolean;
    onClose: () => void;
    visibleColumns: string[];
    setVisibleColumns: (columns: string[]) => void;
}) => {
    const dark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

    const handleToggle = (id: string) => {
        const newSet = new Set(visibleColumns);

        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            // no limit — allow toggling any selectable column (locked columns remain handled by UI)
            newSet.add(id);
        }

        setVisibleColumns(Array.from(newSet));
    };



    const handleReset = () => {
        setVisibleColumns(DEFAULT_COLUMNS);
        try { window.localStorage.removeItem('trading-tracker:execution-log:columnWidths:v1'); } catch {}
        try { window.dispatchEvent(new CustomEvent('tt_execution_log_reset_columns')); } catch {}
    };
    // Drag state for modal reordering
    const modalDraggingIdRef = React.useRef<string | null>(null);
    const [modalDragOverId, setModalDragOverId] = React.useState<string | null>(null);

    const modalList = React.useMemo(() => {
        const ordered = Array.isArray(visibleColumns) ? visibleColumns.slice() : [];
        const rest = ALL_COLUMNS.map((c) => c.id).filter((id: string) => !ordered.includes(id));
        return ordered.concat(rest);
    }, [visibleColumns]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div className={`${dark ? 'bg-zinc-900 border border-zinc-800 text-zinc-100' : 'bg-white'} rounded-lg shadow-xl w-full max-w-lg mx-4 relative flex flex-col max-h-[90vh]`}>
                <div className={`p-4 flex justify-between items-center ${dark ? 'border-b border-zinc-800' : 'border-b border-zinc-200'}`}>
                    <h2 className="text-lg font-semibold">Customize Columns</h2>
                    <button onClick={onClose} className={`p-1 rounded-full ${dark ? 'hover:bg-zinc-800' : 'hover:bg-zinc-100'}`}>
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-4 overflow-y-auto">
                    <p className={`${dark ? 'text-zinc-300' : 'text-zinc-600'} text-sm mb-4`}>Choose the columns to display in the Executions Log.</p>
                    <div className="grid grid-cols-1 gap-y-2">
                        {modalList.map((id: string) => {
                            const col: TableColumn = ALL_COLUMNS.find((c) => c.id === id) || { id, label: id };
                            const isChecked = visibleColumns.includes(col.id);
                            const isDisabled = !!col.locked;
                            return (
                                <div
                                    key={col.id}
                                    draggable
                                    onDragStart={(e) => { try { e.dataTransfer.setData('text/plain', col.id); e.dataTransfer.effectAllowed = 'move'; } catch {} modalDraggingIdRef.current = col.id; }}
                                    onDragEnd={() => { modalDraggingIdRef.current = null; setModalDragOverId(null); }}
                                    onDragOver={(e) => { e.preventDefault(); setModalDragOverId(col.id); }}
                                    onDragLeave={() => { if (modalDragOverId === col.id) setModalDragOverId(null); }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        try {
                                            const draggedId = e.dataTransfer.getData('text/plain');
                                            if (!draggedId || draggedId === col.id) return;
                                            const newOrder = visibleColumns.filter((x: string) => x !== draggedId);
                                            const insertAt = newOrder.indexOf(col.id);
                                            if (insertAt === -1) newOrder.push(draggedId); else newOrder.splice(insertAt, 0, draggedId);
                                            setVisibleColumns(newOrder);
                                        } catch {}
                                        setModalDragOverId(null);
                                        modalDraggingIdRef.current = null;
                                    }}
                                    className={`flex items-center justify-between p-2 rounded-md ${modalDragOverId === col.id ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                                >
                                    <label className={`flex items-center space-x-2 ${isDisabled && !isChecked ? 'cursor-not-allowed' : ''}`}>
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => handleToggle(col.id)}
                                            disabled={isDisabled}
                                            className="h-4 w-4 rounded border-zinc-300 accent-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:accent-zinc-500"
                                        />
                                        <span className={`text-sm ${isDisabled ? 'text-zinc-400' : dark ? 'text-zinc-200' : ''}`}>{col.label}</span>
                                    </label>
                                    <div className="text-zinc-400 text-xs">Drag</div>
                                </div>
                            );
                        })}
                    </div>

                </div>
                <div className={`p-4 flex justify-between items-center gap-2 ${dark ? 'border-t border-zinc-800' : 'border-t border-zinc-200'}`}>
                    <div className="min-w-0">
                        <div className="text-sm text-zinc-400">&nbsp;</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className={`px-4 py-2 rounded-lg text-sm ${dark ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-zinc-100 hover:bg-zinc-200'}`}>Save</button>
                        <button onClick={handleReset} className={`px-4 py-2 rounded-lg text-sm ${dark ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-zinc-100 hover:bg-zinc-200'}`}>Reset</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const TradesTable = ({ rows, visibleColumns, setVisibleColumns, onDelete, onEdit, onView, onOpenSettings, headerSymbol, headerName, headerNameIsYahoo, headerDate, onMergeSelected, onUndoMerge, canUndoMerge, tzMode, highlightedRowKey, pnlBadgeMode, onPnlBadgeModeChange }: {
    rows: TradeRecord[];
    visibleColumns: string[];
    setVisibleColumns: (columns: string[]) => void;
    onDelete: (tradeId: string | number | undefined) => void;
    onEdit: (trade: TradeRecord) => void;
    onView: (trade: TradeRecord) => void;
    onOpenSettings: () => void;
    headerSymbol?: string;
    headerName?: string;
    headerNameIsYahoo?: boolean;
    headerDate?: string;
    onMergeSelected?: (selectedKeys: Array<string | number>) => void;
    onUndoMerge?: () => void;
    canUndoMerge?: boolean;
    tzMode: string;
    highlightedRowKey?: string | number | null;
    pnlBadgeMode: 'NET' | 'GROSS';
    onPnlBadgeModeChange: (mode: 'NET' | 'GROSS') => void;
}) => {
    const tableColumns = useMemo(() => visibleColumns.map((id: string) => ALL_COLUMNS.find((c) => c.id === id) || { id, label: id }), [visibleColumns]);

    const setPnlBadgeMode = (next: 'NET' | 'GROSS' | ((prev: 'NET' | 'GROSS') => 'NET' | 'GROSS')) => {
        const resolved = typeof next === 'function' ? next(pnlBadgeMode) : next;
        onPnlBadgeModeChange(resolved);
    };

    // Selection state for merging
    const [selectedKeys, setSelectedKeys] = React.useState<Set<string>>(new Set());

    // DnD-kit state and refs
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const tableRef = useRef<HTMLTableElement | null>(null);
    const headerRefs = useRef<Record<string, HTMLElement | null>>({});
    const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
    const measuredWidths = useRef<Record<string, number>>({});
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
        try {
            const raw = window.localStorage.getItem('trading-tracker:execution-log:columnWidths:v1');
            if (raw) return JSON.parse(raw);
        } catch {}
        return {};
    });
    const resizingRef = useRef<{ id: string; startX: number; startWidth: number } | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [overlayLabel, setOverlayLabel] = useState<string>('');
    const [insertionIndex, setInsertionIndex] = useState<number | null>(null);
    const didDragRef = useRef(false);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

    // Draggable ids (exclude locked columns)
    const draggableIds = tableColumns.map((c) => c.id).filter((id: string) => id !== 'controls' && id !== 'select');

    const getHeaderOrder = () => tableColumns.map((c) => c.id);

    const measureWidths = () => {
        const widths: Record<string, number> = {};
        for (const c of tableColumns) {
            const el = headerRefs.current[c.id];
            if (el) widths[c.id] = Math.round(el.getBoundingClientRect().width);
        }
        measuredWidths.current = widths;
    };

    // Auto-scroll during drag
    const autoScrollRaf = useRef<number | null>(null);
    const autoScrollSpeed = useRef(0);
    const startAutoScroll = () => {
        if (autoScrollRaf.current != null) return;
        const step = () => {
            try {
                const wrap = wrapperRef.current;
                if (!wrap) return;
                if (autoScrollSpeed.current !== 0) {
                    wrap.scrollLeft += autoScrollSpeed.current;
                }
            } finally {
                autoScrollRaf.current = requestAnimationFrame(step);
            }
        };
        autoScrollRaf.current = requestAnimationFrame(step);
    };
    const stopAutoScroll = () => {
        if (autoScrollRaf.current != null) {
            cancelAnimationFrame(autoScrollRaf.current);
            autoScrollRaf.current = null;
        }
        autoScrollSpeed.current = 0;
    };

    const computeDropLeft = (idx: number | null) => {
        const wrap = wrapperRef.current;
        if (!wrap) return 0;
        const rect = wrap.getBoundingClientRect();
        if (idx == null) return 0;
        const cols = tableColumns.map((c) => c.id);
        if (cols.length === 0) return 0;
        if (idx >= cols.length) {
            const last = headerRefs.current[cols[cols.length - 1]];
            if (!last) return 0;
            return Math.round(last.getBoundingClientRect().right - rect.left + wrap.scrollLeft);
        }
        const target = headerRefs.current[cols[idx]];
        if (!target) return 0;
        return Math.round(target.getBoundingClientRect().left - rect.left + wrap.scrollLeft);
    };

    const dropLeftRef = useRef<number>(0);

    const visibleRowsRef = useRef<TradeRecord[]>([]);

    // Listen for a reset request from the Columns modal so we can restore even widths
    React.useEffect(() => {
        const onReset = () => {
            try {
                const wrap = wrapperRef.current;
                const tableEl = tableRef.current;
                const totalWidth = (wrap?.clientWidth ?? tableEl?.clientWidth ?? 1000);
                const cols = tableColumns.length || 1;
                // reserve small room for the left control column percentage (approx 4%)
                const reserved = Math.round(totalWidth * 0.04);
                const usable = Math.max(320, totalWidth - reserved);
                const even = Math.max(80, Math.floor(usable / cols));
                const out: Record<string, number> = {};
                for (const c of tableColumns) out[c.id] = even;
                try { window.localStorage.setItem('trading-tracker:execution-log:columnWidths:v1', JSON.stringify(out)); } catch {}
                setColumnWidths(out);
            } catch {
                try { window.localStorage.removeItem('trading-tracker:execution-log:columnWidths:v1'); } catch {}
                setColumnWidths({});
            }
        };
        window.addEventListener('tt_execution_log_reset_columns', onReset);
        return () => window.removeEventListener('tt_execution_log_reset_columns', onReset);
    }, [tableColumns]);

    const handleDragStart = (event: DragStartEvent) => {
        const id = String(event.active.id || '');
        setActiveId(id);
        const col = tableColumns.find((c) => c.id === id);
        setOverlayLabel(col ? (col.label || col.id) : id);
        measureWidths();
        setIsDragging(true);
        didDragRef.current = true;

        // compute visible rows currently in the table wrapper to show in overlay
        try {
            const wrap = wrapperRef.current;
            const rect = wrap?.getBoundingClientRect();
            if (rect) {
                const visible: TradeRecord[] = [];
                for (const r of rows) {
                    const el = rowRefs.current[String(r.key)];
                    if (!el) continue;
                    const rrect = el.getBoundingClientRect();
                    // intersect vertically with wrapper
                    if (rrect.bottom >= rect.top && rrect.top <= rect.bottom) visible.push(r);
                    if (visible.length >= 12) break;
                }
                if (visible.length === 0) visibleRowsRef.current = rows.slice(0, 12); else visibleRowsRef.current = visible;
            } else {
                visibleRowsRef.current = rows.slice(0, 12);
            }
        } catch {
            visibleRowsRef.current = rows.slice(0, 12);
        }
    };

    const handleDragMove = (event: DragMoveEvent) => {
        const dragEvent = event as DragMoveEvent & { sensorEvent?: MouseEvent; originalEvent?: MouseEvent };
        const clientX = dragEvent.sensorEvent?.clientX ?? dragEvent.originalEvent?.clientX ?? null;
        const wrap = wrapperRef.current;
        if (clientX != null && wrap) {
            const rect = wrap.getBoundingClientRect();
            const edge = 40;
            const leftDist = clientX - rect.left;
            const rightDist = rect.right - clientX;
            let speed = 0;
            if (leftDist < edge) {
                const t = Math.max(0, (edge - leftDist) / edge);
                speed = -Math.round(6 + t * (24 - 6));
            } else if (rightDist < edge) {
                const t = Math.max(0, (edge - rightDist) / edge);
                speed = Math.round(6 + t * (24 - 6));
            } else {
                speed = 0;
            }
            autoScrollSpeed.current = speed;
            if (speed !== 0) startAutoScroll(); else stopAutoScroll();
        }

        const overId = event.over ? String(event.over.id) : null;
        const order = getHeaderOrder();
        const idx = overId ? order.indexOf(overId) : order.length;
        setInsertionIndex(idx === -1 ? order.length : idx);
        dropLeftRef.current = computeDropLeft(idx === -1 ? order.length : idx);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        stopAutoScroll();
        const active = String(event.active.id || '');
        const over = event.over ? String(event.over.id) : null;
        if (over && active && active !== over) {
            const src = visibleColumns.indexOf(active);
            const dest = visibleColumns.indexOf(over);
            if (src !== -1 && dest !== -1) {
                const next = arrayMove(visibleColumns.slice(), src, dest);
                setVisibleColumns(next);
            }
        }
        setIsDragging(false);
        setActiveId(null);
        setInsertionIndex(null);
        // reset measured widths after a frame
        requestAnimationFrame(() => { measuredWidths.current = {}; });
        // clear didDragRef after frame so clicks can be handled
        requestAnimationFrame(() => { didDragRef.current = false; });
    };

    const handleDragCancel = () => {
        stopAutoScroll();
        setIsDragging(false);
        setActiveId(null);
        setInsertionIndex(null);
        measuredWidths.current = {};
        requestAnimationFrame(() => { didDragRef.current = false; });
    };

    // Column resize handlers
    const startColumnResize = (e: React.MouseEvent, id: string) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = (e.nativeEvent as MouseEvent).clientX;
        const startWidth = headerRefs.current[id] ? Math.round(headerRefs.current[id]!.getBoundingClientRect().width) : (measuredWidths.current[id] || 160);
        resizingRef.current = { id, startX, startWidth };
        const onMove = (ev: MouseEvent) => {
            if (!resizingRef.current) return;
            const delta = ev.clientX - resizingRef.current.startX;
            const next = Math.max(48, Math.round(resizingRef.current.startWidth + delta));
            setColumnWidths((prev) => {
                const out = { ...(prev || {}) } as Record<string, number>;
                out[resizingRef.current!.id] = next;
                try { window.localStorage.setItem('trading-tracker:execution-log:columnWidths:v1', JSON.stringify(out)); } catch {}
                return out;
            });
            // update measured widths for drag overlay consistency
            measuredWidths.current[resizingRef.current.id] = next;
        };
        const onUp = () => {
            resizingRef.current = null;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    // Sortable header component
    function SortableHeader(props: { col: TableColumn; index: number; isSortable: boolean }) {
        const { col, isSortable } = props;
        const id = col.id;
        const sortable = useSortable({ id });

        const nodeRef = (el: HTMLElement | null) => { headerRefs.current[id] = el; sortable.setNodeRef(el); };

        const style: React.CSSProperties = {};
        const transform = sortable.transform ? CSS.Transform.toString(sortable.transform) : undefined;
        if (transform) style.transform = transform;
        if (sortable.transition) style.transition = sortable.transition;
        if (sortable.isDragging) style.opacity = 0.6;

        const handleClick = (e: React.MouseEvent) => {
            if (didDragRef.current) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            // existing click-to-sort behavior preserved if present (no-op here)
        };

        return (
            <th ref={nodeRef} scope="col" className={`relative px-4 py-2 text-center font-medium text-zinc-700 dark:text-gray-300 border-r border-zinc-200 dark:border-[#242428] first:border-l first:border-zinc-200 dark:first:border-[#242428]`} style={style}>
                {isSortable ? (
                    <div {...(sortable.attributes ?? {})} {...(sortable.listeners ?? {})} onClick={handleClick} className="cursor-grab select-none">
                        <div className="flex items-center justify-center gap-2 min-w-0">
                            <span className="truncate px-1 font-medium">{col.id === 'volume' ? 'Volume' : col.label}</span>
                            {col.badge ? (
                                // Make the P&L badge interactive: click to toggle NET <-> GROSS
                                col.id === 'pnl' ? (
                                    <TooltipSimple text={pnlBadgeMode === 'NET' ? String(col.badge.tooltip) : 'Gross realized P&L before commissions & fees.'} offsetX={0}>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setPnlBadgeMode(m => m === 'NET' ? 'GROSS' : 'NET'); }}
                                            className="ml-1 inline-flex items-center justify-center text-xs leading-none font-semibold px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 cursor-pointer"
                                            aria-pressed={pnlBadgeMode === 'GROSS'}
                                            aria-label={pnlBadgeMode === 'NET' ? 'Show gross realized P&L' : 'Show net realized P&L'}
                                        >
                                            {pnlBadgeMode}
                                        </button>
                                    </TooltipSimple>
                                ) : (
                                    <TooltipSimple text={String(col.badge.tooltip)} offsetX={0}>
                                        <span className="ml-1 inline-flex items-center justify-center text-[11px] leading-none font-semibold px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700" aria-label={String(col.badge.tooltip)}>
                                            {String(col.badge.text)}
                                        </span>
                                    </TooltipSimple>
                                )
                            ) : null}
                        </div>
                    </div>
                ) : (
                    <div onClick={handleClick} className="select-none">
                        <div className="flex items-center justify-center gap-2 min-w-0">
                            <span className="truncate px-1 font-medium">{col.id === 'volume' ? 'Volume' : col.label}</span>
                            {col.badge ? (
                                <TooltipSimple text={String(col.badge.tooltip)} offsetX={0}>
                                    <span className="ml-1 inline-flex items-center justify-center text-[11px] leading-none font-semibold px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700" aria-label={String(col.badge.tooltip)}>
                                        {String(col.badge.text)}
                                    </span>
                                </TooltipSimple>
                            ) : null}
                        </div>
                    </div>
                )}
                {/* Resize handle (right edge) */}
                <div
                    onMouseDown={(e) => startColumnResize(e, id)}
                    role="separator"
                    aria-orientation="vertical"
                    title="Resize column"
                    className="absolute right-0 top-0 bottom-0 w-2 -mr-2 cursor-col-resize touch-none"
                    style={{ zIndex: 60 }}
                />
            </th>
        );
    }

    // Render cell content for a given column id and trade row — shared between table and ghost
    const renderCellContent = (colId: string, trade: TradeRecord) => {
        const actionText = String(trade.action || '').toUpperCase();
        let isOpenAction = actionText.includes('OPEN') || actionText === 'OPEN';
        let isClosedAction = actionText.includes('CLOSED') || actionText === 'CLOSED' || actionText.includes('CLOSE');
        // If action not present on row object, consult labelsMap by key when available
        try {
            const metaByKey = (labelsMap && labelsMap.byKey && trade && trade.key) ? labelsMap.byKey[trade.key] : undefined;
            if (!isOpenAction && metaByKey && metaByKey.label) {
                const lbl = String(metaByKey.label).toUpperCase();
                if (lbl.includes('OPEN')) isOpenAction = true;
                if (lbl.includes('CLOSED') || lbl.includes('CLOSE')) isClosedAction = true;
            }
        } catch {}

        // Fallback heuristic: if there's no close timestamp (or exit time), treat as open
        try {
            if (!isOpenAction) {
                const hasClose = typeof trade.closeTimestampMs !== 'undefined' ? trade.closeTimestampMs != null : (typeof trade.closeTime !== 'undefined' ? trade.closeTime != null : true);
                const hasOpen = typeof trade.openTimestampMs !== 'undefined' ? trade.openTimestampMs != null : (typeof trade.openTime !== 'undefined' ? trade.openTime != null : true);
                if (hasOpen && !hasClose) isOpenAction = true;
            }
        } catch {}
        if (colId === 'time') return fmtTime24hWithSeconds(trade.timestamp, tzMode);
        if (colId === 'symbol') return <span className="font-semibold text-zinc-900 dark:text-gray-100">{trade.symbol}</span>;
        if (colId === 'tradeId') return <span className="text-xs text-zinc-600 dark:text-zinc-300">{trade.storageId || trade.id || MISSING_VALUE}</span>;
        if (colId === 'volume') {
            if (typeof trade.volume === 'number' && Number.isFinite(trade.volume)) return trade.volume < 0 ? `-${Math.abs(trade.volume)}` : `${trade.volume}`;
            return MISSING_VALUE;
        }
        if (colId === 'price') {
            const raw = (
                (trade as any).price
                ?? (trade as any).avgPrice
                ?? (trade as any).entryPrice
                ?? (trade as any).exitPrice
                ?? (trade as any).entry_price
                ?? (trade as any).avg_price
                ?? (trade as any)?.legs?.[0]?.price
            );
            const p = Number(raw);
            return Number.isFinite(p) ? formatTradePriceValue(p, trade) : MISSING_VALUE;
        }
        if (colId === 'side') {
            const vol = typeof trade.volume === 'number' ? Number(trade.volume) : NaN;
            let rawSide = '';
            if (Number.isFinite(vol) && vol !== 0) rawSide = vol < 0 ? 'sell' : 'buy';
            if (!rawSide) rawSide = String(trade.side ?? '').trim().toLowerCase();
            if (!rawSide) return MISSING_VALUE;
            const isBuy = rawSide.startsWith('l') || rawSide.startsWith('b') || rawSide === 'buy' || rawSide === 'long';
            const isSell = rawSide.startsWith('s') || rawSide === 'sell' || rawSide === 'short';
            if (isBuy) return (<span className="inline-flex items-center justify-center gap-2 px-2 py-1 rounded-md text-xs font-semibold whitespace-nowrap leading-none bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700"><ArrowUp className="w-3.5 h-3.5" /><span className="uppercase text-[11px] tracking-wide leading-none">BUY</span></span>);
            if (isSell) return (<span className="inline-flex items-center justify-center gap-2 px-2 py-1 rounded-md text-xs font-semibold whitespace-nowrap leading-none bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-700"><ArrowDown className="w-3.5 h-3.5" /><span className="uppercase text-[11px] tracking-wide leading-none">SELL</span></span>);
            return MISSING_VALUE;
        }
        if (colId === 'action') {
            let label = MISSING_VALUE;
            const l = actionText;
            if (l.includes('OPEN') || l === 'OPEN') label = 'OPEN';
            if (l.includes('CLOSE') || l === 'CLOSED') label = 'CLOSE';
            return <span className="inline-flex items-center justify-center gap-2 px-2 py-1 rounded-md text-xs font-semibold whitespace-nowrap leading-none border bg-zinc-100 text-zinc-700">{label}</span>;
        }
        if (colId === 'position') return typeof trade.position === 'number' ? trade.position : MISSING_VALUE;
        if (colId === 'pnl' && isOpenAction) return <span className="text-zinc-400">{MISSING_VALUE}</span>;
        if (colId === 'pnl') {
            const chosen = resolveExecutionPnlForMode(trade, pnlBadgeMode);
            if (Number.isFinite(chosen)) {
                return <span className={`font-semibold ${getSignedToneClass(chosen, 'text-green-500', 'text-red-500')}`}>{fmtMoney(chosen)}</span>;
            }

            // Fallback legacy behavior if resolved fields not present
            if (isClosedAction) {
                const contracts = Math.max(1, Math.abs(Number(trade.volume) || 0));
                const display = Number.isFinite(Number(trade.pnl)) ? Number(trade.pnl) * contracts : 0;
                return <span className={`font-semibold ${getSignedToneClass(display, 'text-green-500', 'text-red-500')}`}>{fmtMoney(display)}</span>;
            }
            if (Number.isFinite(Number(trade.pnl))) {
                const num = Number(trade.pnl);
                return <span className={`font-semibold ${getSignedToneClass(num, 'text-green-500', 'text-red-500')}`}>{fmtMoney(num)}</span>;
            }
            return MISSING_VALUE;
        }
        if (colId === 'commission' || colId === 'commissionAndFees') return ((trade.commission_present || trade.commissionAndFees_present) ? <span className={`text-sm font-medium ${getSignedToneClass(-Math.abs(Number(trade[colId] || 0)), 'text-green-500', 'text-red-500')}`}>{formatTopCurrency(-Math.abs(Number(trade[colId] || 0)))}</span> : <span className="text-zinc-400">{MISSING_VALUE}</span>);
        if (colId === 'duration' && isOpenAction) return MISSING_VALUE;
        if (colId === 'duration') return fmtDurationMinutes(trade.durationMinutes);
        if (colId === 'controls') {
            const isDemo = isDemoTrade(trade);
            return (
                <div className="flex items-center justify-center gap-2">
                    <InstantTooltip content="Show on chart">
                        <button onClick={() => onView(trade)} className="text-zinc-400 hover:text-blue-500 transition-colors p-1" title="Show/focus on chart"><Eye className="w-4 h-4" /></button>
                    </InstantTooltip>
                    <InstantTooltip content={isDemo ? "Edit unavailable for demo trades" : "Edit"}>
                        <button
                            disabled={isDemo}
                            onClick={() => !isDemo && onEdit(trade)}
                            className={`p-1 transition-colors ${isDemo ? 'text-zinc-300 dark:text-zinc-700 cursor-not-allowed' : 'text-zinc-400 hover:text-green-500'}`}
                            title=""
                        >
                            <Edit3 className="w-4 h-4" />
                        </button>
                    </InstantTooltip>
                    <InstantTooltip content={isDemo ? "Delete unavailable for demo trades" : "Delete"}>
                        <button
                            disabled={isDemo}
                            onClick={() => !isDemo && onDelete(trade.key || trade.id || trade.storageId)}
                            className={`p-1 transition-colors ${isDemo ? 'text-zinc-300 dark:text-zinc-700 cursor-not-allowed' : 'text-zinc-400 hover:text-red-500'}`}
                            title=""
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </InstantTooltip>
                </div>
            );
        }
        const fallbackValue = trade[colId];
        if (typeof fallbackValue === 'string' || typeof fallbackValue === 'number' || typeof fallbackValue === 'boolean') {
            return fallbackValue;
        }
        return MISSING_VALUE;
    };

    // Drag state for header reordering (visual indicators)

    React.useEffect(() => {
        // keep selection in-sync with visible rows (remove any keys no longer present)
        setSelectedKeys(prev => {
            const next = new Set<string>();
            const keys = new Set((rows || []).map((r) => String(r.key)));
            for (const k of prev) if (keys.has(k)) next.add(k);
            return next;
        });
    }, [rows, pnlBadgeMode]);

    // Export visible executions as CSV. Honors pnlBadgeMode (NET/GROSS) using resolved fields when present.
    const exportExecutionsCsv = () => {
        try {
            const cols = visibleColumns.slice();
            const headers = cols.map((id: string) => (ALL_COLUMNS.find((c) => c.id === id) || { id, label: id }).label);

            const lines: string[] = [];
            lines.push(headers.join(','));

            for (const r of rows) {
                const values: string[] = [];
                for (const id of cols) {
                    let cell: unknown = '';
                    if (id === 'time') cell = fmtTime24hWithSeconds(r.timestamp, tzMode);
                    else if (id === 'price') cell = r.price ?? r.avgPrice ?? '';
                    else if (id === 'volume') cell = typeof r.volume === 'number' ? String(r.volume) : '';
                    else if (id === 'side') cell = r.side ?? '';
                    else if (id === 'action') cell = r.action ?? '';
                    else if (id === 'position') cell = typeof r.positionAfter === 'number' ? String(r.positionAfter) : (typeof r.position === 'number' ? String(r.position) : '');
                    else if (id === 'duration') cell = typeof r.durationMinutes === 'number' ? String(r.durationMinutes) : '';
                    else if (id === 'commissionAndFees') cell = typeof r.commissionAndFees === 'number' ? String(r.commissionAndFees) : '';
                    else if (id === 'pnl') {
                        const chosen = resolveExecutionPnlForMode(r, pnlBadgeMode);
                        if (Number.isFinite(chosen)) cell = String(chosen);
                        else cell = typeof r.pnl === 'number' ? String(r.pnl) : '';
                    } else {
                        cell = r[id] ?? '';
                    }

                    // sanitize
                    let out = String(cell ?? '');
                    out = out.replace(/\r?\n/g, ' ').replace(/,/g, '');
                    values.push(out);
                }
                lines.push(values.join(','));
            }

            const csv = lines.join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `executions-${new Date().toISOString().slice(0,10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export executions CSV failed', err);
            try { window.dispatchEvent(new CustomEvent('tt:toast', { detail: { message: 'Export failed', variant: 'error' } })); } catch {}
        }
    };

    const toggleRow = (key: string | number | undefined) => {
        const k = String(key);
        setSelectedKeys(prev => {
            const next = new Set(prev);
            if (next.has(k)) next.delete(k); else next.add(k);
            return next;
        });
    };

    const selectAllVisible = () => {
        if (!rows || rows.length === 0) return;
        const allKeys = (rows || []).map((r: any) => String(r.key));
        const currentlyAll = allKeys.every((k: string) => selectedKeys.has(k));
        if (currentlyAll) setSelectedKeys(new Set());
        else setSelectedKeys(new Set(allKeys));
    };

    const selectedCount = selectedKeys.size;

    // Compute labels for executions (Long/Short for opening, Add/Subtract while open, Close on close)
    const labelsMap = useMemo(() => {
        const byIndex: Record<number, { label: LabeledExecution['label']; positionAfter: number }> = {};
        const byKey: Record<string | number, { label: LabeledExecution['label']; positionAfter: number }> = {};

        // Group executions by symbol so positions don't bleed across instruments
        const bySymbol = new Map<string, { rowIndex: number; quantity: number; key?: string | number }[]>();

        (rows || []).forEach((r: any, rowIndex: number) => {
            const symbol = String(r.symbol || '').trim();
            const quantity = Number.isFinite(Number(r.volume)) ? Number(r.volume) : 0;
            const exec = { rowIndex, quantity, key: r.key };
            if (!bySymbol.has(symbol)) bySymbol.set(symbol, []);
            bySymbol.get(symbol)!.push(exec);
        });

        // Walk each symbol's executions and assign labels deterministically
        for (const list of bySymbol.values()) {
            let position = 0;
            let openedSide: 'LONG' | 'SHORT' | null = null;

            for (const exec of list) {
                const prevPosition = position;
                position += exec.quantity;

                let label: LabeledExecution['label'];

                // Opening trade
                if (prevPosition === 0 && position !== 0) {
                    openedSide = position > 0 ? 'LONG' : 'SHORT';
                    label = openedSide === 'LONG' ? 'OPEN LONG' : 'OPEN SHORT';
                }

                // Closing trade (only when we actually hit zero)
                else if (position === 0) {
                    const closedSide: 'LONG' | 'SHORT' = openedSide ?? (prevPosition < 0 ? 'SHORT' : 'LONG');
                    label = closedSide === 'LONG' ? 'CLOSED LONG' : 'CLOSED SHORT';
                    openedSide = null;
                }

                // Reversal (crosses through zero): treat as opening the new side
                else if (Math.sign(prevPosition) !== 0 && Math.sign(position) !== 0 && Math.sign(prevPosition) !== Math.sign(position)) {
                    openedSide = position > 0 ? 'LONG' : 'SHORT';
                    label = openedSide === 'LONG' ? 'OPEN LONG' : 'OPEN SHORT';
                }

                // Adding to position
                else if (Math.sign(exec.quantity) === Math.sign(prevPosition)) {
                    label = 'ADDING';
                } else {
                    label = 'SUBTRACTING';
                }

                const meta = { label, positionAfter: position } as { label: LabeledExecution['label']; positionAfter: number };
                byIndex[exec.rowIndex] = meta;
                if (typeof exec.key !== 'undefined') byKey[exec.key] = meta;
            }
        }

        return { byIndex, byKey };
    }, [rows]);

    // Compute totals for the executions table (shown in a footer row)
    const totals = useMemo(() => {
        const t = {
            totalVolume: 0,
            totalPnl: 0,
            totalCommission: 0,
            totalCommissionAndFees: 0,
            totalDuration: 0,
            count: 0,
        } as { totalVolume: number; totalPnl: number; totalCommission: number; totalCommissionAndFees: number; totalDuration: number; count: number };

        // Deduplicate rows by stable key (storageId/key/timestamp) to avoid double-counting
        const seen = new Set<string>();
        const uniqueRows: any[] = [];
        (rows || []).forEach((r: any) => {
            const keyParts = [String(r.storageId ?? r.key ?? ''), String(Number(r.timestamp) || ''), String(Number(r.volume) || '')];
            const stableKey = keyParts.join('|');
            if (seen.has(stableKey)) return;
            seen.add(stableKey);
            uniqueRows.push(r);

            const v = Number.isFinite(Number(r.volume)) ? Number(r.volume) : 0;
            t.totalVolume += Math.abs(v);
            // Use gross/net fields when available depending on header badge mode.
            let pnlValue = resolveExecutionPnlForMode(r, pnlBadgeMode);
            if (!Number.isFinite(pnlValue)) pnlValue = Number.isFinite(Number(r.pnl)) ? Number(r.pnl) : 0;
            t.totalPnl += pnlValue;
            t.totalCommission += Number.isFinite(Number(r.commission)) ? Number(r.commission) : 0;
            const caf = Number.isFinite(Number(r.commissionAndFees))
                ? Number(r.commissionAndFees)
                : ((Number.isFinite(Number(r.fees)) ? Number(r.fees) : 0) + (Number.isFinite(Number(r.commission)) ? Number(r.commission) : 0));
            t.totalCommissionAndFees += caf;
            t.count += 1;
        });

        // Compute canonical position-cycle duration across completed cycles
        const executionsForCycles = uniqueRows.map((r: any) => ({
            id: r.id ?? r.storageId ?? r.key,
            timestamp: Number(r.timestamp),
            side: (r.side || (Number(r.volume) < 0 ? 'sell' : 'buy')) as any,
            qty: Math.abs(Number(r.volume) || 0),
            price: Number(r.price ?? r.avgPrice ?? 0),
        })).filter(e => Number.isFinite(e.timestamp) && e.qty > 0 && Number.isFinite(e.price));

        if (executionsForCycles.length > 0) {
            const state = computeDeterministicPositionState({ executions: executionsForCycles, multiplier: 1, markPrice: null });
            const closedPhases = state.phases.filter(phase => phase.endTime != null && phase.endTime >= phase.startTime);
            const totalElapsedMs = closedPhases.reduce((sum, phase) => sum + ((phase.endTime as number) - phase.startTime), 0);
            t.totalDuration = Math.max(0, Math.round(totalElapsedMs / 60000));
        }

        return t;
    }, [rows, pnlBadgeMode]);

    return (
        <div className="bg-white dark:bg-[#1a1a1c] rounded-xl border border-zinc-200 dark:border-[#242428] shadow-md overflow-visible">
                {/* Header with Separator (applied via border-b on container) - Updated to match ticker line */}
                <div className="p-4 flex flex-col gap-3 border-b border-zinc-200 dark:border-zinc-700 relative md:flex-row md:items-center md:gap-0">
                    <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 md:gap-3">
                        <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Execution Log</h2>
                        <TooltipIcon text={"Complete list of trade executions."} offsetX={20} />

                        <span aria-hidden="true" className="w-px h-5 bg-zinc-200 dark:bg-zinc-700 inline-block rounded" />

                        <div className="inline-flex flex-wrap items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400 md:ml-1">
                            <Calendar className="w-4 h-4" />
                            <span className="whitespace-nowrap">{headerDate ?? MISSING_VALUE}</span>

                        </div>
                    </div>

                    {/* Center: absolutely centered ticker + company (keeps perfect centering regardless of left/right widths) */}
                    <div className="hidden md:block absolute left-1/2 top-[46%] -translate-x-1/2 -translate-y-1/2 pointer-events-none w-max">
                        {headerSymbol || headerName ? (
                            <div className="inline-block pointer-events-auto">
                                {headerNameIsYahoo && headerSymbol ? (
                                    <a
                                        href={`https://finance.yahoo.com/quote/${encodeURIComponent(String(headerSymbol).toUpperCase())}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Open on Yahoo Finance"
                                        className="inline-flex items-baseline gap-3 justify-center hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500/30 rounded"
                                    >
                                        {headerSymbol ? (
                                            <div className="text-2xl sm:text-3xl font-bold tracking-wide text-zinc-900 dark:text-white">
                                                {String(headerSymbol).toUpperCase()}
                                            </div>
                                        ) : null}

                                        {headerName ? (
                                            <div className="text-sm sm:text-base text-zinc-500 dark:text-zinc-400">
                                                {headerName}
                                            </div>
                                        ) : null}
                                    </a>
                                ) : (
                                    <div className="flex items-baseline gap-3 justify-center">
                                        {headerSymbol ? (
                                            <div className="text-2xl sm:text-3xl font-bold tracking-wide text-zinc-900 dark:text-white">
                                                {String(headerSymbol).toUpperCase()}
                                            </div>
                                        ) : null}

                                        {headerName ? (
                                            <div className="text-sm sm:text-base text-zinc-500 dark:text-zinc-400">
                                                {headerName}
                                            </div>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>

                    <div className="md:hidden">
                        {headerSymbol || headerName ? (
                            <div className="flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1 text-center">
                                {headerSymbol ? (
                                    <div className="text-xl font-bold tracking-wide text-zinc-900 dark:text-white">
                                        {String(headerSymbol).toUpperCase()}
                                    </div>
                                ) : null}

                                {headerName ? (
                                    <div className="text-sm text-zinc-500 dark:text-zinc-400">
                                        {headerName}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 md:ml-auto md:flex-nowrap">
                            {isFeatureEnabled(currentRuntime.features, 'exportCsv') && (
                                <button
                                    type="button"
                                    onClick={() => exportExecutionsCsv()}
                                    className="flex items-center gap-2 px-3 h-9 rounded-lg border text-sm font-semibold bg-zinc-100 text-zinc-800 border-zinc-200 hover:bg-zinc-50 dark:bg-[#1a1a1c] dark:border-gray-700 dark:text-gray-100 dark:hover:bg-[#222226] transition-colors"
                                    title="Export executions CSV"
                                >
                                    <Download className="w-4 h-4" />
                                    <span>Export</span>
                                </button>
                            )}
                        {canUndoMerge && (
                            <button
                                type="button"
                                onClick={() => {
                                    if (canUndoMerge && onUndoMerge) onUndoMerge();
                                }}
                                className={`flex items-center gap-2 px-3 h-9 rounded-lg border text-sm font-semibold transition-colors ${'bg-green-500 text-white border-transparent hover:bg-green-600'}`}
                                title={'Undo last merge'}
                            >
                                <span>Undo Merge</span>
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => {
                                if (selectedCount >= 2 && onMergeSelected) {
                                    onMergeSelected(Array.from(selectedKeys));
                                    setSelectedKeys(new Set());
                                }
                            }}
                            disabled={selectedCount < 2}
                            className={`flex items-center gap-2 px-3 h-9 rounded-lg border text-sm font-semibold transition-colors ${selectedCount >= 2 ? 'bg-blue-500 text-white border-transparent hover:bg-blue-600' : 'bg-zinc-100 text-zinc-800 border-zinc-200 hover:bg-zinc-50 dark:bg-[#1a1a1c] dark:border-gray-700 dark:text-gray-100 dark:hover:bg-[#222226]'}`}
                            title={selectedCount >= 2 ? `Merge ${selectedCount} trades` : 'Select 2+ trades to enable merge'}
                        >
                            <Layers2 className={`w-4 h-4 ${selectedCount >= 2 ? 'text-white' : ''}`} />
                            <span>Merge Trades</span>
                        </button>
                        {/* Add Trade intentionally hidden per request */}
                        {/* Columns settings button (moved to far right) */}
                        <button
                            onClick={() => onOpenSettings && onOpenSettings()}
                            className="flex items-center gap-2 px-3 h-9 rounded-lg border text-sm font-semibold bg-zinc-100 text-zinc-800 border-zinc-200 hover:bg-zinc-50 dark:bg-[#1a1a1c] dark:border-gray-700 dark:text-gray-100 dark:hover:bg-[#222226] transition-colors"
                            title="Customize columns"
                        >
                            <Settings className="w-4 h-4" />
                            <span>Columns</span>
                        </button>

                    </div>
                </div>

            <div ref={(el) => wrapperRef.current = el} className="relative overflow-x-hidden">
                <table ref={(el) => tableRef.current = el} className="w-full text-sm table-fixed min-w-[800px]">
                    <colgroup>
                        <col style={{ width: '4%' }} />
                                                    {tableColumns.map((col: any) => (
                            <col key={col.id} style={
                                columnWidths && columnWidths[col.id]
                                    ? { width: `${columnWidths[col.id]}px` }
                                    : (isDragging && measuredWidths.current[col.id] ? { width: `${measuredWidths.current[col.id]}px` } : undefined)
                            } />
                        ))}
                    </colgroup>
                    <thead className="border-b border-zinc-200 dark:border-[#242428]/80 select-none sticky top-0 bg-inherit z-20">
                        <DndContext
                            sensors={sensors}
                            collisionDetection={undefined}
                            onDragStart={handleDragStart}
                            onDragMove={handleDragMove}
                            onDragEnd={handleDragEnd}
                            onDragCancel={handleDragCancel}
                        >
                            <SortableContext items={tableColumns.map((c: any) => c.id)} strategy={horizontalListSortingStrategy}>
                                <tr className="bg-zinc-50 dark:bg-[#222226]">
                                    <th className="w-[4%] whitespace-nowrap px-1 py-2 text-center font-medium text-zinc-700 dark:text-gray-300 border-r border-zinc-200 dark:border-[#242428]">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4"
                                            disabled={rows && rows.some(r => isDemoTrade(r))}
                                            checked={rows && rows.length > 0 && selectedKeys.size === rows.length}
                                            onChange={selectAllVisible}
                                            aria-label="Select all executions"
                                        />
                                    </th>
                                                {tableColumns.map((col: any, idx: number) => (
                                                    <SortableHeader
                                                        key={col.id}
                                                        col={col}
                                                        index={idx}
                                                        isSortable={draggableIds.includes(col.id)}
                                                    />
                                                ))}
                                </tr>
                            </SortableContext>
                            <DragOverlay>
                                {activeId && draggableIds.includes(activeId) ? (() => {
                                    const w = measuredWidths.current[activeId] || 160;
                                    const wrapperH = wrapperRef.current ? wrapperRef.current.clientHeight : undefined;
                                    const headerH = headerRefs.current[activeId] ? Math.round(headerRefs.current[activeId]!.getBoundingClientRect().height) : 40;
                                    const bodyH = wrapperH ? Math.max(80, wrapperH - headerH) : undefined;
                                    return (
                                        <div style={{ width: w, opacity: 0.45 }} className="">
                                            <div className="bg-zinc-50 dark:bg-[#111318] rounded shadow-lg border border-zinc-200 dark:border-[#242428] overflow-hidden" style={{ height: wrapperH ?? 'auto' }}>
                                                <div className="px-4 py-2 font-medium text-sm text-zinc-800 dark:text-zinc-100">{overlayLabel}</div>
                                                <div className="divide-y divide-zinc-100 dark:divide-gray-800" style={{ height: bodyH ?? 'auto', overflowY: 'auto' }}>
                                                    {(visibleRowsRef.current.length ? visibleRowsRef.current : rows).map((r: any, i: number) => (
                                                        <div key={i} className="px-3 py-2 text-sm text-zinc-700 dark:text-zinc-300">
                                                            {renderCellContent(activeId, r)}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })() : null}
                            </DragOverlay>
                        </DndContext>
                    </thead>
                    <tbody>
                        {rows.length ? rows.map((trade: any, rowIndex: number) => (
                            <tr
                                ref={(el) => { try { rowRefs.current[String(trade.key)] = el; } catch {} }}
                                key={trade.key}
                                className={`border-b border-zinc-200 dark:border-[#242428]/80 hover:bg-zinc-50 dark:hover:bg-[#222226]/40 transition-colors ${String(highlightedRowKey ?? '') === String(trade.key ?? '') ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                            >
                                <td className="w-[4%] whitespace-nowrap px-1 py-2.5 text-center border-r border-zinc-200 dark:border-[#242428]">
                                    <input
                                        type="checkbox"
                                        className="w-4 h-4"
                                        disabled={isDemoTrade(trade)}
                                        checked={selectedKeys.has(String(trade.key))}
                                        onChange={() => !isDemoTrade(trade) && toggleRow(trade.key)}
                                        aria-label={`Select execution ${trade.key}`}
                                    />
                                </td>
                                    {tableColumns.map((col: any) => (
                                    <td key={col.id} className="px-4 py-2.5 text-center border-r border-zinc-200 dark:border-[#242428] first:border-l first:border-zinc-200 dark:first:border-[#242428]">
                                        {col.id === 'time' && fmtTime24hWithSeconds(trade.timestamp, tzMode)}
                                        {col.id === 'symbol' && <span className="font-semibold text-zinc-900 dark:text-gray-100">{trade.symbol}</span>}
                                        {col.id === 'tradeId' && <span className="text-xs text-zinc-600 dark:text-zinc-300">{trade.storageId || trade.id || MISSING_VALUE}</span>}
                                        {col.id === 'price' && (
                                            (() => {
                                                // Match marker preference: exec.price -> exec.avgPrice -> trade entry/exit price fallbacks
                                                const raw = (
                                                    (trade as any).price
                                                    ?? (trade as any).avgPrice
                                                    ?? (trade as any).entryPrice
                                                    ?? (trade as any).exitPrice
                                                    ?? (trade as any).entry_price
                                                    ?? (trade as any).avg_price
                                                    ?? (trade as any)?.legs?.[0]?.price
                                                );
                                                const p = Number(raw);
                                                return Number.isFinite(p) ? formatTradePriceValue(p, trade) : MISSING_VALUE;
                                            })()
                                        )}
                                        {col.id === 'volume' && (
                                            (typeof trade.volume === 'number' && Number.isFinite(trade.volume))
                                                ? (trade.volume < 0 ? `-${Math.abs(trade.volume)}` : `${trade.volume}`)
                                                : MISSING_VALUE
                                        )}
                                        {col.id === 'side' && (() => {
                                            // Prefer explicit execution sign from `volume` when present; fallback to row.side/trade.side
                                            const vol = typeof trade.volume === 'number' ? Number(trade.volume) : NaN;
                                            let rawSide = '';
                                            if (Number.isFinite(vol) && vol !== 0) rawSide = vol < 0 ? 'sell' : 'buy';
                                            if (!rawSide) rawSide = String(trade.side ?? '').trim().toLowerCase();
                                            if (!rawSide) return MISSING_VALUE;

                                            const isBuy = rawSide.startsWith('l') || rawSide.startsWith('b') || rawSide === 'buy' || rawSide === 'long';
                                            const isSell = rawSide.startsWith('s') || rawSide === 'sell' || rawSide === 'short';

                                            if (isBuy) {
                                                return (
                                                    <span className="inline-flex items-center justify-center gap-2 px-2 py-1 rounded-md text-xs font-semibold whitespace-nowrap leading-none bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700">
                                                        <ArrowUp className="w-3.5 h-3.5" />
                                                        <span className="uppercase text-[11px] tracking-wide leading-none">BUY</span>
                                                    </span>
                                                );
                                            }
                                            if (isSell) {
                                                return (
                                                    <span className="inline-flex items-center justify-center gap-2 px-2 py-1 rounded-md text-xs font-semibold whitespace-nowrap leading-none bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-700">
                                                        <ArrowDown className="w-3.5 h-3.5" />
                                                        <span className="uppercase text-[11px] tracking-wide leading-none">SELL</span>
                                                    </span>
                                                );
                                            }
                                            return <span className="inline-flex items-center justify-center px-2 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap leading-none bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700">{MISSING_VALUE}</span>;
                                        })()}

                                        {col.id === 'action' && (() => {
                                            // Merged rows show explicit MERGED pill
                                            if (trade && (trade as any).merged) {
                                                return (
                                                    <span className={`inline-flex items-center justify-center gap-2 px-2 py-1 rounded-md text-xs font-semibold whitespace-nowrap leading-none border bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-700`}>
                                                        <Layers2 className="w-3.5 h-3.5" />
                                                        <span className="uppercase text-[11px] tracking-wide leading-none">MERGED</span>
                                                    </span>
                                                );
                                            }

                                            const explicitAction = String((trade as any).action ?? '').toUpperCase();
                                            if (explicitAction) {
                                                const actionText = explicitAction === 'OPEN'
                                                    ? 'Open'
                                                    : explicitAction === 'SCALE_IN'
                                                        ? 'Scale in'
                                                        : explicitAction === 'SCALE_OUT'
                                                            ? 'Scale out'
                                                            : explicitAction === 'CLOSE'
                                                                ? 'Close'
                                                                : MISSING_VALUE;

                                                const classByAction = actionText === 'Open' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-700' :
                                                    actionText === 'Scale in' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700' :
                                                    actionText === 'Scale out' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-700' :
                                                    'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200 border-zinc-200 dark:border-zinc-600';

                                                let iconForAction: JSX.Element | null = null;
                                                if (actionText === 'Scale in') iconForAction = <ArrowUpRight className="w-3.5 h-3.5" />;
                                                else if (actionText === 'Scale out') iconForAction = <ArrowDownRight className="w-3.5 h-3.5" />;
                                                else if (actionText === 'Open') iconForAction = <ArrowRight className="w-3.5 h-3.5" />;
                                                else if (actionText === 'Close') iconForAction = <XIcon className="w-3.5 h-3.5" />;

                                                return (
                                                    <span className={`inline-flex items-center justify-center gap-2 px-2 py-1 rounded-md text-xs font-semibold whitespace-nowrap leading-none border ${classByAction}`}>
                                                        {iconForAction}
                                                        <span className="uppercase text-[11px] tracking-wide leading-none">{actionText}</span>
                                                    </span>
                                                );
                                            }

                                            const metaByKey = labelsMap.byKey && trade.key ? labelsMap.byKey[trade.key] : undefined;
                                            const metaByIndex = labelsMap.byIndex ? labelsMap.byIndex[rowIndex] : undefined;
                                            const labelRaw = (metaByKey ?? metaByIndex)?.label;
                                            if (!labelRaw) return MISSING_VALUE;
                                            const label = String(labelRaw).toUpperCase();
                                            let actionText = MISSING_VALUE;
                                            if (label.includes('OPEN')) actionText = 'Open';
                                            else if (label === 'ADDING') actionText = 'Scale in';
                                            else if (label === 'SUBTRACTING') actionText = 'Scale out';
                                            else if (label.includes('CLOSED')) actionText = 'Close';

                                            const classByAction = actionText === 'Open' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-700' :
                                                actionText === 'Scale in' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700' :
                                                actionText === 'Scale out' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-700' :
                                                'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200 border-zinc-200 dark:border-zinc-600';

                                            // Choose an icon that reflects the action per design spec
                                            let iconForAction: JSX.Element | null = null;
                                            if (actionText === 'Scale in') iconForAction = <ArrowUpRight className="w-3.5 h-3.5" />;
                                            else if (actionText === 'Scale out') iconForAction = <ArrowDownRight className="w-3.5 h-3.5" />;
                                            else if (actionText === 'Open') iconForAction = <ArrowRight className="w-3.5 h-3.5" />;
                                            else if (actionText === 'Close') iconForAction = <XIcon className="w-3.5 h-3.5" />;

                                            return (
                                                <span className={`inline-flex items-center justify-center gap-2 px-2 py-1 rounded-md text-xs font-semibold whitespace-nowrap leading-none border ${classByAction}`}>
                                                    {iconForAction}
                                                    <span className="uppercase text-[11px] tracking-wide leading-none">{actionText}</span>
                                                </span>
                                            );
                                        })()}

                                        {col.id === 'position' && (() => {
                                            if (Number.isFinite(Number((trade as any).positionAfter))) return Number((trade as any).positionAfter);
                                            const metaByKey = labelsMap.byKey && trade.key ? labelsMap.byKey[trade.key] : undefined;
                                            const metaByIndex = labelsMap.byIndex ? labelsMap.byIndex[rowIndex] : undefined;
                                            const posAfter = (metaByKey ?? metaByIndex)?.positionAfter;
                                            return typeof posAfter === 'number' ? posAfter : MISSING_VALUE;
                                        })()}

                                        {col.id === 'pnl' && (() => {
                                            const explicitResolvedQty = Number((trade as any).rowResolvedQty ?? (trade as any).closedQty);
                                            const explicitRowGross = Number((trade as any).rowRealizedGross);
                                            const explicitRowNet = Number((trade as any).rowRealizedNet ?? trade.pnl);
                                            const hasExplicitResolverData = Number.isFinite(explicitResolvedQty)
                                                || Number.isFinite(explicitRowGross)
                                                || Number.isFinite(explicitRowNet);
                                            if (hasExplicitResolverData) {
                                                const shouldRender = Number.isFinite(explicitResolvedQty) && explicitResolvedQty > 0;
                                                if (!shouldRender) return <span className="text-zinc-400">{MISSING_VALUE}</span>;
                                                const pnlValue = resolveExecutionPnlForMode(trade, pnlBadgeMode);
                                                if (!Number.isFinite(pnlValue)) return <span className="text-zinc-400">{MISSING_VALUE}</span>;
                                                return <span className={`font-semibold ${pnlValue >= 0 ? 'text-green-500' : 'text-red-500'}`}>{fmtMoney(pnlValue)}</span>;
                                            }

                                            const explicitClosedQty = Number(
                                                (trade as any).closedQty
                                                ?? (trade as any).closed_quantity
                                                ?? (trade as any).closeQty
                                                ?? (trade as any).closedVolume
                                            );

                                            let closedQty = 0;
                                            if (Number.isFinite(explicitClosedQty) && explicitClosedQty >= 0) {
                                                closedQty = Math.abs(explicitClosedQty);
                                            } else {
                                                const metaByKey = labelsMap.byKey && trade.key ? labelsMap.byKey[trade.key] : undefined;
                                                const metaByIndex = labelsMap.byIndex ? labelsMap.byIndex[rowIndex] : undefined;
                                                const label = String((metaByKey ?? metaByIndex)?.label ?? '').toUpperCase();
                                                const isClosingAction = label === 'SUBTRACTING' || label.includes('CLOSED') || label.includes('CLOSE');
                                                if (isClosingAction) {
                                                    const vol = Number(trade.volume);
                                                    closedQty = Number.isFinite(vol) ? Math.abs(vol) : 0;
                                                }
                                            }

                                            if (closedQty <= 0) return <span className="text-zinc-400">{MISSING_VALUE}</span>;

                                            const pnlValue = resolveExecutionPnlForMode(trade, pnlBadgeMode);
                                            if (!Number.isFinite(pnlValue)) return <span className="text-zinc-400">—</span>;
                                            return <span className={`font-semibold ${pnlValue >= 0 ? 'text-green-500' : 'text-red-500'}`}>{fmtMoney(pnlValue)}</span>;
                                        })()}
                                        {col.id === 'commission' && (
                                            (trade.commission_present)
                                                ? <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">{fmtMoney(Number(trade.commission || 0))}</span>
                                                : <span className="text-zinc-400">—</span>
                                        )}
                                        {col.id === 'commissionAndFees' && (
                                            (trade.commissionAndFees_present || trade.commission_present || trade.fees_present)
                                                ? <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">{fmtMoney(Number(trade.commissionAndFees || 0))}</span>
                                                : <span className="text-zinc-400">—</span>
                                        )}
                                        {col.id === 'duration' && (() => {
                                            const explicitResolvedQty = Number((trade as any).rowResolvedQty ?? (trade as any).closedQty);
                                            if (Number.isFinite(explicitResolvedQty)) {
                                                if (explicitResolvedQty <= 0) return MISSING_VALUE;
                                                return fmtDurationMinutes(trade.durationMinutes);
                                            }

                                            // Legacy fallback for imported rows that only expose close-like semantics
                                            const explicitClosedQty = Number(
                                                (trade as any).closedQty
                                                ?? (trade as any).closed_quantity
                                                ?? (trade as any).closeQty
                                                ?? (trade as any).closedVolume
                                            );
                                            let isClosed = false;
                                            if (Number.isFinite(explicitClosedQty) && explicitClosedQty > 0) isClosed = true;
                                            if (!isClosed) {
                                                const metaByKey = labelsMap.byKey && trade.key ? labelsMap.byKey[trade.key] : undefined;
                                                const metaByIndex = labelsMap.byIndex ? labelsMap.byIndex[rowIndex] : undefined;
                                                const labelRaw = (metaByKey ?? metaByIndex)?.label;
                                                const labelUp = labelRaw ? String(labelRaw).toUpperCase() : '';
                                                if (labelUp.includes('CLOSED') || labelUp.includes('CLOSE') || labelUp === 'SUBTRACTING') isClosed = true;
                                            }
                                            if (!isClosed) return MISSING_VALUE;
                                            return fmtDurationMinutes(trade.durationMinutes);
                                        })()}
                                        {col.id === 'controls' && (() => {
                                            const isDemo = isDemoTrade(trade);
                                            return (
                                                <div className="flex items-center justify-center gap-2">
                                                    <InstantTooltip content="Show on chart">
                                                        <button onClick={() => onView(trade)} className="text-zinc-400 hover:text-blue-500 transition-colors p-1" title="Show/focus on chart"><Eye className="w-4 h-4" /></button>
                                                    </InstantTooltip>
                                                    <InstantTooltip content={isDemo ? "Edit unavailable for demo trades" : "Edit"}>
                                                        <button
                                                            disabled={isDemo}
                                                            onClick={() => !isDemo && onEdit(trade)}
                                                            className={`p-1 transition-colors ${isDemo ? 'text-zinc-300 dark:text-zinc-700 cursor-not-allowed' : 'text-zinc-400 hover:text-green-500'}`}
                                                            title=""
                                                        >
                                                            <Edit3 className="w-4 h-4" />
                                                        </button>
                                                    </InstantTooltip>
                                                    <InstantTooltip content={isDemo ? "Delete unavailable for demo trades" : "Delete"}>
                                                        <button
                                                            disabled={isDemo}
                                                            onClick={() => !isDemo && onDelete(trade.key || trade.id || trade.storageId)}
                                                            className={`p-1 transition-colors ${isDemo ? 'text-zinc-300 dark:text-zinc-700 cursor-not-allowed' : 'text-zinc-400 hover:text-red-500'}`}
                                                            title=""
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </InstantTooltip>
                                                </div>
                                            );
                                        })()}
                                        {/* Fallback for other columns (exclude numeric/currency/explicitly-rendered columns) */}
                                        {!['time','symbol','tradeId','price','volume','side','action','position','pnl','duration','controls','commission','commissionAndFees'].includes(col.id) && (trade[col.id] ?? MISSING_VALUE)}
                                    </td>
                                ))}
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={tableColumns.length + 1} className="px-4 py-10 text-center text-zinc-500 dark:text-gray-500">
                                    No trades for this day
                                </td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr className="bg-zinc-50 dark:bg-[#222226] border-t-2 border-zinc-300 dark:border-[#242428]">
                            <td className="w-[1%] whitespace-nowrap px-1 py-3 text-center border-none font-bold text-sm md:text-base">&nbsp;</td>
                            {tableColumns.map((col: any, ci: number) => (
                                                <td key={col.id} className="px-6 py-3 text-center border-none font-bold text-sm md:text-base" style={isDragging && measuredWidths.current[col.id] ? { width: `${measuredWidths.current[col.id]}px`, minWidth: `${measuredWidths.current[col.id]}px`, maxWidth: `${measuredWidths.current[col.id]}px` } : undefined}>
                                    {ci === 0 ? (
                                         <span className="whitespace-nowrap">Session Totals</span>
                                    ) : col.id === 'volume' ? (
                                        totals.totalVolume
                                    ) : col.id === 'pnl' ? (
                                        <span className={`${totals.totalPnl >= 0 ? 'text-green-500' : 'text-red-400'}`}>{fmtMoney(totals.totalPnl)}</span>
                                    ) : col.id === 'commission' ? (
                                        fmtMoney(totals.totalCommission)
                                    ) : col.id === 'commissionAndFees' ? (
                                        fmtMoney(totals.totalCommissionAndFees)
                                    ) : col.id === 'duration' ? (
                                        fmtDurationMinutes(totals.totalDuration)
                                    ) : col.id === 'executionCount' ? (
                                        totals.count
                                    ) : (
                                        ''
                                    )}
                                </td>
                            ))}
                        </tr>
                    </tfoot>
                </table>
                {isDragging && insertionIndex !== null && (
                    <div className="pointer-events-none absolute top-0 bottom-0" style={{ left: dropLeftRef.current, width: 2, background: 'rgba(59,130,246,0.9)', transition: 'left 120ms linear' }} />
                )}
            </div>
        </div>
    );
};

// --- MAIN DASHBOARD COMPONENT ---

interface JournalEntryPageProps {
  date: Date | null | string;
  onNavigateDate?: (d: Date) => void;
    focusSymbol?: string;
    trade?: Trade | null;
    onSaveInitialRisk?: (amount: number | null, method: InitialRiskMethod | null, stopPrice: number | null) => Promise<void> | void;
    onEditTrade?: () => void;
        tradeNotes?: { value: string; onChange: (html: string) => void };
    tradeTags?: { value: string[]; onAdd: (tag: string) => void; onRemove: (tag: string) => void };
    onNavigateTrade?: (t: Trade) => void;
    prevTrade?: Trade | null;
    nextTrade?: Trade | null;
    comparableTrades?: Trade[];
    forcedExecutions?: Array<{
        time: number;
        timestamp?: number;
        openTimestampMs?: number | null;
        price: number;
        side: 'buy' | 'sell';
        qty: number;
        volume?: number;
        commission?: number;
        fees?: number;
        commissionAndFees?: number;
        rowRealizedNet?: number;
        rowRealizedGross?: number;
        rowFees?: number;
        action?: ExecutionRowAction | string;
        symbol?: string;
        assetClass?: string;
        instrument?: string;
        positionAfter?: number;
        closedQty?: number;
        rowResolvedQty?: number;
    }>;
    chartExecutionSourceMessage?: string | null;
}

const JournalEntryPage: React.FC<JournalEntryPageProps> = ({ date: propDate, onNavigateDate, focusSymbol, trade, onSaveInitialRisk, onEditTrade, tradeNotes, tradeTags, onNavigateTrade, prevTrade, nextTrade, comparableTrades = [], forcedExecutions, chartExecutionSourceMessage }) => {
    const imported = useImportedTradesRecords();
    const selectedTradeIsDemo = React.useMemo(() => {
        return isDemoTrade(trade);
    }, [trade]);
    const [metricDisplayMode, setMetricDisplayMode] = React.useState<'NET' | 'GROSS'>(() => pnlMode.getPnlMode());
    const [riskEditorOpen, setRiskEditorOpen] = React.useState(false);
    const [riskMethod, setRiskMethod] = React.useState<InitialRiskMethod>('amount');
    const [riskAmountInput, setRiskAmountInput] = React.useState('');
    const [riskStopInput, setRiskStopInput] = React.useState('');
    const [riskSaveError, setRiskSaveError] = React.useState('');
    const [riskSaving, setRiskSaving] = React.useState(false);
    const riskDialogRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        if (!riskEditorOpen) return;
        riskDialogRef.current?.animate?.([
            { boxShadow: '0 0 0 0 rgba(59, 130, 246, 0)' },
            { boxShadow: '0 0 0 7px rgba(59, 130, 246, 0.8)' },
            { boxShadow: '0 0 0 0 rgba(59, 130, 246, 0)' },
        ], { duration: 850, iterations: 1, easing: 'ease-out' });
    }, [riskEditorOpen]);

    React.useEffect(() => {
        return pnlMode.onPnlModeChange((mode) => {
            if (mode === 'NET' || mode === 'GROSS') setMetricDisplayMode(mode);
        });
    }, []);
    const updatePnlMode = React.useCallback((mode: 'NET' | 'GROSS') => {
        setMetricDisplayMode(mode);
        try { pnlMode.setPnlMode(mode); } catch {}
    }, []);

    const tzMode = useDisplayTimezone();

    const { headerDate, baseDate } = React.useMemo(() => {
        const d = typeof propDate === 'string' ? new Date(propDate) : (propDate ?? null);
        const safe = d && Number.isFinite(d.getTime()) ? d : null;
        const header = safe
            ? formatChartTime(Math.floor(safe.getTime()/1000), 'local', true, tzMode)
            : FALLBACK_DATE_LABEL;
        return { headerDate: header, baseDate: safe };
    }, [propDate, tzMode]);

    const headerDateDisplay = React.useMemo(() => {
        const value = String(headerDate || '');
        const match = value.match(/^(.*?)(\s+)(\d{1,2}:\d{2}(?::\d{2})?)$/);
        if (!match) return value;
        const datePart = String(match[1] || '').replace(/[\s,]+$/, '');
        return datePart + ' - ' + (match[3] || '');
    }, [headerDate]);
    const canNavigateByDate = Boolean(onNavigateDate && baseDate);
    // reference optional props/vars to avoid 'declared but never read' editor warnings
    void onNavigateTrade;
    void prevTrade;
    void nextTrade;
    void canNavigateByDate;

    const dayTrades = React.useMemo(() => {
        if (!baseDate) return [] as ImportedTradeRecord[];
        const start = getStartOfDayLocal(baseDate);
        const end = start + 24 * 60 * 60 * 1000;
        const symbolFilter = (focusSymbol || '').trim().toUpperCase();
        return imported
            .filter(r => Number.isFinite(r.timestamp) && r.timestamp >= start && r.timestamp < end)
            .filter(r => {
                if (!symbolFilter) return true;
                const sym = String(r.symbol || '').trim().toUpperCase();
                return sym === symbolFilter;
            })
            .slice()
            .sort((a, b) => a.timestamp - b.timestamp);
    }, [imported, baseDate, focusSymbol]);

    const headerInstrument = React.useMemo(() => {
        const sym = (focusSymbol || dayTrades?.[0]?.symbol) as any;
        return normalizeHeaderSymbol(sym);
    }, [focusSymbol, dayTrades]);

    // Live company-name lookup (cache + remote lookup)
    const [liveHeaderName, setLiveHeaderName] = React.useState<string | undefined>(() => headerInstrument?.name);
    // Whether the displayed header/company name was fetched (or cached) from Yahoo Finance
    const [headerNameIsYahoo, setHeaderNameIsYahoo] = React.useState<boolean>(() => {
        try {
            return !!(headerInstrument?.symbol && getCachedCompany(headerInstrument.symbol));
        } catch {
            return false;
        }
    });

    React.useEffect(() => {
        let mounted = true;
        const symbol = headerInstrument?.symbol;

        // prefer explicit company from imported row
        if (dayTrades?.[0]?.company) {
            setLiveHeaderName(dayTrades[0].company);
            setHeaderNameIsYahoo(false);
            return;
        }

        // prefer any static mapping already present
        if (headerInstrument?.name) {
            setLiveHeaderName(headerInstrument.name);
            setHeaderNameIsYahoo(false);
            return;
        }

        if (!symbol) {
            setLiveHeaderName(undefined);
            setHeaderNameIsYahoo(false);
            return;
        }

        // use cached remote value immediately when available
        const cached = getCachedCompany(symbol);
        if (cached) {
            setLiveHeaderName(cached);
            setHeaderNameIsYahoo(true);
            return;
        }

        (async () => {
            try {
                const name = await lookupCompanyName(symbol);
                if (!mounted) return;
                setLiveHeaderName(name ?? undefined);
                // if it's now cached (lookupCompanyName fetch -> setCachedCompany), treat as Yahoo-sourced
                setHeaderNameIsYahoo(!!getCachedCompany(symbol));
            } catch {
                if (mounted) setHeaderNameIsYahoo(false);
            }
        })();

        return () => { mounted = false; };
    }, [headerInstrument?.symbol, dayTrades, headerInstrument?.name]);

    const [isCustomEntryOpen, setIsCustomEntryOpen] = React.useState(false);
    const [editingTrade, setEditingTrade] = React.useState<any>(null); // State for editing
    const [viewingTrade, setViewingTrade] = React.useState<any>(null); // State for viewing details
    const [isColumnSettingsOpen, setIsColumnSettingsOpen] = React.useState(false);
    const [isTagsModalOpen, setIsTagsModalOpen] = React.useState(false); // State for Tags Modal
    const [visibleColumns, setVisibleColumns] = React.useState<string[]>(() => {
        try {
            if (typeof window !== 'undefined') {
                const saved = window.localStorage.getItem('trading-tracker:execution-log:columnOrder:v1');
                if (saved) return JSON.parse(saved);
            }
        } catch {}
        return DEFAULT_COLUMNS.slice();
    });

    React.useEffect(() => {
        try {
            if (typeof window !== 'undefined') window.localStorage.setItem('trading-tracker:execution-log:columnOrder:v1', JSON.stringify(visibleColumns));
        } catch {}
    }, [visibleColumns]);

    // Validate persisted order: remove unknown ids, append missing, enforce locked ends
    React.useEffect(() => {
        const valid = ALL_COLUMNS.map(c => c.id);
        let next = visibleColumns.filter((id: string) => valid.includes(id));
        // append missing in default order
        for (const id of valid) if (!next.includes(id)) next.push(id);
        // enforce 'controls' at end
        next = next.filter((id: string) => id !== 'controls');
        next.push('controls');
        // ensure stable update
        if (JSON.stringify(next) !== JSON.stringify(visibleColumns)) setVisibleColumns(next);
    }, [visibleColumns, setVisibleColumns]);
    type ChartExecution = {
        id?: string;
        storageId?: string;
        executionId?: string;
        orderId?: string;
        timestampResolutionMs?: number | null;
        key?: string;
        time: number;
        timestamp?: number;
        openTimestampMs?: number | null;
        price: number;
        execSide?: 'buy' | 'sell';
        side: 'buy' | 'sell';
        qty: number;
        volume?: number;
        commission?: number;
        fees?: number;
        commissionAndFees?: number;
        rowRealizedNet?: number;
        rowRealizedGross?: number;
        rowFees?: number;
        action?: string;
        symbol?: string;
        assetClass?: string;
        instrument?: string;
    };

    type CustomExecutionLike = {
        storageId?: string;
        timestamp?: number;
        openTimestampMs?: number;
        symbol?: string;
        volume?: number;
        side?: string;
        price?: number;
        commission?: number;
        fees?: number;
        commissionAndFees?: number;
        action?: string;
        positionAfter?: number;
        rowResolvedQty?: number;
        rowRealizedGross?: number;
        rowRealizedNet?: number;
        rowFees?: number;
        durationMinutes?: number;
    };
    type CustomRow = {
        id: string;
        timestamp: number;
        symbol: string;
        volume: number;
        side: 'Long' | 'Short' | '';
        pnl: number;
        durationMinutes?: number;
        fees?: number;
        commission?: number;
        commissionAndFees?: number;
        notes?: string;
        merged?: boolean;
        price?: number;
        avgEntry?: number;
        positionAfter?: number;
        maxPositionSize?: number;
        tradedSize?: number;
        bestExitGross?: number;
        bestExitNet?: number;
        efficiencyGrossPct?: number;
        efficiencyNetPct?: number;
        mae?: number;
        mfe?: number;
        mergedExecutions?: CustomExecutionLike[];
        positionFlow?: Array<{ timestampMs: number | null; positionAfter: number; action: string }>;
    };

    // Custom rows are back!
    const [customRows, setCustomRows] = React.useState<CustomRow[]>([]);

    // Storage IDs of executions that were removed (e.g. after a merge) — used to filter rows even when
    // the original executions come from `trade.executionsList` (grouped trades) or synthesized ids.
    const [removedExecutionIds, setRemovedExecutionIds] = React.useState<string[]>([]);
    const [chartReplayState, setChartReplayState] = React.useState<{
        isReplayMode: boolean;
        isLoading: boolean;
        replayTimeMs: number | null;
        currentMarkPrice: number | null;
        metricBars: MetricBar[];
        metricBarResolutionMs: number | null;
        metricSource: 'market_bars' | null;
        priceMismatch: boolean;
    }>({
        isReplayMode: false,
        isLoading: false,
        replayTimeMs: null,
        currentMarkPrice: null,
        metricBars: [],
        metricBarResolutionMs: null,
        metricSource: null,
        priceMismatch: false,
    });
    const [chartFocusRequest, setChartFocusRequest] = React.useState<{ executionId: string; timestampMs: number; pulseNonce: number } | null>(null);
    const [highlightedExecutionRowKey, setHighlightedExecutionRowKey] = React.useState<string | null>(null);
    const executionHighlightTimerRef = React.useRef<number | null>(null);
    const chartSectionRef = React.useRef<HTMLDivElement | null>(null);

    type LastMergeSnapshot = {
        mergedRowId: string;
        removedCustomRows: Array<{id: string, timestamp: number, symbol: string, volume: number, side: 'Long' | 'Short' | '', pnl: number, durationMinutes?: number, fees?: number, commission?: number, notes?: string}>;
        removedImportedRows: ImportedTradeRecord[];
        removedExecutionIds: string[];
        localTradeId?: string;
        localTradeStorageId?: string;
        previousLocalExecutionsList?: any[];
    };
    const [lastMergeSnapshot, setLastMergeSnapshot] = React.useState<LastMergeSnapshot | null>(null);

    // Build rows for the Executions table. Prefer executions that belong to the provided `trade` (if any):
    // 1) use trade.executionsList when present
    // 2) use trade.sourceIds to filter the authoritative imported rows
    // 3) fallback to all executions for the day (dayTrades)
    const rows = React.useMemo(() => {
        // prefer explicit executionsList attached to the trade (grouped trade from TradesPage)
        let sourceRows: ImportedTradeRecord[] = [];
        const tradeOpenFallbackMs = normalizeTimestampToMs(
            (trade as any)?.openTimestampMs
            ?? (trade as any)?.entryTimestampMs
            ?? (trade as any)?.openTime
            ?? (trade as any)?.entryTime
            ?? (trade as any)?.timestamp
            ?? (trade as any)?.date
        );
        const tradeCloseFallbackMs = normalizeTimestampToMs(
            (trade as any)?.closeTimestampMs
            ?? (trade as any)?.exitTimestampMs
            ?? (trade as any)?.closeTime
            ?? (trade as any)?.exitTime
            ?? (trade as any)?.closeDate
            ?? (trade as any)?.exitDate
            ?? (trade as any)?.closedAt
        );

        if (trade && Array.isArray((trade as any).executionsList)) {
            if ((trade as any).executionsList.length === 0) {
                return [];
            }
            const execList = (trade as any).executionsList as any[];
            const multiplierSymbol = String((execList[0] as any)?.symbol ?? (trade as any)?.symbol ?? '');
            const multiplierAssetClass = String((execList[0] as any)?.assetClass ?? (trade as any)?.assetClass ?? (trade as any)?.market ?? '').toUpperCase();
            const multiplier = multiplierAssetClass.includes('FUT')
                ? getContractMultiplier(multiplierSymbol, 'FUTURES', (execList[0] as any)?.instrument ?? (trade as any)?.instrument)
                : 1;
            const resolvedRows = resolveExecutionRows(execList, { multiplier });

            sourceRows = execList.map((e: any, i: number) => {
                const resolved = resolvedRows[i];
                const execPrice = Number(
                    e.price
                    ?? e.avgPrice
                    ?? e.fillPrice
                    ?? e.entryPrice
                    ?? e.exitPrice
                    ?? e.entry_price
                    ?? e.avg_price
                );
                const explicitCommissionAndFees = Number(e.commissionAndFees ?? e.commissionsAndFees);
                const execCommission = Number.isFinite(Number(e.commission)) ? Number(e.commission) : 0;
                const execFees = Number.isFinite(Number(e.fees ?? e.fee)) ? Number(e.fees ?? e.fee) : 0;
                const totalExecutionCosts = Number.isFinite(explicitCommissionAndFees)
                    ? explicitCommissionAndFees
                    : (execCommission + execFees);

                return ({
                    storageId: e.storageId ?? e.id ?? `${String(trade.id ?? '')}-exec-${i}`,
                    orderId: e.orderId,
                    timestampResolutionMs: inferTradeInsightTimestampResolutionMs(e.openTimestampMs ?? e.timestamp ?? e.time),
                    openTimestampMs: resolved.timestampMs ?? normalizeTimestampToMs(e.openTimestampMs ?? e.timestamp ?? e.time ?? trade.openTimestampMs ?? trade.timestamp) ?? Date.now(),
                    closeTimestampMs: resolved.rowCloseTimestampMs,
                    timestamp: resolved.timestampMs ?? normalizeTimestampToMs(e.openTimestampMs ?? e.timestamp ?? e.time ?? trade.openTimestampMs ?? trade.timestamp) ?? Date.now(),
                    symbol: e.symbol ?? trade.symbol,
                    price: Number.isFinite(execPrice) ? execPrice : undefined,
                    avgPrice: Number.isFinite(Number(e.avgPrice ?? e.avg_price)) ? Number(e.avgPrice ?? e.avg_price) : undefined,
                    volume: resolved.signedQty,
                    shares: resolved.signedQty,
                    execSide: resolved.side,
                    side: resolved.side,
                    action: resolved.rowAction,
                    positionAfter: resolved.runningPosition,
                    closedQty: resolved.rowResolvedQty,
                    rowResolvedQty: resolved.rowResolvedQty,
                    rowRealizedGross: resolved.rowRealizedGross,
                    rowRealizedNet: resolved.rowRealizedNet,
                    rowFees: resolved.rowFees,
                    pnl: resolved.rowRealizedNet,
                    fees: execFees,
                    commission: execCommission,
                    commissionAndFees: Number.isFinite(explicitCommissionAndFees) ? explicitCommissionAndFees : totalExecutionCosts,
                    durationMinutes: Number.isFinite(Number(resolved.rowDurationMinutes)) ? Number(resolved.rowDurationMinutes) : undefined,
                    notes: e.notes ?? '',
                } as ImportedTradeRecord);
            });
        } else if (trade && Array.isArray((trade as any).sourceIds)) {
            if ((trade as any).sourceIds.length === 0) {
                return [];
            }
            const ids = new Set((trade as any).sourceIds.map((id: any) => String(id)));
            sourceRows = dayTrades.filter((d) => ids.has(String(d.storageId ?? d.timestamp)));
        } else {
            // fallback: all executions for the selected day (same behavior as before)
            sourceRows = dayTrades;
        }

        // Remove any executions that were previously marked deleted (e.g. removed by a merge).
        const removedSet = new Set((removedExecutionIds || []).map(String));
        sourceRows = sourceRows.filter(s => !removedSet.has(String(s.storageId ?? s.timestamp)));

        const sourceExecTimesMs = sourceRows
            .map((s) => normalizeTimestampToMs(
                (s as any)?.timestamp
                ?? (s as any)?.openTimestampMs
                ?? (s as any)?.openTime
                ?? (s as any)?.entryTime
                ?? (s as any)?.time
                ?? (s as any)?.date
            ))
            .filter((v): v is number => Number.isFinite(Number(v)));
        const sourceOpenFallbackMs = sourceExecTimesMs.length ? Math.min(...sourceExecTimesMs) : null;
        const sourceCloseFallbackMs = sourceExecTimesMs.length ? Math.max(...sourceExecTimesMs) : null;

        // Resolve execution-level gross/net/action fields for imported rows that do not already
        // come from `trade.executionsList`. Group by symbol + assetClass so position accounting
        // does not bleed across instruments.
        const resolvedByIndex = new Map<number, ReturnType<typeof resolveExecutionRows>[number]>();
        const groupedSourceRows = new Map<string, Array<{ originalIndex: number; row: ImportedTradeRecord }>>();

        sourceRows.forEach((row, originalIndex) => {
            const symbol = String((row as any)?.symbol ?? '').trim();
            const assetClass = String((row as any)?.assetClass ?? (trade as any)?.assetClass ?? (trade as any)?.market ?? '').toUpperCase();
            const groupKey = `${symbol}__${assetClass}`;
            if (!groupedSourceRows.has(groupKey)) groupedSourceRows.set(groupKey, []);
            groupedSourceRows.get(groupKey)!.push({ originalIndex, row });
        });

        for (const group of groupedSourceRows.values()) {
            const sorted = group
                .slice()
                .sort((a, b) => {
                    const aTs = normalizeTimestampToMs((a.row as any)?.timestamp ?? (a.row as any)?.openTimestampMs ?? (a.row as any)?.time ?? (a.row as any)?.date) ?? 0;
                    const bTs = normalizeTimestampToMs((b.row as any)?.timestamp ?? (b.row as any)?.openTimestampMs ?? (b.row as any)?.time ?? (b.row as any)?.date) ?? 0;
                    return aTs - bTs;
                });

            const sample = sorted[0]?.row as any;
            const symbol = String(sample?.symbol ?? trade?.symbol ?? '');
            const assetClass = String(sample?.assetClass ?? (trade as any)?.assetClass ?? (trade as any)?.market ?? '').toUpperCase();
            const multiplier = assetClass.includes('FUT')
                ? getContractMultiplier(symbol, 'FUTURES', sample?.instrument ?? (trade as any)?.instrument)
                : 1;

            const resolved = resolveExecutionRows(sorted.map(item => item.row as any), { multiplier });
            sorted.forEach((item, idx) => {
                resolvedByIndex.set(item.originalIndex, resolved[idx]);
            });
        }

        const importedRows = sourceRows.map((t, sourceIndex) => {
            const resolved = resolvedByIndex.get(sourceIndex);
            const vol = Number.isFinite(t.volume) ? Number(t.volume) : (Number.isFinite(t.shares) ? Number(t.shares) : 0);
            const rawPnl = pnlOfTrade(t);
            const contractsForRow = Math.max(1, Math.abs(Math.round(Number(vol) || 0)));
            const normalizedInputSide = String((t as any).side ?? '').trim().toLowerCase();
            const side = (
                normalizedInputSide === 'long' || normalizedInputSide === 'buy'
                    ? 'Long'
                    : normalizedInputSide === 'short' || normalizedInputSide === 'sell'
                        ? 'Short'
                        : Number.isFinite(Number(vol)) && Number(vol) !== 0
                            ? (Number(vol) < 0 ? 'Short' : 'Long')
                            : (resolved?.side === 'sell' ? 'Short' : 'Long')
            ) as 'Long' | 'Short';
            const hasExplicitCloseTimestamp = Object.prototype.hasOwnProperty.call(t, 'closeTimestampMs');
            const explicitCloseCandidate = hasExplicitCloseTimestamp
                ? (t as any).closeTimestampMs
                : ((t as any).exitTimestampMs
                    ?? (t as any).closeTime
                    ?? (t as any).exitTime
                    ?? (t as any).closeDate
                    ?? (t as any).exitDate
                    ?? (t as any).closedAt);

            let normalizedOpenTsRaw = normalizeTimestampToMs(
                (t as any).openTimestampMs
                ?? (t as any).entryTimestampMs
                ?? (t as any).openTime
                ?? (t as any).entryTime
                ?? t.timestamp
                ?? (t as any).time
                ?? (t as any).date
                ?? sourceOpenFallbackMs
                ?? tradeOpenFallbackMs
            );
            if (normalizedOpenTsRaw == null) normalizedOpenTsRaw = Number(t.timestamp);
            const normalizedOpenTs = Number.isFinite(Number(normalizedOpenTsRaw)) ? Number(normalizedOpenTsRaw) : Date.now();
            const normalizedCloseTsRaw = normalizeTimestampToMs(
                explicitCloseCandidate
                ?? (!hasExplicitCloseTimestamp ? (tradeCloseFallbackMs ?? sourceCloseFallbackMs) : null)
            );
            const hasClose = Number.isFinite(Number(normalizedCloseTsRaw));
            const explicitRowPnl = Number((t as any).rowRealizedNet);
            const explicitRowGross = Number((t as any).rowRealizedGross);
            const explicitRowFees = Number((t as any).rowFees);

            return {
                key: t.key ?? t.storageId,
                storageId: t.storageId, // Keep for identification
                orderId: (t as any).orderId,
                timestampResolutionMs: Number.isFinite(Number((t as any).timestampResolutionMs))
                    ? Number((t as any).timestampResolutionMs)
                    : inferTradeInsightTimestampResolutionMs((t as any).timestamp ?? (t as any).openTimestampMs),
                openTimestampMs: normalizedOpenTs,
                closeTimestampMs: hasClose ? Number(normalizedCloseTsRaw) : null,
                timestamp: Number(t.timestamp),
                symbol: t.symbol,
                    volume: Number(vol),
                    // prefer execution-level price fields; fall back to trade-level entry/exit prices
                    price: Number.isFinite(Number((t as any).price ?? (t as any).avgPrice ?? (t as any).entryPrice ?? (t as any).exitPrice ?? (t as any).fillPrice ?? (t as any).entry_price ?? (t as any).avg_price ?? (t as any)?.legs?.[0]?.price))
                        ? Number((t as any).price ?? (t as any).avgPrice ?? (t as any).entryPrice ?? (t as any).exitPrice ?? (t as any).fillPrice ?? (t as any).entry_price ?? (t as any).avg_price ?? (t as any)?.legs?.[0]?.price)
                        : undefined,
                execSide: Number.isFinite(Number(vol)) && Number(vol) !== 0
                    ? (Number(vol) < 0 ? 'sell' : 'buy')
                    : resolved?.side,
                side,
                // Normalize: store execution TOTAL PnL (per-execution amount). If imports provide
                // per-contract PnL, multiply by contract count so downstream code can rely on
                // `row.pnl` being the execution's total realized PnL.
                pnl: Number.isFinite(explicitRowPnl)
                    ? explicitRowPnl
                    : (Number.isFinite(Number(resolved?.rowRealizedNet))
                        ? Number(resolved?.rowRealizedNet)
                        : (hasClose ? (Number.isFinite(Number(rawPnl)) ? Number(rawPnl) * contractsForRow : 0) : 0)),
                fees: Number.isFinite((t as any).fees) ? Number((t as any).fees) : 0,
                commission: Number.isFinite((t as any).commission) ? Number((t as any).commission) : 0,
                commissionAndFees: Number.isFinite((t as any).commissionAndFees)
                    ? Number((t as any).commissionAndFees)
                    : ((Number.isFinite((t as any).fees) ? Number((t as any).fees) : 0) + (Number.isFinite((t as any).commission) ? Number((t as any).commission) : 0)),
                // Presence flags to distinguish "no data" vs explicit zero
                fees_present: typeof (t as any).fees !== 'undefined',
                commission_present: typeof (t as any).commission !== 'undefined',
                commissionAndFees_present: typeof (t as any).commissionAndFees !== 'undefined',
                action: (t as any).action ?? resolved?.rowAction,
                positionAfter: Number.isFinite(Number((t as any).positionAfter)) ? Number((t as any).positionAfter) : resolved?.runningPosition,
                closedQty: Number.isFinite(Number((t as any).closedQty)) ? Number((t as any).closedQty) : resolved?.rowResolvedQty,
                rowResolvedQty: Number.isFinite(Number((t as any).rowResolvedQty)) ? Number((t as any).rowResolvedQty) : resolved?.rowResolvedQty,
                rowRealizedGross: Number.isFinite(explicitRowGross) ? explicitRowGross : resolved?.rowRealizedGross,
                rowRealizedNet: Number.isFinite(explicitRowPnl) ? explicitRowPnl : resolved?.rowRealizedNet,
                rowFees: Number.isFinite(explicitRowFees) ? explicitRowFees : resolved?.rowFees,
                durationMinutes: Number.isFinite(t.durationMinutes)
                    ? Number(t.durationMinutes)
                    : (Number.isFinite((t as any).duration)
                        ? Number((t as any).duration)
                        : (Number.isFinite(Number(resolved?.rowDurationMinutes)) ? Number(resolved?.rowDurationMinutes) : undefined)),
                notes: t.notes || '',
            };
        });

        const merged = [...importedRows, ...customRows.map((c: any) => ({
            key: c.id,
            id: c.id, // Keep for identification
            openTimestampMs: Number(c.timestamp) || Date.now(),
            closeTimestampMs: null,
            timestamp: c.timestamp,
            symbol: c.symbol,
            volume: c.volume,
            side: (c.side || (c.pnl >= 0 ? 'Long' : 'Short')) as 'Long' | 'Short',
            pnl: c.pnl,
            price: Number.isFinite(Number((c as any).price)) ? Number((c as any).price) : undefined,
            avgEntry: Number.isFinite(Number((c as any).avgEntry)) ? Number((c as any).avgEntry) : undefined,
            fees: Number.isFinite((c as any).fees) ? Number((c as any).fees) : 0,
            commission: Number.isFinite((c as any).commission) ? Number((c as any).commission) : 0,
            commissionAndFees: Number.isFinite((c as any).commissionAndFees)
                ? Number((c as any).commissionAndFees)
                : ((Number.isFinite((c as any).fees) ? Number((c as any).fees) : 0) + (Number.isFinite((c as any).commission) ? Number((c as any).commission) : 0)),
            fees_present: typeof (c as any).fees !== 'undefined',
            commission_present: typeof (c as any).commission !== 'undefined',
            commissionAndFees_present: typeof (c as any).commissionAndFees !== 'undefined',
            durationMinutes: c.durationMinutes,
            notes: c.notes || '',
            // propagate custom metadata (e.g. merged flag)
            merged: (c as any).merged === true,
            mergedExecutions: Array.isArray((c as any).mergedExecutions) ? (c as any).mergedExecutions : undefined,
            positionFlow: Array.isArray((c as any).positionFlow) ? (c as any).positionFlow : undefined,
            positionAfter: Number.isFinite(Number((c as any).positionAfter)) ? Number((c as any).positionAfter) : undefined,
            maxPositionSize: Number.isFinite(Number((c as any).maxPositionSize)) ? Number((c as any).maxPositionSize) : undefined,
            tradedSize: Number.isFinite(Number((c as any).tradedSize)) ? Number((c as any).tradedSize) : undefined,
            bestExitGross: Number.isFinite(Number((c as any).bestExitGross)) ? Number((c as any).bestExitGross) : undefined,
            bestExitNet: Number.isFinite(Number((c as any).bestExitNet)) ? Number((c as any).bestExitNet) : undefined,
            efficiencyGrossPct: Number.isFinite(Number((c as any).efficiencyGrossPct)) ? Number((c as any).efficiencyGrossPct) : undefined,
            efficiencyNetPct: Number.isFinite(Number((c as any).efficiencyNetPct)) ? Number((c as any).efficiencyNetPct) : undefined,
            mae: Number.isFinite(Number((c as any).mae)) ? Number((c as any).mae) : undefined,
            mfe: Number.isFinite(Number((c as any).mfe)) ? Number((c as any).mfe) : undefined,
        }))];

        merged.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
        return merged;
    }, [dayTrades, customRows, trade, removedExecutionIds]);

    const executionsForChart = React.useMemo<ChartExecution[]>(() => {
        const toChartExecutionSide = (row: any): 'buy' | 'sell' => {
            const raw = String(
                row?.execSide
                ?? row?.side
                ?? row?.type
                ?? row?.direction
                ?? row?.position
                ?? ''
            ).trim().toLowerCase();
            if (raw === '-' || raw === '--') return 'sell';
            if (raw === '+' || raw === '++') return 'buy';
            if (raw === 's' || raw.includes('sell') || raw.includes('short')) return 'sell';
            if (raw === 'b' || raw.includes('buy') || raw.includes('long')) return 'buy';

            const qty = Number(row?.qty ?? row?.volume ?? row?.quantity ?? row?.shares);
            if (Number.isFinite(qty) && qty !== 0) return qty < 0 ? 'sell' : 'buy';

            return 'buy';
        };

        if (!rows || rows.length === 0) {
            if (forcedExecutions && Array.isArray(forcedExecutions) && forcedExecutions.length > 0) {
                return forcedExecutions.map((r: any) => ({
                    id: (r as any).id != null ? String((r as any).id) : undefined,
                    storageId: (r as any).storageId != null ? String((r as any).storageId) : undefined,
                    executionId: (r as any).executionId != null ? String((r as any).executionId) : undefined,
                    orderId: (r as any).orderId != null ? String((r as any).orderId) : undefined,
                    timestampResolutionMs: Number.isFinite(Number((r as any).timestampResolutionMs))
                        ? Number((r as any).timestampResolutionMs)
                        : inferTradeInsightTimestampResolutionMs((r as any).timestamp ?? (r as any).time),
                    key: (r as any).key != null ? String((r as any).key) : undefined,
                    time: normalizeEpochMs((r as any).time) ?? normalizeEpochMs((r as any).timestamp) ?? Date.now(),
                    timestamp: normalizeEpochMs((r as any).timestamp) ?? normalizeEpochMs((r as any).time) ?? Date.now(),
                    openTimestampMs: normalizeEpochMs((r as any).openTimestampMs) ?? normalizeEpochMs((r as any).timestamp) ?? normalizeEpochMs((r as any).time) ?? null,
                    price: Number.isFinite(Number((r as any).price ?? (r as any).avgPrice ?? (r as any).fillPrice)) ? Number((r as any).price ?? (r as any).avgPrice ?? (r as any).fillPrice) : 0,
                    execSide: (r as any).execSide,
                    side: toChartExecutionSide(r),
                    qty: Number.isFinite(Number((r as any).qty)) ? Number((r as any).qty) : 1,
                    volume: Number((r as any).volume ?? (r as any).qty),
                    commission: Number.isFinite(Number((r as any).commission)) ? Number((r as any).commission) : 0,
                    fees: Number.isFinite(Number((r as any).fees ?? (r as any).fee)) ? Number((r as any).fees ?? (r as any).fee) : 0,
                    commissionAndFees: Number.isFinite(Number((r as any).commissionAndFees ?? (r as any).commissionsAndFees)) ? Number((r as any).commissionAndFees ?? (r as any).commissionsAndFees) : undefined,
                    rowRealizedNet: Number.isFinite(Number((r as any).rowRealizedNet)) ? Number((r as any).rowRealizedNet) : undefined,
                    rowRealizedGross: Number.isFinite(Number((r as any).rowRealizedGross)) ? Number((r as any).rowRealizedGross) : undefined,
                    rowFees: Number.isFinite(Number((r as any).rowFees)) ? Number((r as any).rowFees) : undefined,
                    action: (r as any).action,
                    symbol: (r as any).symbol,
                    assetClass: (r as any).assetClass,
                    instrument: (r as any).instrument,
                }));
            }

            return [];
        }

        const executionSourceRows = rows.flatMap((row: any) => {
            if (Array.isArray(row?.mergedExecutions) && row.mergedExecutions.length > 0) {
                return row.mergedExecutions;
            }
            return [row];
        });

        return executionSourceRows.map((r: any) => ({
            id: (r as any).id != null ? String((r as any).id) : undefined,
            storageId: (r as any).storageId != null ? String((r as any).storageId) : undefined,
            executionId: (r as any).executionId != null ? String((r as any).executionId) : undefined,
            orderId: (r as any).orderId != null ? String((r as any).orderId) : undefined,
            timestampResolutionMs: Number.isFinite(Number((r as any).timestampResolutionMs))
                ? Number((r as any).timestampResolutionMs)
                : inferTradeInsightTimestampResolutionMs((r as any).timestamp ?? (r as any).openTimestampMs),
            key: (r as any).key != null ? String((r as any).key) : undefined,
            time: normalizeEpochMs((r as any).openTimestampMs ?? (r as any).timestamp) ?? Date.now(),
            timestamp: normalizeEpochMs((r as any).timestamp ?? (r as any).openTimestampMs) ?? Date.now(),
            openTimestampMs: normalizeEpochMs((r as any).openTimestampMs ?? (r as any).timestamp) ?? null,
            price: Number.isFinite(Number((r as any).price ?? (r as any).avgPrice ?? (r as any).entryPrice ?? (r as any).exitPrice)) ? Number((r as any).price ?? (r as any).avgPrice ?? (r as any).entryPrice ?? (r as any).exitPrice) : 0,
            execSide: (r as any).execSide,
            side: toChartExecutionSide(r),
            qty: Number.isFinite(Number((r as any).volume)) ? Math.abs(Number((r as any).volume)) : 1,
            volume: Number.isFinite(Number((r as any).volume)) ? Number((r as any).volume) : undefined,
            commission: Number.isFinite(Number((r as any).commission)) ? Number((r as any).commission) : 0,
            fees: Number.isFinite(Number((r as any).fees)) ? Number((r as any).fees) : 0,
            commissionAndFees: Number.isFinite(Number((r as any).commissionAndFees)) ? Number((r as any).commissionAndFees) : undefined,
            rowRealizedNet: Number.isFinite(Number((r as any).rowRealizedNet)) ? Number((r as any).rowRealizedNet) : undefined,
            rowRealizedGross: Number.isFinite(Number((r as any).rowRealizedGross)) ? Number((r as any).rowRealizedGross) : undefined,
            rowFees: Number.isFinite(Number((r as any).rowFees)) ? Number((r as any).rowFees) : undefined,
            action: (r as any).action,
            symbol: (r as any).symbol,
            assetClass: (r as any).assetClass ?? (trade as any)?.assetClass,
            instrument: (r as any).instrument,
        }));
    }, [rows, forcedExecutions, trade]);



    const chartSymbol = React.useMemo(() => {
        const candidates = [
            focusSymbol,
            dayTrades?.[0]?.symbol,
            rows?.[0]?.symbol,
            headerInstrument?.symbol,
        ];

        for (const c of candidates) {
            const s = sanitizeSymbolForChart(c as any);
            if (s) return s;
        }
        return '';
    }, [focusSymbol, dayTrades, rows, headerInstrument]);

    const chartContract = React.useMemo(() => {
        const candidates = [focusSymbol, dayTrades?.[0]?.symbol, rows?.[0]?.symbol, headerInstrument?.symbol];
        for (const c of candidates) {
            const yyyymm = contractSymbolToYyyymm(c as any);
            if (yyyymm) return { lastTradeDateOrContractMonth: yyyymm, localSymbol: String(c || '').toUpperCase().replace(/^\/+/, '') };
        }
        for (const e of (rows || [])) {
            const s = ((e as any)?.symbol || (e as any)?.localSymbol || (e as any)?.ticker);
            const yyyymm = contractSymbolToYyyymm(s as any);
            if (yyyymm) return { lastTradeDateOrContractMonth: yyyymm, localSymbol: String(s || '').toUpperCase().replace(/^\/+/, '') };
        }
        return null;
    }, [focusSymbol, dayTrades, rows, headerInstrument]);

    const chartInstrumentType = React.useMemo(() => {
        const explicit = normalizeSecType((trade as any)?.secType)
            ?? normalizeSecType((trade as any)?.sec_type)
            ?? normalizeSecType((trade as any)?.instrumentType)
            ?? normalizeSecType((trade as any)?.assetClass)
            ?? normalizeSecType((dayTrades as any)?.[0]?.secType)
            ?? normalizeSecType((dayTrades as any)?.[0]?.sec_type)
            ?? normalizeSecType((dayTrades as any)?.[0]?.instrumentType)
            ?? normalizeSecType((dayTrades as any)?.[0]?.assetClass);

        if (explicit) return secTypeToInstrumentType(explicit);
        return inferInstrumentType(chartSymbol);
    }, [chartSymbol, trade, dayTrades]);

    const chartSecType = React.useMemo(() => {
        return instrumentTypeToSecType(chartInstrumentType);
    }, [chartInstrumentType]);

    const [replayLaunchControlsStatus, setReplayLaunchControlsStatus] = React.useState<ReplayLaunchControlsStatus | null>(null);
    const [replayLaunchControlsReady, setReplayLaunchControlsReady] = React.useState(false);
    const [replayChartEnabled] = useReplayChartVisibility();

    React.useEffect(() => {
        let cancelled = false;

        const run = async () => {
            try {
                const payload = await fetchReplayLaunchControlsStatus();
                if (cancelled) return;
                setReplayLaunchControlsStatus(parseReplayLaunchControlsStatus(payload));
            } catch {
                if (cancelled) return;
                setReplayLaunchControlsStatus(null);
            } finally {
                if (!cancelled) setReplayLaunchControlsReady(true);
            }
        };

        void run();
        return () => {
            cancelled = true;
        };
    }, []);

    const replayLaunchAssetClass = React.useMemo(() => {
        return mapSecTypeToLaunchAssetClass(chartSecType);
    }, [chartSecType]);

    const replayBlockedMessage = React.useMemo(() => {
        if (!replayLaunchControlsReady) return null;
        return getReplayBlockedMessage({
            status: replayLaunchControlsStatus,
            assetClass: replayLaunchAssetClass,
        });
    }, [replayLaunchControlsReady, replayLaunchControlsStatus, replayLaunchAssetClass]);

    const replayChartBlockedMessage = React.useMemo(() => {
        if (replayChartEnabled) return null;
        return 'Replay chart is currently turned off by admin.';
    }, [replayChartEnabled]);

    const chartAssetClass = React.useMemo<AssetClass>(() => {
        if (chartSecType === 'FUT') return 'FUT';
        if (chartSecType === 'CRYPTO') return 'CRYPTO';
        return 'STK';
    }, [chartSecType]);

    const executionUnixSec = React.useMemo(() => {
        const candidates: unknown[] = [
            (trade as any)?.openTimestampMs,
            (trade as any)?.entryTime,
            (trade as any)?.openTime,
            (trade as any)?.timestamp,
            rows?.[0]?.openTimestampMs,
            executionsForChart?.[0]?.time,
            rows?.[0]?.timestamp,
        ];
        for (const candidate of candidates) {
            const sec = toCanonicalUnixSec(candidate);
            if (sec != null) return sec;
        }
        throw new Error('TradeDetails requires canonical execution UNIX seconds for chart session resolution');
    }, [trade, executionsForChart, rows]);

    const chartTrade = React.useMemo(() => ({
        id: (trade as any)?.id ?? (trade as any)?._id ?? (rows as any)?.[0]?.id ?? (rows as any)?.[0]?._id,
        symbol: chartSymbol as any,
        secType: chartSecType,
        instrumentType: chartInstrumentType,
        isDemo: selectedTradeIsDemo,
        source: (trade as any)?.source ?? (selectedTradeIsDemo ? 'demo' : undefined),
        sourceType: (trade as any)?.sourceType ?? (selectedTradeIsDemo ? 'demo' : undefined),
        ...(chartContract ? {
            lastTradeDateOrContractMonth: chartContract.lastTradeDateOrContractMonth,
            localSymbol: chartContract.localSymbol,
        } : {}),
    }), [trade, rows, chartSymbol, chartSecType, chartInstrumentType, chartContract, selectedTradeIsDemo]);

    const primaryDisplaySymbol = React.useMemo(() => {
        if (chartSecType !== 'FUT') return headerInstrument?.symbol || MISSING_VALUE;

        const exactRaw = String(
            chartContract?.localSymbol
            ?? (trade as any)?.localSymbol
            ?? (dayTrades as any)?.[0]?.localSymbol
            ?? ''
        ).trim();

        const exactDisplay = formatFuturesDisplaySymbol(exactRaw);
        if (exactDisplay) return exactDisplay;

        return formatFuturesDisplaySymbol(headerInstrument?.symbol || chartSymbol || MISSING_VALUE) || (headerInstrument?.symbol || MISSING_VALUE);
    }, [chartSecType, headerInstrument, chartContract, trade, dayTrades, chartSymbol]);

    const chartTradeId = React.useMemo(
        () => (trade as any)?.id ?? (trade as any)?._id ?? (rows as any)?.[0]?.id ?? (rows as any)?.[0]?._id,
        [trade, rows],
    );

    const visibleRows = React.useMemo(() => {
        if (!chartReplayState.isReplayMode) return rows;
        const cutoffMs = Number(chartReplayState.replayTimeMs);
        if (!Number.isFinite(cutoffMs) || cutoffMs <= 0) return [];

        return rows.filter((row) => {
            const rowTs = normalizeTimestampToMs(
                (row as any).openTimestampMs
                ?? (row as any).timestamp
                ?? (row as any).time
                ?? (row as any).date
            );
            return rowTs != null && rowTs <= cutoffMs;
        });
    }, [rows, chartReplayState]);

    const handleReplayStateChange = React.useCallback((nextState: any) => {
        const nextIsReplayMode = Boolean(nextState?.isReplayMode);
        const nextIsLoading = Boolean(nextState?.isLoading);
        const nextReplayTimeMs = Number.isFinite(Number(nextState?.replayTimeMs)) ? Number(nextState.replayTimeMs) : null;
        const nextCurrentMarkPrice = Number.isFinite(Number(nextState?.currentMarkPrice)) ? Number(nextState.currentMarkPrice) : null;
        const nextMetricBars: MetricBar[] = Array.isArray(nextState?.metricBars)
            ? (nextState.metricBars as any[])
                .map((bar) => {
                    const rawTime = Number((bar as any)?.time);
                    const rawHigh = Number((bar as any)?.high ?? (bar as any)?.h);
                    const rawLow = Number((bar as any)?.low ?? (bar as any)?.l);
                    const time = Number.isFinite(rawTime) ? (rawTime > 1_000_000_000_000 ? Math.floor(rawTime) : Math.floor(rawTime * 1000)) : NaN;
                    return {
                        time,
                        high: rawHigh,
                        low: rawLow,
                    };
                })
                .filter((bar) => Number.isFinite(bar.time) && Number.isFinite(bar.high) && Number.isFinite(bar.low))
            : [];
        const resolvedTimeframe = String(nextState?.chartResolution?.resolvedTimeframe ?? '').trim().toLowerCase();
        const nextMetricBarResolutionMs = resolvedTimeframe === '1m'
            ? 60_000
            : resolvedTimeframe === '5m'
                ? 300_000
                : resolvedTimeframe === '15m'
                    ? 900_000
                    : resolvedTimeframe === '1h'
                        ? 3_600_000
                        : resolvedTimeframe === '1d'
                            ? 86_400_000
                            : null;
        const nextMetricSource = nextMetricBars.length > 0 && nextMetricBarResolutionMs != null ? 'market_bars' as const : null;
        const nextPriceMismatch = Boolean(nextState?.priceMismatch);

        setChartReplayState((prev) => {
            const sameBars = prev.metricBars.length === nextMetricBars.length
                && prev.metricBars.every((bar, idx) => {
                    const nextBar = nextMetricBars[idx];
                    return nextBar
                        && bar.time === nextBar.time
                        && bar.high === nextBar.high
                        && bar.low === nextBar.low;
                });

            if (prev.isReplayMode === nextIsReplayMode && prev.isLoading === nextIsLoading && prev.replayTimeMs === nextReplayTimeMs && prev.currentMarkPrice === nextCurrentMarkPrice && prev.metricBarResolutionMs === nextMetricBarResolutionMs && prev.metricSource === nextMetricSource && prev.priceMismatch === nextPriceMismatch && sameBars) {
                return prev;
            }
            return {
                isReplayMode: nextIsReplayMode,
                isLoading: nextIsLoading,
                replayTimeMs: nextReplayTimeMs,
                currentMarkPrice: nextCurrentMarkPrice,
                metricBars: nextMetricBars,
                metricBarResolutionMs: nextMetricBarResolutionMs,
                metricSource: nextMetricSource,
                priceMismatch: nextPriceMismatch,
            };
        });
    }, []);

    const handleSaveTrade = (tradeData: any) => {
        if (tradeData.storageId) {
            // It's an imported trade, mock update
            updateImportedTrade(tradeData);
        } else if (customRows.some(r => r.id === tradeData.id)) {
            // It's an existing custom trade, update
            setCustomRows(prev => prev.map(r => r.id === tradeData.id ? tradeData : r));
        } else {
            // New trade
            setCustomRows(prev => [...prev, tradeData]);
        }
        setEditingTrade(null);
    };

    const handleSetInitialRiskForViewedTrade = React.useCallback((targetTrade: any, value: number) => {
        const updated = {
            ...targetTrade,
            initialRisk: value,
            initial_risk: value,
            riskAmount: value,
        };

        if (updated.storageId) {
            updateImportedTrade(updated);
        } else if (customRows.some((row) => row.id === updated.id)) {
            setCustomRows((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
        }

        setViewingTrade(updated);
        try {
            window.dispatchEvent(new CustomEvent('tt:toast', {
                detail: {
                    message: 'Initial Risk updated.',
                    variant: 'success',
                    duration: 2400,
                },
            }));
        } catch {}
    }, [customRows]);

    // Merge selected rows into a single consolidated custom row
    const handleMergeSelected = (selectedKeys: Array<string | number>) => {
        const keySet = new Set(selectedKeys.map(String));
        const toMerge = rows.filter(r => keySet.has(String(r.key)));
        if (!toMerge || toMerge.length < 2) return; // validation: need at least 2

        // Deterministic reconstruction from merged execution set
        const normalizedExecutions = [...toMerge]
            .map((r, index) => {
                const timestampMs = normalizeTimestampToMs(
                    (r as any).openTimestampMs
                    ?? (r as any).timestamp
                    ?? (r as any).time
                    ?? (r as any).date
                ) ?? Date.now();
                const volume = Number.isFinite(Number((r as any).volume)) ? Number((r as any).volume) : 0;
                const side = volume < 0 ? 'sell' : 'buy';
                const qty = Math.abs(volume);
                const priceCandidate = Number(
                    (r as any).price
                    ?? (r as any).avgPrice
                    ?? (r as any).entryPrice
                    ?? (r as any).exitPrice
                    ?? (r as any).fillPrice
                    ?? (r as any).entry_price
                    ?? (r as any).avg_price
                );
                const price = Number.isFinite(priceCandidate) ? priceCandidate : null;
                const fee = Number.isFinite(Number((r as any).commissionAndFees))
                    ? Number((r as any).commissionAndFees)
                    : ((Number.isFinite(Number((r as any).fees)) ? Number((r as any).fees) : 0)
                        + (Number.isFinite(Number((r as any).commission)) ? Number((r as any).commission) : 0));

                return {
                    id: String((r as any).storageId ?? (r as any).id ?? (r as any).key ?? `merged-exec-${index}`),
                    timestampMs,
                    side,
                    qty,
                    signedQty: side === 'sell' ? -qty : qty,
                    price,
                    fee,
                    symbol: String((r as any).symbol ?? toMerge[0]?.symbol ?? ''),
                };
            })
            .filter((e) => e.qty > 0 && Number.isFinite(Number(e.price)))
            .sort((a, b) => a.timestampMs - b.timestampMs);

        if (normalizedExecutions.length < 2) return;

        const mergeSymbol = String(normalizedExecutions[0]?.symbol ?? toMerge[0]?.symbol ?? '');
        const mergeAssetClass = String((trade as any)?.assetClass ?? (trade as any)?.market ?? (toMerge[0] as any)?.assetClass ?? '').toUpperCase();
        const mergeMultiplier = mergeAssetClass.includes('FUT')
            ? getContractMultiplier(mergeSymbol, 'FUTURES', (trade as any)?.instrument)
            : 1;
        const resolved = resolveExecutionRows(
            normalizedExecutions.map((exec) => ({
                id: exec.id,
                timestamp: exec.timestampMs,
                side: exec.side,
                qty: exec.qty,
                price: exec.price ?? undefined,
                commissionAndFees: exec.fee,
            })),
            { multiplier: mergeMultiplier },
        );

        const minTs = normalizedExecutions[0]?.timestampMs ?? Date.now();
        const maxTs = normalizedExecutions[normalizedExecutions.length - 1]?.timestampMs ?? minTs;
        const totalSignedVolume = normalizedExecutions.reduce((sum, exec) => sum + exec.signedQty, 0);
        const tradedSize = normalizedExecutions.reduce((sum, exec) => sum + exec.qty, 0);
        const grossPnl = resolved.reduce((sum, row) => sum + (Number.isFinite(Number(row.rowRealizedGross)) ? Number(row.rowRealizedGross) : 0), 0);
        const netPnl = resolved.reduce((sum, row) => sum + (Number.isFinite(Number(row.rowRealizedNet)) ? Number(row.rowRealizedNet) : 0), 0);
        const recomputedFees = resolved.reduce((sum, row) => sum + (Number.isFinite(Number(row.rowFees)) ? Number(row.rowFees) : 0), 0);
        const sourceCommission = toMerge.reduce((sum, row) => sum + (Number.isFinite(Number((row as any).commission)) ? Number((row as any).commission) : 0), 0);
        const sourceFees = toMerge.reduce((sum, row) => sum + (Number.isFinite(Number((row as any).fees)) ? Number((row as any).fees) : 0), 0);
        const maxPositionSize = resolved.reduce((peak, row) => Math.max(peak, Math.abs(Number(row.runningPosition) || 0)), 0);

        let weightedEntryNotional = 0;
        let weightedEntryQty = 0;
        for (const row of resolved) {
            const px = Number(row.price);
            if (!Number.isFinite(px)) continue;
            if (row.rowAction === 'OPEN' || row.rowAction === 'SCALE_IN') {
                weightedEntryQty += Number(row.quantity) || 0;
                weightedEntryNotional += (Number(row.quantity) || 0) * px;
            }
        }
        const avgEntry = weightedEntryQty > 0 ? (weightedEntryNotional / weightedEntryQty) : undefined;

        const closeTsCandidates = resolved
            .flatMap((row) => row.matches.map((m) => Number(m.closeTimestampMs)))
            .filter((v) => Number.isFinite(v) && v > 0);
        const closeTs = closeTsCandidates.length > 0 ? Math.max(...closeTsCandidates) : maxTs;
        const durationMinutes = Math.max(0, Math.round((closeTs - minTs) / 60000));

        const mergedMetricExecutions: MetricExecution[] = normalizedExecutions.map((exec, index) => ({
            id: `${exec.id}-${index}`,
            timestamp: exec.timestampMs > 1_000_000_000_000 ? Math.floor(exec.timestampMs / 1000) : Math.floor(exec.timestampMs),
            side: exec.side === 'sell' ? 'SELL' : 'BUY',
            qty: exec.qty,
            price: Number(exec.price),
            fee: Number(exec.fee) || 0,
        }));
        const mergedBars = (chartReplayState.metricBars || [])
            .map((bar) => ({ time: Number(bar.time), high: Number(bar.high), low: Number(bar.low) }))
            .filter((bar) => Number.isFinite(bar.time) && Number.isFinite(bar.high) && Number.isFinite(bar.low));
        const mergedSummary = buildTradeDetailsDeterministicSummary({
            executions: mergedMetricExecutions,
            bars: mergedBars,
            multiplier: mergeMultiplier,
            clampEfficiency: false,
            debug: false,
        });

        const mergedExecutionsForRow = normalizedExecutions.map((exec, index) => {
            const resolvedRow = resolved[index];
            return {
                storageId: `${exec.id}`,
                timestamp: exec.timestampMs,
                openTimestampMs: exec.timestampMs,
                symbol: mergeSymbol,
                volume: exec.signedQty,
                side: exec.side,
                price: Number(exec.price),
                commissionAndFees: Number(exec.fee) || 0,
                action: resolvedRow?.rowAction,
                positionAfter: resolvedRow?.runningPosition,
                rowResolvedQty: resolvedRow?.rowResolvedQty,
                rowRealizedGross: resolvedRow?.rowRealizedGross,
                rowRealizedNet: resolvedRow?.rowRealizedNet,
                rowFees: resolvedRow?.rowFees,
                durationMinutes: resolvedRow?.rowDurationMinutes,
            };
        });

        const mergedId = `merged-${Date.now()}`;
        const mergedRow = {
            id: mergedId,
            key: mergedId,
            timestamp: minTs,
            symbol: mergeSymbol,
            volume: totalSignedVolume,
            side: totalSignedVolume >= 0 ? 'Long' : 'Short',
            pnl: Number(netPnl),
            rowRealizedGross: Number(grossPnl),
            rowRealizedNet: Number(netPnl),
            durationMinutes,
            fees: Number.isFinite(sourceFees) && sourceFees !== 0 ? sourceFees : Number(recomputedFees),
            commission: Number.isFinite(sourceCommission) ? sourceCommission : 0,
            commissionAndFees: Number(recomputedFees),
            avgEntry: Number.isFinite(Number(avgEntry)) ? Number(avgEntry) : undefined,
            positionAfter: Number(resolved[resolved.length - 1]?.runningPosition ?? 0),
            maxPositionSize,
            tradedSize,
            bestExitGross: Number(mergedSummary.bestExitGross),
            bestExitNet: Number(mergedSummary.bestExitNet),
            efficiencyGrossPct: Number(mergedSummary.efficiencyGrossPct),
            efficiencyNetPct: Number(mergedSummary.efficiencyNetPct),
            mae: Number(mergedSummary.totalMae),
            mfe: Number(mergedSummary.totalMfe),
            positionFlow: resolved.map((row) => ({
                timestampMs: Number.isFinite(Number(row.timestampMs)) ? Number(row.timestampMs) : null,
                positionAfter: Number(row.runningPosition) || 0,
                action: String(row.rowAction || ''),
            })),
            mergedExecutions: mergedExecutionsForRow,
            notes: `Merged from ${toMerge.length} executions (reconstructed)` ,
            price: (() => {
                const firstPrice = normalizedExecutions.find((exec) => Number.isFinite(Number(exec.price)))?.price;
                return Number.isFinite(Number(firstPrice)) ? Number(firstPrice) : undefined;
            })(),
            merged: true,
        } as any;

        // Remove originals from imported storage and customRows, then insert mergedRow
        const importedKeysToDelete: string[] = [];
        const customKeysToRemove = new Set<string>();
        const removedCustomRows: LastMergeSnapshot['removedCustomRows'] = [];
        const removedImportedRows: ImportedTradeRecord[] = [];

        let localTradeForSnapshot: any = null;
        try {
            localTradeForSnapshot = getLocalTradeRow(trade?.id);
        } catch {
            localTradeForSnapshot = null;
        }
        const previousLocalExecutionsList = Array.isArray(localTradeForSnapshot?.executionsList)
            ? [...localTradeForSnapshot.executionsList]
            : undefined;

        toMerge.forEach(r => {
            // identify imported rows by presence of storageId (imported rows in `rows` have storageId)
            if ((r as any).storageId) {
                importedKeysToDelete.push(String((r as any).storageId));
                removedImportedRows.push({
                    storageId: String((r as any).storageId),
                    openTimestampMs: Number((r as any).openTimestampMs ?? (r as any).timestamp) || Date.now(),
                    closeTimestampMs: Number.isFinite(Number((r as any).closeTimestampMs)) ? Number((r as any).closeTimestampMs) : null,
                    timestamp: Number((r as any).timestamp) || Date.now(),
                    symbol: String((r as any).symbol || ''),
                    volume: Number.isFinite(Number((r as any).volume)) ? Number((r as any).volume) : 0,
                    side: String((r as any).side || ''),
                    pnl: Number.isFinite(Number((r as any).pnl)) ? Number((r as any).pnl) : 0,
                    fees: Number.isFinite(Number((r as any).fees)) ? Number((r as any).fees) : 0,
                    commission: Number.isFinite(Number((r as any).commission)) ? Number((r as any).commission) : 0,
                    commissionAndFees: Number.isFinite(Number((r as any).commissionAndFees))
                        ? Number((r as any).commissionAndFees)
                        : ((Number.isFinite(Number((r as any).fees)) ? Number((r as any).fees) : 0) + (Number.isFinite(Number((r as any).commission)) ? Number((r as any).commission) : 0)),
                    durationMinutes: Number.isFinite(Number((r as any).durationMinutes)) ? Number((r as any).durationMinutes) : undefined,
                    notes: String((r as any).notes || ''),
                });
            }
            // mark any custom row ids for removal
            if ((r as any).id) {
                customKeysToRemove.add(String((r as any).id));
                removedCustomRows.push({
                    id: String((r as any).id),
                    timestamp: Number((r as any).timestamp) || Date.now(),
                    symbol: String((r as any).symbol || ''),
                    volume: Number.isFinite(Number((r as any).volume)) ? Number((r as any).volume) : 0,
                    side: ((r as any).side || ((Number((r as any).pnl) || 0) >= 0 ? 'Long' : 'Short')) as 'Long' | 'Short' | '',
                    pnl: Number.isFinite(Number((r as any).pnl)) ? Number((r as any).pnl) : 0,
                    durationMinutes: Number.isFinite(Number((r as any).durationMinutes)) ? Number((r as any).durationMinutes) : undefined,
                    fees: Number.isFinite(Number((r as any).fees)) ? Number((r as any).fees) : 0,
                    commission: Number.isFinite(Number((r as any).commission)) ? Number((r as any).commission) : 0,
                    notes: String((r as any).notes || ''),
                });
            }
        });

        // Update customRows atomically: remove selected custom rows, insert mergedRow at position of earliest original
        setCustomRows(prev => {
            // remove selected custom rows
            const filtered = prev.filter(c => !customKeysToRemove.has(String(c.id)));
            // find insertion index by timestamp (place mergedRow where earliest original was)
            const insertAt = Math.max(0, filtered.findIndex(c => (c.timestamp || 0) >= minTs));
            const next = [...filtered];
            if (insertAt === -1 || insertAt >= next.length) next.push(mergedRow);
            else next.splice(insertAt, 0, mergedRow);
            return next;
        });

        // Mark executions (by storage/key) for removal from any in-memory/grouped lists so they no longer appear
        const execStorageIdsToRemove = toMerge.map(r => String(r.key)).filter(k => !customKeysToRemove.has(k));
        if (execStorageIdsToRemove.length > 0) {
            setRemovedExecutionIds(prev => Array.from(new Set([...(prev || []), ...execStorageIdsToRemove])));

            // If this trade has a local `executionsList` entry in `tt_trades`, remove the executions there too
            try {
                const local = getLocalTradeRow(trade?.id);
                if (local && Array.isArray(local.executionsList)) {
                    const filteredExecs = local.executionsList.filter((ex: any) => {
                        const sid = String(ex.storageId ?? ex.id ?? ex.timestamp ?? ex.time ?? '');
                        // also allow matching by numeric timestamp string
                        const tsStr = String(Number(ex.timestamp || ex.time || 0));
                        return !execStorageIdsToRemove.includes(sid) && !execStorageIdsToRemove.includes(tsStr);
                    });

                    // persist to localStorage list `tt_trades`
                    try {
                        const listRaw = readPrivateTradingJson('trades-cache', 'local', ['tt_trades']);
                        if (listRaw) {
                            const list = JSON.parse(listRaw);
                            if (Array.isArray(list)) {
                                const updated = list.map((r: any) => {
                                    if (String(r?.id) === String(local.id) || String(r?.storageId) === String(local.storageId)) {
                                        return { ...r, executionsList: filteredExecs };
                                    }
                                    return r;
                                });
                                writePrivateTradingJson('trades-cache', JSON.stringify(updated));
                            }
                        }
                    } catch { /* ignore persistence errors */ }

                    // update session snapshot for immediate UI reflection in other views
                    try {
                        const rawLast = readPrivateTradingJson('last-view-trade', 'session', ['tt_last_view_trade']);
                        if (rawLast) {
                            const parsed = JSON.parse(rawLast);
                            if (parsed && (String(parsed.id) === String(local.id) || String(parsed.storageId) === String(local.storageId))) {
                                parsed.executionsList = filteredExecs;
                                writePrivateTradingJson('last-view-trade', JSON.stringify(parsed), 'session');
                            }
                        }
                    } catch { /* ignore */ }
                }
            } catch { /* ignore */ }
        }

        // Save one-step undo snapshot
        setLastMergeSnapshot({
            mergedRowId: mergedId,
            removedCustomRows,
            removedImportedRows,
            removedExecutionIds: execStorageIdsToRemove,
            localTradeId: String(localTradeForSnapshot?.id ?? ''),
            localTradeStorageId: String(localTradeForSnapshot?.storageId ?? ''),
            previousLocalExecutionsList,
        });

        // Delete imported rows after updating state
        importedKeysToDelete.forEach(k => deleteImportedTrade(k));
    };

    const handleUndoMerge = () => {
        if (!lastMergeSnapshot) return;

        // Remove merged row and restore removed custom rows
        setCustomRows(prev => {
            const withoutMerged = prev.filter(r => String(r.id) !== String(lastMergeSnapshot.mergedRowId));
            const existing = new Set(withoutMerged.map(r => String(r.id)));
            const restored = [...withoutMerged];
            (lastMergeSnapshot.removedCustomRows || []).forEach(r => {
                if (!existing.has(String(r.id))) restored.push(r);
            });
            restored.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
            return restored;
        });

        // Restore deleted imported executions
        if (Array.isArray(lastMergeSnapshot.removedImportedRows) && lastMergeSnapshot.removedImportedRows.length > 0) {
            saveImportedTrades(lastMergeSnapshot.removedImportedRows, { append: true });
        }

        // Unmark removed execution IDs so grouped/local execution sources become visible again
        const removedSet = new Set((lastMergeSnapshot.removedExecutionIds || []).map(String));
        setRemovedExecutionIds(prev => (prev || []).filter(id => !removedSet.has(String(id))));

        // Restore local stored executionsList for this trade when available
        if (Array.isArray(lastMergeSnapshot.previousLocalExecutionsList)) {
            try {
                const listRaw = readPrivateTradingJson('trades-cache', 'local', ['tt_trades']);
                if (listRaw) {
                    const list = JSON.parse(listRaw);
                    if (Array.isArray(list)) {
                        const updated = list.map((r: any) => {
                            const sameTrade =
                                String(r?.id ?? '') === String(lastMergeSnapshot.localTradeId ?? '') ||
                                String(r?.storageId ?? '') === String(lastMergeSnapshot.localTradeStorageId ?? '');
                            return sameTrade
                                ? { ...r, executionsList: [...(lastMergeSnapshot.previousLocalExecutionsList || [])] }
                                : r;
                        });
                        writePrivateTradingJson('trades-cache', JSON.stringify(updated));
                    }
                }
            } catch {}

            try {
                const rawLast = readPrivateTradingJson('last-view-trade', 'session', ['tt_last_view_trade']);
                if (rawLast) {
                    const parsed = JSON.parse(rawLast);
                    const sameTrade =
                        String(parsed?.id ?? '') === String(lastMergeSnapshot.localTradeId ?? '') ||
                        String(parsed?.storageId ?? '') === String(lastMergeSnapshot.localTradeStorageId ?? '');
                    if (sameTrade) {
                        parsed.executionsList = [...(lastMergeSnapshot.previousLocalExecutionsList || [])];
                        writePrivateTradingJson('last-view-trade', JSON.stringify(parsed), 'session');
                    }
                }
            } catch {}
        }

        setLastMergeSnapshot(null);
    };

    const handleDeleteTrade = (id: string | number | undefined) => {
        const execId = String(id ?? '').trim();

        // 1) Try custom rows (by id or key)
        const removedFromCustom = customRows.some((r: any) => String(r.id) === execId || String((r as any).key ?? '') === execId);
        if (removedFromCustom) {
            setCustomRows(prev => prev.filter((r: any) => !(String(r.id) === execId || String((r as any).key ?? '') === execId)));
            // mark as removed so grouped views / charts drop the marker immediately
            setRemovedExecutionIds(prev => Array.from(new Set([...(prev || []), execId])));
            return;
        }

        // 2) Try imported records (identify by storageId, key, or timestamp)
        const found = imported.find((rec: any) => {
            try {
                if (!rec) return false;
                const storage = String(rec.storageId ?? '');
                const key = String(rec.key ?? '');
                const ts = String(Number(rec.timestamp || rec.openTimestampMs || rec.time || 0));
                if (storage && storage === execId) return true;
                if (key && key === execId) return true;
                if (ts && ts === execId) return true;
                return false;
            } catch { return false; }
        });

        if (found) {
            // remove from imported storage using storageId when available, fallback to timestamp
            const toDelete = String(found.storageId ?? found.timestamp ?? execId);
            try { deleteImportedTrade(toDelete); } catch { /* best-effort */ }
            setRemovedExecutionIds(prev => Array.from(new Set([...(prev || []), toDelete])));

            // Also remove from any persisted local trade executionsList if present
            try {
                const raw = readPrivateTradingJson('trades-cache', 'local', ['tt_trades']);
                if (raw) {
                    const list = JSON.parse(raw) as any[];
                    if (Array.isArray(list)) {
                        const updated = list.map(r => {
                            if (!Array.isArray(r.executionsList)) return r;
                            return { ...r, executionsList: r.executionsList.filter((ex: any) => {
                                const exId = String(ex.storageId ?? ex.id ?? ex.timestamp ?? ex.time ?? ex.key ?? '');
                                return exId !== execId && exId !== String(found.storageId ?? '');
                            }) };
                        });
                        try { writePrivateTradingJson('trades-cache', JSON.stringify(updated)); } catch {}
                    }
                }
            } catch { /* ignore persistence errors */ }

            return;
        }

        // 3) Fallback: if execId looks numeric, try match by numeric timestamps
        const asNum = Number(execId);
        if (Number.isFinite(asNum)) {
            const matchByTs = imported.find((rec: any) => Number(rec.timestamp) === asNum || Number(rec.openTimestampMs) === asNum || Number(rec.closeTimestampMs) === asNum);
            if (matchByTs) {
                const toDelete = String(matchByTs.storageId ?? matchByTs.timestamp ?? execId);
                try { deleteImportedTrade(toDelete); } catch {}
                setRemovedExecutionIds(prev => Array.from(new Set([...(prev || []), toDelete])));
                return;
            }
        }

        // 4) As last resort, mark the id as removed so UI/markers update and avoid stale rows
        setRemovedExecutionIds(prev => Array.from(new Set([...(prev || []), execId])));
    };

    const openEditModal = (trade: any) => {
        setEditingTrade(trade);
        setIsCustomEntryOpen(true);
    };

    const openViewModal = (trade: any) => {
        const ts = normalizeTimestampToMs(
            (trade as any)?.openTimestampMs
            ?? (trade as any)?.timestamp
            ?? (trade as any)?.time
            ?? (trade as any)?.date
        );
        if (!Number.isFinite(Number(ts))) return;
        const timestampMs = Number(ts);
        const executionId = String(
            (trade as any)?.storageId
            ?? (trade as any)?.executionId
            ?? (trade as any)?.id
            ?? (trade as any)?.orderId
            ?? (trade as any)?.key
            ?? timestampMs
        );

        const highlightedKey = String((trade as any)?.key ?? (trade as any)?.storageId ?? (trade as any)?.id ?? executionId);
        setHighlightedExecutionRowKey(highlightedKey);
        if (executionHighlightTimerRef.current != null) {
            try { window.clearTimeout(executionHighlightTimerRef.current); } catch {}
        }
        executionHighlightTimerRef.current = window.setTimeout(() => {
            setHighlightedExecutionRowKey((current) => (current === highlightedKey ? null : current));
            executionHighlightTimerRef.current = null;
        }, 2100);

        try {
            chartSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch {}

        setChartFocusRequest((prev) => ({
            executionId,
            timestampMs,
            pulseNonce: Number(prev?.pulseNonce ?? 0) + 1,
        }));

        setChartReplayState((prev) => {
            if (!prev.isReplayMode) return prev;
            if (prev.replayTimeMs === timestampMs) return prev;
            return {
                ...prev,
                replayTimeMs: timestampMs,
            };
        });
    };

    React.useEffect(() => {
        return () => {
            if (executionHighlightTimerRef.current != null) {
                try { window.clearTimeout(executionHighlightTimerRef.current); } catch {}
                executionHighlightTimerRef.current = null;
            }
        };
    }, []);

    const handleModalClose = () => {
        setIsCustomEntryOpen(false);
        setEditingTrade(null);
    }

    const metricBars = React.useMemo(() => chartReplayState.metricBars, [chartReplayState.metricBars]);
    const currentMarkPrice = chartReplayState.currentMarkPrice;
    const currentMarkPriceNumber = Number(currentMarkPrice);
    const hasCurrentMarkPrice = currentMarkPrice != null && Number.isFinite(currentMarkPriceNumber) && currentMarkPriceNumber > 0;

    const latestPositionAfter = React.useMemo(() => {
        const candidates = [
            Number((trade as any)?.positionAfter),
            ...rows.map((row) => Number((row as any)?.positionAfter ?? (row as any)?.position_after ?? (row as any)?.netPositionAfter)),
        ].filter((value) => Number.isFinite(value));
        if (!candidates.length) return null;
        return candidates[candidates.length - 1];
    }, [rows, trade]);

    const selectedCloseTimestampMs = React.useMemo(() => {
        const tradeCloseTimestamp = normalizeTimestampToMs(
            (trade as any)?.closeTimestampMs
            ?? (trade as any)?.closedAt
            ?? (trade as any)?.closeTime
            ?? (trade as any)?.closeDate
            ?? (trade as any)?.exitTimestampMs
            ?? (trade as any)?.exitTimestamp
            ?? (trade as any)?.exitTime
        );
        if (tradeCloseTimestamp != null) return tradeCloseTimestamp;

        let latestRowClose: number | null = null;
        for (const row of rows) {
            const rowCloseTimestamp = normalizeTimestampToMs(
                (row as any)?.closeTimestampMs
                ?? (row as any)?.closedAt
                ?? (row as any)?.closeTime
                ?? (row as any)?.closeDate
                ?? (row as any)?.exitTimestampMs
                ?? (row as any)?.exitTimestamp
                ?? (row as any)?.exitTime
            );
            if (rowCloseTimestamp != null && (latestRowClose == null || rowCloseTimestamp > latestRowClose)) {
                latestRowClose = rowCloseTimestamp;
            }
        }
        return latestRowClose;
    }, [rows, trade]);

    const normalizedTradeStatus = normalizeTradeStatus(trade as any);
    const tradeStatusRaw = String((trade as any)?.status ?? '').trim().toLowerCase();
    const isOpenTrade = normalizedTradeStatus === 'open' || tradeStatusRaw === 'open' || (tradeStatusRaw !== 'closed' && latestPositionAfter != null && latestPositionAfter !== 0 && selectedCloseTimestampMs == null);

    const deterministicMultiplier = React.useMemo(() => {
        if (chartSecType !== 'FUT') return 1;
        return getContractMultiplier(chartSymbol, 'FUTURES', (trade as any)?.instrument);
    }, [chartSecType, chartSymbol, trade]);

    const metricExecutions = React.useMemo<MetricExecution[]>(() => {
        if (rows.length === 0) return [];
        return (executionsForChart || []).map((execution) => ({
            id: execution.id,
            timestamp: execution.timestamp ?? execution.time,
            side: execution.side === 'sell' ? 'SELL' : 'BUY',
            qty: execution.qty,
            price: execution.price,
            fee: Number(execution.commissionAndFees ?? execution.commission ?? execution.fees ?? 0),
        }));
    }, [executionsForChart, rows.length]);
    const tradeInsightExecutions = React.useMemo(() => (executionsForChart || []).map((execution, index) => ({
        id: execution.id ?? execution.executionId ?? execution.storageId ?? execution.key ?? `execution-${index}`,
        timestamp: execution.timestamp ?? execution.time,
        side: execution.side,
        qty: execution.qty,
        price: execution.price,
        fee: Number(execution.commissionAndFees ?? execution.commission ?? execution.fees ?? 0),
        actionGroupId: execution.orderId ?? null,
    })), [executionsForChart]);
    const tradeInsightExecutionStructure = React.useMemo(() => getExecutionStructure(tradeInsightExecutions), [tradeInsightExecutions]);
    const tradeInsightExecutionTimestampResolutionMs = React.useMemo(() => {
        const resolutions = (executionsForChart || [])
            .map((execution) => Number(execution.timestampResolutionMs ?? inferTradeInsightTimestampResolutionMs(execution.timestamp ?? execution.time)))
            .filter((value) => Number.isFinite(value) && value > 0);
        return resolutions.length > 0 ? Math.max(...resolutions) : null;
    }, [executionsForChart]);

    const tradeInsightMarketBarsCoherent = React.useMemo(() => isOpenTrade || hasCoherentTradeInsightBars({
        executions: tradeInsightExecutions,
        bars: metricBars,
        marketBarResolutionMs: chartReplayState.metricBarResolutionMs,
    }), [isOpenTrade, tradeInsightExecutions, metricBars, chartReplayState.metricBarResolutionMs]);
    const excursionBars = tradeInsightMarketBarsCoherent ? metricBars : [];
    const excursionPriceMismatch = chartReplayState.priceMismatch || (metricBars.length > 0 && !tradeInsightMarketBarsCoherent);
    const deterministicSummary = React.useMemo(() => {
        return buildTradeDetailsDeterministicSummary({
            executions: metricExecutions,
            bars: excursionBars,
            multiplier: deterministicMultiplier,
            clampEfficiency: false,
            debug: false,
        });
    }, [metricExecutions, excursionBars, deterministicMultiplier]);

    const hasCanonicalBars = excursionBars.length > 0;
    const hasCanonicalExecutions = metricExecutions.length > 0;
    const normalizedTradeDetails = React.useMemo(() => normalizeTradeForDetails(trade as any), [trade]);
    const financialSummary = React.useMemo(() => resolveTradeDetailsFinancialSummary({
        mode: metricDisplayMode,
        normalizedTrade: normalizedTradeDetails,
        deterministicSummary,
        hasCanonicalExecutions,
        hasChartBars: hasCanonicalBars,
        preferNormalizedExcursions: selectedTradeIsDemo,
        suppressExcursions: excursionPriceMismatch && !selectedTradeIsDemo,
    }), [deterministicSummary, hasCanonicalBars, hasCanonicalExecutions, metricDisplayMode, normalizedTradeDetails, selectedTradeIsDemo, excursionPriceMismatch]);
    const canonicalExcursionAvailability = chartReplayState.isLoading && !hasCanonicalBars
        ? 'loading'
        : financialSummary.excursionAvailability;
    const canonicalExcursionUnavailableReason = financialSummary.excursionErrorReason || (
        canonicalExcursionAvailability === 'unavailable'
            ? 'A complete excursion series is unavailable for this trade.'
            : null
    );
    // Total volume should be absolute (buys + sells)
    const volume = React.useMemo(() => rows.reduce((acc, r) => {
        const v = Number.isFinite((r as any).volume) ? Number((r as any).volume) : 0;
        return acc + Math.abs(v);
    }, 0), [rows]);
    const displayedVolume = volume > 0 ? volume : (normalizedTradeDetails.tradedSize ?? normalizedTradeDetails.quantity ?? 0);

    // Calculate Max Position Size
    const maxPositionSize = React.useMemo(() => computePeakOpenPosition(rows), [rows]);
    const displayedMaxPositionSize = maxPositionSize > 0 ? maxPositionSize : (normalizedTradeDetails.maxPositionSize ?? normalizedTradeDetails.quantity ?? 0);
    const tradedSizeTitle = chartSecType === 'FUT' ? 'Contracts Traded' : 'Shares Traded';
    const tradedSizeSubtext = chartSecType === 'FUT' ? 'Total Contracts' : 'Total Shares';
    const isGrossMode = metricDisplayMode === 'GROSS';
    const tradeInsightTickSize = React.useMemo(() => {
        if (chartSecType !== 'FUT') return null;
        return resolveFuturesTickSize(chartSymbol, (trade as any)?.instrument) ?? null;
    }, [chartSecType, chartSymbol, trade]);
    const tradeInsightHasSingleCanonicalCycle = tradeInsightExecutionStructure?.positionCycleCount === 1;
    const tradeInsightMfeTimestampMs = tradeInsightHasSingleCanonicalCycle && tradeInsightMarketBarsCoherent && chartReplayState.metricSource === 'market_bars'
        ? deterministicSummary.favorablePeakTimestampMs
        : null;
    const tradeInsightFinalExitTimestampMs = tradeInsightExecutionStructure?.finalExitTimestampMs ?? null;
    const tradeInsightMfe = financialSummary.selectedMfe;
    const tradeInsightMae = financialSummary.selectedMae;
    const tradeInsightComparableRetentions = React.useMemo(() => {
        if (selectedTradeIsDemo) return [];
        const source = getTradeInsightComparableSource(trade as any, comparableTrades.length > 0 ? comparableTrades : []);
        const values = source
            .filter((candidate: any) => isSameTradeInsightProfile(trade as any, candidate))
            .map((candidate: any) => getTradeInsightRetention(candidate, metricDisplayMode))
            .filter((value): value is number => value != null && Number.isFinite(value));
        if (values.length >= 20) return values;
        const fallbackValues = source
            .map((candidate: any) => getTradeInsightRetention(candidate, metricDisplayMode))
            .filter((value): value is number => value != null && Number.isFinite(value));
        return fallbackValues.length >= 20 ? fallbackValues : values;
    }, [comparableTrades, metricDisplayMode, selectedTradeIsDemo, trade]);
    const activePnl = financialSummary.selectedPnl ?? 0;
    const openTradeState = React.useMemo(() => {
        if (!isOpenTrade || !hasCurrentMarkPrice || metricExecutions.length === 0) return null;
        try {
            return computeDeterministicPositionState({
                executions: metricExecutions,
                multiplier: deterministicMultiplier,
                markPrice: currentMarkPriceNumber,
            });
        } catch {
            return null;
        }
    }, [currentMarkPriceNumber, deterministicMultiplier, hasCurrentMarkPrice, isOpenTrade, metricExecutions]);

    const openTradePnl = openTradeState
        ? (isGrossMode
            ? openTradeState.realizedGross + openTradeState.unrealizedGross
            : openTradeState.totalPnl)
        : null;
    const summaryPnlValue = isOpenTrade
        ? (Number.isFinite(Number(openTradePnl)) ? Number(openTradePnl) : null)
        : (Number.isFinite(Number(activePnl)) ? Number(activePnl) : null);

    const tradeSideTextRaw = String((trade as any)?.side ?? rows?.[rows.length - 1]?.side ?? '').trim().toLowerCase();
    const inferredShortSide = tradeSideTextRaw.startsWith('s') || tradeSideTextRaw.includes('short') || tradeSideTextRaw === 'sell' || Number((trade as any)?.volume) < 0;
    const captureTitle = chartSecType === 'FUT' ? 'Ticks Captured' : (chartSecType === 'CASH' ? 'Pips Captured' : 'Price Captured');
    const captureUnitLabel = getCaptureUnitLabel(chartSecType);
    const captureUnitSize = chartSecType === 'FUT'
        ? resolveFuturesTickSize(chartSymbol, (trade as any)?.instrument)
        : chartSecType === 'CASH'
            ? (getForexQuoteCurrency(chartSymbol) === 'JPY' ? 0.01 : 0.0001)
            : 1;
    const captureEntryPrice = readFirstPositiveFiniteNumber(
        (trade as any)?.entryPrice,
        (trade as any)?.avgEntryPrice,
        (trade as any)?.avgEntry,
        rows[0]?.price,
        metricExecutions[0]?.price,
    );
    const captureExitPrice = isOpenTrade
        ? (hasCurrentMarkPrice ? currentMarkPriceNumber : null)
        : readFirstPositiveFiniteNumber(
            (trade as any)?.exitPrice,
            (trade as any)?.avgExitPrice,
            (trade as any)?.avgExit,
            rows[rows.length - 1]?.price,
            metricExecutions[metricExecutions.length - 1]?.price,
        );
    const captureMovementValue = captureEntryPrice != null && captureExitPrice != null
        ? (chartSecType === 'FUT' && captureUnitSize == null
            ? null
            : computeDirectionalTicks(captureEntryPrice, captureExitPrice, inferredShortSide ? 'sell' : 'buy', captureUnitSize ?? 1))
        : normalizedTradeDetails.priceCaptured;
    const riskRewardRatio = financialSummary.selectedRiskRewardRatio;
    const hasInitialRisk = normalizedTradeDetails.initialRisk != null
        && Number.isFinite(normalizedTradeDetails.initialRisk)
        && normalizedTradeDetails.initialRisk > 0;
    const riskStopPreview = React.useMemo(() => {
        if (!riskEditorOpen || riskMethod !== 'stopPrice' || riskStopInput.trim() === '') return null;
        const opening = metricExecutions[0];
        if (!opening) return { ok: false as const, reason: 'Opening execution data is unavailable. Use Risk Amount ($).' };
        const openingSide = String(opening.side).toUpperCase() === 'SELL' ? 'sell' : 'buy';
        const openingQuantity = metricExecutions
            .filter((execution) => String(execution.side).toUpperCase() === String(opening.side).toUpperCase()
                && Math.abs(execution.timestamp - opening.timestamp) <= 1000)
            .reduce((sum, execution) => sum + Math.abs(execution.qty), 0);
        const assetClass: StopRiskAssetClass = chartSecType === 'FUT' ? 'FUTURES'
            : chartSecType === 'CASH' ? 'FOREX'
                : chartSecType === 'CRYPTO' ? 'CRYPTO'
                    : chartSecType === 'STK' ? 'STOCKS' : 'OTHER';
        return computeInitialRiskFromStop({
            assetClass,
            side: openingSide,
            entryPrice: opening.price,
            quantity: openingQuantity,
            stopPrice: Number(riskStopInput),
            futuresMultiplier: assetClass === 'FUTURES' ? deterministicMultiplier : null,
        });
    }, [riskEditorOpen, riskMethod, riskStopInput, metricExecutions, chartSecType, deterministicMultiplier]);
    const riskInputBlank = (riskMethod === 'amount' ? riskAmountInput : riskStopInput).trim() === '';
    const riskAmountValid = Number.isFinite(Number(riskAmountInput)) && Number(riskAmountInput) > 0;
    const riskCanSave = riskInputBlank || (riskMethod === 'amount' ? riskAmountValid : riskStopPreview?.ok === true);
    const saveRiskFromDetails = async () => {
        if (!riskCanSave) return;
        const amount = riskInputBlank ? null : riskMethod === 'amount' ? Number(riskAmountInput) : riskStopPreview?.ok ? riskStopPreview.totalRisk : null;
        setRiskSaving(true);
        setRiskSaveError('');
        try {
            if (!onSaveInitialRisk) throw new Error('Initial Risk cannot be saved for this trade.');
            await onSaveInitialRisk(amount, amount == null ? null : riskMethod, amount != null && riskMethod === 'stopPrice' ? Number(riskStopInput) : null);
            setRiskEditorOpen(false);
        } catch (error) {
            setRiskSaveError(error instanceof Error ? error.message : 'Initial Risk could not be saved. Your input is still here; please try again.');
        } finally {
            setRiskSaving(false);
        }
    };
    const topMae = financialSummary.selectedMae;
    const topMfe = financialSummary.selectedMfe;
    const excursionMetricLoading = canonicalExcursionAvailability === 'loading';
    const formatExcursionMetricValue = React.useCallback((value: number | null, signed = false) => {
        if (excursionMetricLoading) {
            return <span className="inline-block h-8 w-full max-w-[128px] animate-pulse rounded bg-zinc-700/60 align-middle" aria-label="Loading excursion metric" />;
        }
        if (value == null) {
            return <span className="text-[clamp(0.75rem,10cqi,1.125rem)] font-bold text-zinc-500 dark:text-zinc-400">Unavailable</span>;
        }
        return signed ? formatTopCurrency(value, true) : formatTopCurrency(value);
    }, [excursionMetricLoading]);
    const smallMetricValueClassName = 'top-metric-value';
    const activePnlToneClassName = getSignedToneClass(activePnl, 'text-[#22C55E]', 'text-[#EF4444]');
    const feeDisplayValue = -Math.abs(financialSummary.totalFees);
    const feeToneClassName = getSignedToneClass(feeDisplayValue, 'text-[#22C55E]', 'text-[#EF4444]');
    const dark = isDarkTheme();
    const openBadgeNode = (
        <span
            className={`inline-flex items-center rounded-md px-3 py-1 text-sm font-semibold tracking-[0.18em] -translate-y-3 ${
                dark
                    ? 'bg-emerald-950/40 text-emerald-300 border border-emerald-700 shadow-[inset_0_0_0_1px_rgba(16,185,129,0.12)]'
                    : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            }`}
        >
            POSITION OPEN
        </span>
    );
    const summaryPnlToneClassName = isOpenTrade
        ? (dark ? 'text-[#71A7FF]' : 'text-[#3788E5]')
        : activePnlToneClassName;
    const summaryPnlValueNode = isOpenTrade
        ? openBadgeNode
        : summaryPnlValue != null
            ? formatTopCurrency(summaryPnlValue)
            : <span className="text-zinc-400 dark:text-zinc-500">{MISSING_VALUE}</span>;
    const tradeSideLabel = inferredShortSide ? 'SHORT' : 'LONG';
    const tradeSideToneClassName = inferredShortSide
        ? (dark ? 'bg-[#2A1414] text-[#FF8A8A] border-[#7A2424]' : 'bg-red-50 text-red-700 border-red-200')
        : (dark ? 'bg-[#10261B] text-[#7EE2A8] border-[#235A3A]' : 'bg-emerald-50 text-emerald-700 border-emerald-200');
    const riskRewardToneClassName = riskRewardRatio == null
        ? (dark ? 'text-white' : 'text-zinc-900')
        : getSignedToneClass(riskRewardRatio, 'text-[#22C55E]', 'text-[#EF4444]');
    const capturedAmountToneClassName = getSignedToneClass(captureMovementValue, 'text-[#22C55E]', 'text-[#EF4444]');
    const captureMovementValueNode = isOpenTrade
        ? <span className="font-bold text-zinc-700 dark:text-zinc-200">N/A</span>
        : formatMovementValueWithUnit(captureMovementValue, captureUnitLabel);
    const topMaeToneClassName = getZeroNeutralToneClass(topMae, 'text-[#EF4444]');
    const topMfeToneClassName = getZeroNeutralToneClass(topMfe, 'text-[#22C55E]');

    useEffect(() => {
        if (!import.meta.env.DEV) return;
        console.info('[TradeDetails][DEV] MFE/MAE diagnostics', {
            summaryMfe: deterministicSummary.avgPositionMfe,
            summaryMae: deterministicSummary.avgPositionMae,
            displayedMfe: topMfe,
            displayedMae: topMae,
        });
    }, [deterministicSummary.avgPositionMfe, deterministicSummary.avgPositionMae, topMfe, topMae]);

    // isDark state removed — was unused in this component

    // Notes Data
    const { note: dailyNote, saveNote: saveDailyNote } = useDailyNotes(baseDate);
    const note = tradeNotes?.value ?? dailyNote;
    const saveNote = tradeNotes?.onChange ?? saveDailyNote;
    const dailyTags = useDailyTags(baseDate);
    const tags = tradeTags?.value ?? dailyTags.tags;
    const addTag = tradeTags?.onAdd ?? dailyTags.addTag;
    const removeTag = tradeTags?.onRemove ?? dailyTags.removeTag;
    const compactTradeTags = isDemoTrade(trade);
    const displayedTags = getVisibleTradeTags(tags, compactTradeTags, trade);

    // Detect whether a side menu/sidebar is present (used to adjust header spacing)
    const [sideMenuActive, setSideMenuActive] = React.useState<boolean>(() => {
        try {
            return !!document.querySelector('.sidebar-root');
        } catch {
            return false;
        }
    });

    React.useEffect(() => {
        try { setSideMenuActive(!!document.querySelector('.sidebar-root')); } catch {}
    }, []);



    return (
        <div className="w-full max-w-none min-h-screen bg-transparent pb-8 text-zinc-900 dark:bg-transparent dark:text-white py-0 px-0" data-tour-target="trade-details">
            <div className="w-full max-w-none min-w-0 space-y-6">
                <header className={`flex flex-col gap-4 border-b ${dark ? 'border-[#242428]' : 'border-zinc-200'} pb-4 mb-8 ${sideMenuActive ? 'pt-0' : 'mt-4'}`}>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div className="min-w-0 flex-1">
                        {sideMenuActive && (
                            <button
                                type="button"
                                onClick={() => { try { (window as any).__TT_navigate?.('Trades'); } catch {} }}
                                className="mb-3 inline-flex h-8 items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-3 text-xs font-semibold uppercase tracking-[0.12em] text-zinc-700 transition-colors hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700"
                            >
                                <ArrowLeft className="h-3.5 w-3.5" />
                                BACK
                            </button>
                        )}
                        <h1 className={`text-4xl font-bold flex items-baseline gap-3 tracking-tight ${dark ? 'text-white' : 'text-zinc-900'}`}>
                            {primaryDisplaySymbol}
                            {resolveTradeDetailsCompanyName(dayTrades?.[0]?.company, liveHeaderName, headerInstrument.name) ? (
                                <span className={`text-lg font-normal ${dark ? 'text-gray-400' : 'text-zinc-500'}`}>
                                    {resolveTradeDetailsCompanyName(dayTrades?.[0]?.company, liveHeaderName, headerInstrument.name)}
                                </span>
                            ) : null}
                        </h1>
                        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
                            <span className={`inline-flex h-8 items-center text-sm font-medium ${dark ? 'text-gray-400' : 'text-zinc-500'}`}>{headerDateDisplay}</span>
                            <div role="group" aria-label="P&L display mode" className={`flex h-8 flex-row items-center gap-1 rounded-lg border p-0.5 shadow-[inset_0_2px_5px_rgba(15,23,42,0.14)] ${dark ? 'border-zinc-700/80 bg-zinc-800/90 shadow-[inset_0_2px_6px_rgba(0,0,0,0.55)]' : 'border-zinc-200 bg-zinc-50/80'}`}>
                                <button
                                    type="button"
                                    aria-pressed={isGrossMode}
                                    onClick={() => updatePnlMode('GROSS')}
                                    className={`compact-segment-button h-6 rounded-md px-3.5 text-xs font-medium transition-colors ${isGrossMode ? 'bg-blue-600 text-white shadow-[0_10px_24px_-16px_rgba(37,99,235,0.95)]' : (dark ? 'text-zinc-300 hover:bg-zinc-700/90 hover:text-white' : 'text-zinc-600 hover:bg-white')}`}
                                >
                                    Gross
                                </button>
                                <button
                                    type="button"
                                    aria-pressed={!isGrossMode}
                                    onClick={() => updatePnlMode('NET')}
                                    className={`compact-segment-button h-6 rounded-md px-3.5 text-xs font-medium transition-colors ${!isGrossMode ? 'bg-blue-600 text-white shadow-[0_10px_24px_-16px_rgba(37,99,235,0.95)]' : (dark ? 'text-zinc-300 hover:bg-zinc-700/90 hover:text-white' : 'text-zinc-600 hover:bg-white')}`}
                                >
                                    Net
                                </button>
                            </div>
                        <span className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold border ${tradeSideToneClassName}`} title={tradeSideLabel === 'SHORT' ? 'Short position' : 'Long position'}>
                            {tradeSideLabel === 'SHORT' ? <TrendingDown className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />}
                            <span className="uppercase text-[12px] tracking-wide leading-none">{tradeSideLabel}</span>
                        </span>
                        </div>
                    </div>

                                    <div className="flex max-w-full shrink-0 flex-wrap items-center justify-end gap-2 sm:pb-[1px]">
                                        {displayedTags.map((tag) => (
                                            <RemovableTag
                                                key={tag}
                                                label={tag}
                                                title={`Remove ${tag}`}
                                                onRemove={() => removeTag(tag)}
                                                className="!h-[30px] !w-auto !gap-1.5 !rounded-lg !border-[#3788E5]/30 !bg-[#3788E5]/10 !px-3 !py-1.5 !text-[11px] !font-medium !uppercase !tracking-wide !text-[#3788E5] hover:!bg-[#3788E5]/20"
                                            />
                                        ))}
                                        {displayedTags.length > 0 && (
                                            <span
                                                aria-hidden="true"
                                                className="mx-3 h-5 w-px shrink-0 bg-gray-700"
                                            />
                                        )}
                                        <button
                                            type="button"
                                            className="flex h-9 items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-100 px-3 text-sm font-semibold text-zinc-800 transition-colors hover:bg-zinc-50 dark:border-gray-700 dark:bg-[#1a1a1c] dark:text-gray-100 dark:hover:bg-[#222226]"
                                            onClick={() => setIsTagsModalOpen(true)}
                                        >
                                            <Tag className="h-4 w-4"/> Add/Edit Tags
                                        </button>
                                        <button
                                            type="button"
                                            className="flex h-9 items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-100 px-3 text-sm font-semibold text-zinc-800 transition-colors hover:bg-zinc-50 dark:border-gray-700 dark:bg-[#1a1a1c] dark:text-gray-100 dark:hover:bg-[#222226]"
                                            onClick={onEditTrade}
                                        >
                                            <Edit3 className="h-4 w-4" /> Edit Trade
                                        </button>
                                    </div>
                    </div>
                </header>

                <div className="flex flex-col gap-6">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
                        <div className="lg:col-span-8 flex flex-col gap-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                                <TopMetricCard
                                        title={isOpenTrade ? 'Open P&L' : 'Closed P&L'}
                                        value={summaryPnlValueNode}
                                        tooltipText={isOpenTrade ? 'Current mark-to-market result.' : `${isGrossMode ? 'Gross realized P&L before costs.' : 'Net realized P&L after fees and commissions.'}`}
                                        valueClassName="top-metric-value top-metric-value--lg"
                                        valueToneClassName={summaryPnlToneClassName}
                                        valueStyle={!isOpenTrade && summaryPnlValue != null
                                            ? (summaryPnlValue > 0
                                                ? { textShadow: dark ? '0 0 14px rgba(34,197,94,0.65)' : '0 0 8px rgba(34,197,94,0.28)' }
                                                : summaryPnlValue < 0
                                                    ? { textShadow: dark ? '0 0 14px rgba(239,68,68,0.65)' : '0 0 8px rgba(239,68,68,0.24)' }
                                                    : undefined)
                                            : undefined}
                                        subtext={isOpenTrade
                                            ? (
                                                <div>
                                                    <div>Unrealized {isGrossMode ? 'Gross' : 'Net'} P&L</div>
                                                    {!hasCurrentMarkPrice ? <div className="text-[#3788E5] text-sm font-semibold mt-1">Current mark price unavailable</div> : null}
                                                </div>
                                            )
                                            : `Realized ${isGrossMode ? 'Gross' : 'Net'} P&L`}
                                    />
                                <TopMetricCard
                                    title="Fees & Commissions"
                                    value={formatTopCurrency(feeDisplayValue)}
                                    tooltipText="Fees and commissions are displayed as costs and deducted from Gross P&L to produce Net P&L."
                                    valueClassName={smallMetricValueClassName}
                                    valueToneClassName={feeToneClassName}
                                    subtext="Total Fees"
                                />
                                <TopMetricCard
                                    title={tradedSizeTitle}
                                    value={displayedVolume}
                                    tooltipText={`Total ${chartSecType === 'FUT' ? 'contracts' : 'shares'} executed.`}
                                    valueClassName={smallMetricValueClassName}
                                    valueToneClassName={dark ? 'text-white' : 'text-zinc-900'}
                                    subtext={tradedSizeSubtext}
                                />
                                <TopMetricCard
                                    title="Max Position Size"
                                    value={displayedMaxPositionSize}
                                    tooltipText="Largest size held."
                                    valueClassName={smallMetricValueClassName}
                                    valueToneClassName={dark ? 'text-white' : 'text-zinc-900'}
                                    subtext={displayedMaxPositionSize > 1 ? 'Peak Open Shares' : 'Peak Open Share'}
                                />
                            </div>

                            <section className="flex flex-col">
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                                    <TopMetricCard
                                        title="Risk/Reward Ratio"
                                        value={riskRewardRatio != null && Number.isFinite(riskRewardRatio) ? `${Number(riskRewardRatio).toFixed(2)}R` : 'N/A'}
                                        tooltipText={isOpenTrade ? "Current return versus initial risk.\nRequires Initial Risk to be set on the Trades page or Settings > Charting Setup > Risk rules" : "Return versus initial risk.\nRequires Initial Risk to be set on the Trades page or Settings > Charting Setup > Risk rules"}
                                        valueClassName={smallMetricValueClassName}
                                        valueToneClassName={riskRewardToneClassName}
                                        subtext={(
                                            <div>
                                                <div>{isOpenTrade ? 'Current return vs initial risk' : 'Realized return vs initial risk'}</div>
                                                {isOpenTrade && !hasCurrentMarkPrice ? (
                                                    <div className="text-[#3788E5] text-sm font-semibold mt-1">Current mark price unavailable</div>
                                                ) : null}
                                                {!hasInitialRisk ? (
                                                    <button
                                                        type="button"
                                                        className="mt-1 text-sm font-semibold text-[#EF4444] underline underline-offset-2 hover:text-red-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#EF4444]"
                                                        onClick={() => {
                                                            if (!trade) return;
                                                            setRiskMethod('amount');
                                                            setRiskAmountInput('');
                                                            setRiskStopInput('');
                                                            setRiskSaveError('');
                                                            setRiskEditorOpen(true);
                                                        }}
                                                    >
                                                        Initial Risk not set
                                                    </button>
                                                ) : null}
                                            </div>
                                        )}
                                    />
                                    <TopMetricCard
                                        title={captureTitle}
                                        value={captureMovementValueNode}
                                        tooltipText={isOpenTrade ? 'Difference between entry and the latest chart price.' : 'Difference between entry and exit.'}
                                        valueClassName={smallMetricValueClassName}
                                        valueToneClassName={isOpenTrade ? (dark ? 'text-zinc-200' : 'text-zinc-700') : capturedAmountToneClassName}
                                        subtext={isOpenTrade ? (hasCurrentMarkPrice ? 'Current move vs entry' : 'Mark price unavailable') : 'Realized move'}
                                    />
                                    <TopMetricCard
                                        title={`MAE (${isGrossMode ? 'Gross' : 'Net'})`}
                                        value={formatExcursionMetricValue(topMae)}
                                        tooltipText={`Worst unrealized drawdown. Displayed as ${isGrossMode ? 'Gross' : 'Net'}.`}
                                        valueClassName={smallMetricValueClassName}
                                        valueToneClassName={topMaeToneClassName}
                                        subtext="Drawdown (MAE)"
                                    />
                                    <TopMetricCard
                                        title={`MFE (${isGrossMode ? 'Gross' : 'Net'})`}
                                        value={formatExcursionMetricValue(topMfe, true)}
                                        tooltipText={`Best unrealized profit. Displayed as ${isGrossMode ? 'Gross' : 'Net'}.`}
                                        valueClassName={smallMetricValueClassName}
                                        valueToneClassName={topMfeToneClassName}
                                        subtext="Upside (MFE)"
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="lg:col-span-4 flex min-h-0">
                            <TopTradeExcursionCard
                                isGross={isGrossMode}
                                pnl={summaryPnlValue ?? Number.NaN}
                                mae={topMae}
                                mfe={topMfe}
                                isOpen={isOpenTrade}
                                hasCurrentMarkPrice={hasCurrentMarkPrice}
                                availability={canonicalExcursionAvailability}
                                unavailableReason={canonicalExcursionUnavailableReason}
                            />
                        </div>

                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
                        <div className="lg:col-span-8 flex flex-col lg:h-full">
                            <div className="flex-1">
                                <TopNotesSection note={String(note || '')} onChange={saveNote} />
                            </div>
                        </div>

                        <div className="lg:col-span-4 flex flex-col lg:h-full">
                            <TradeInsightCard
                                tradeId={(trade as any)?.tradeId ?? (trade as any)?.id ?? (trade as any)?.storageId}
                                closedPnl={summaryPnlValue}
                                mae={tradeInsightMae}
                                mfe={tradeInsightMfe}
                                isOpen={isOpenTrade}
                                mfeTimestampMs={tradeInsightMfeTimestampMs}
                                maeTimestampMs={deterministicSummary.maximumDrawdownTimestampMs}
                                firstEntryTimestampMs={tradeInsightExecutionStructure?.firstEntryTimestampMs ?? null}
                                finalExitTimestampMs={tradeInsightFinalExitTimestampMs}
                                executionTimestampResolutionMs={tradeInsightExecutionTimestampResolutionMs}
                                marketBarResolutionMs={chartReplayState.metricBarResolutionMs}
                                metricSource={tradeInsightMarketBarsCoherent ? chartReplayState.metricSource : null}
                                entryCount={tradeInsightExecutionStructure?.entryCount ?? null}
                                exitCount={tradeInsightExecutionStructure?.exitCount ?? null}
                                executionCountKind={tradeInsightExecutionStructure?.countKind ?? null}
                                initialRiskAmount={normalizedTradeDetails.initialRisk}
                                peakPositionSize={displayedMaxPositionSize}
                                instrumentTickSize={tradeInsightTickSize}
                                instrumentPointValue={chartSecType === 'FUT' ? deterministicMultiplier : null}
                                estimatedRoundTripTradingCosts={financialSummary.totalFees}
                                comparableExitRetentions={tradeInsightComparableRetentions}
                            />
                        </div>
                    </div>
                </div>

{/* --- CHART (Above Executions Table) --- */}
<div ref={chartSectionRef} className="w-full pt-2">
    <div className="flex items-center gap-2 mb-3">
                        <h2 className="text-lg font-semibold text-zinc-900 dark:text-white" data-tour-target="trade-replay">Chart / Replay</h2>
        {replayChartEnabled && !replayChartBlockedMessage && !replayBlockedMessage && (
            <InstantTooltip content="Replay is currently in beta. Data availability and playback behavior may vary by symbol and provider.">
                <span
                    aria-label="Replay is in Beta"
                    className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded-full bg-blue-100 dark:bg-zinc-800 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-zinc-700 cursor-help"
                >
                    BETA
                </span>
            </InstantTooltip>
        )}
    </div>
    <div className="w-full mb-6 bg-white dark:bg-[#1a1a1c] p-0 overflow-hidden rounded-none md:rounded-xl border border-zinc-200 dark:border-[#242428] shadow-md">
    {chartExecutionSourceMessage ? (
        <div
            role="status"
            aria-live="polite"
            className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/35 dark:text-amber-200"
        >
            {chartExecutionSourceMessage}
        </div>
    ) : null}
    { !FORCE_NOTES_FIRST ? (
        !replayLaunchControlsReady ? (
            <div className="h-[420px] w-full flex items-center justify-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 text-xs font-semibold">
                    Checking replay availability...
                </div>
            </div>
        ) : replayChartBlockedMessage ? (
            <div className="h-[420px] w-full flex items-center justify-center">
                <div className="max-w-2xl inline-flex items-start gap-2 px-3 py-2 rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 text-sm font-medium">
                    {replayChartBlockedMessage}
                </div>
            </div>
        ) : replayBlockedMessage ? (
            <div className="h-[420px] w-full flex items-center justify-center">
                <div className="max-w-2xl inline-flex items-start gap-2 px-3 py-2 rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 text-sm font-medium">
                    {replayBlockedMessage}
                </div>
            </div>
        ) : chartSymbol ? (
            <TradeReplayChart
                trade={chartTrade}
                tradeId={chartTradeId}
                isDemo={selectedTradeIsDemo}
                displaySymbol={primaryDisplaySymbol}
                executionUnixSec={executionUnixSec}
                assetClass={chartAssetClass}
                executions={executionsForChart}
                focusExecutionRequest={chartFocusRequest}
                onReplayStateChange={handleReplayStateChange}
            />
        ) : (
            <div className="h-[720px] w-full flex items-start justify-start">
                <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 text-xs font-semibold">
                    Missing symbol — select a trade/day with a symbol, or make sure your executions have `symbol`.
                </div>
            </div>
        )
    ) : null}
  </div>
</div>

                {/* --- 3. TRADES TABLE (Restored) --- */}
                <TradesTable
                    rows={visibleRows}
                    visibleColumns={visibleColumns}
                    setVisibleColumns={setVisibleColumns}
                    tzMode={tzMode}
                    onOpenSettings={() => setIsColumnSettingsOpen(true)}
                    onDelete={handleDeleteTrade}
                    onEdit={openEditModal}
                    onView={openViewModal}
                    onMergeSelected={handleMergeSelected}
                    onUndoMerge={handleUndoMerge}
                    canUndoMerge={!!lastMergeSnapshot}
                    highlightedRowKey={highlightedExecutionRowKey}
                    headerSymbol={primaryDisplaySymbol}
                    headerName={dayTrades?.[0]?.company ?? liveHeaderName ?? headerInstrument?.name}
                    headerNameIsYahoo={headerNameIsYahoo}
                    headerDate={headerDate}
                    pnlBadgeMode={metricDisplayMode}
                    onPnlBadgeModeChange={updatePnlMode}
                />

                <CustomEntryModal
                    isOpen={isCustomEntryOpen}
                    onClose={handleModalClose}
                    onAdd={handleSaveTrade}
                    initialData={editingTrade}
                    defaultSymbol={headerInstrument.symbol}
                />

                <TradeDetailsModal
                    isOpen={!!viewingTrade}
                    onClose={() => setViewingTrade(null)}
                    trade={viewingTrade}
                    onSetInitialRisk={handleSetInitialRiskForViewedTrade}
                />

                <ColumnSettingsModal
                    isOpen={isColumnSettingsOpen}
                    onClose={() => setIsColumnSettingsOpen(false)}
                    visibleColumns={visibleColumns}
                    setVisibleColumns={setVisibleColumns}
                />

                <TagsModal
                    isOpen={isTagsModalOpen}
                    onClose={() => setIsTagsModalOpen(false)}
                    tags={tags}
                    onAdd={addTag}
                    onRemove={removeTag}
                />

                {riskEditorOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="presentation">
                        <div className="absolute inset-0 bg-[#030712]/70 backdrop-blur-sm" onClick={() => setRiskEditorOpen(false)} />
                        <div ref={riskDialogRef} role="dialog" aria-modal="true" aria-label="Set Initial Risk" className="relative w-full max-w-[420px] overflow-hidden rounded-xl border border-zinc-800 bg-[#1a1a1c] text-white shadow-lg">
                            <div className="flex items-start justify-between border-b border-zinc-800 px-5 py-4">
                                <div>
                                    <h3 className="text-lg font-semibold">Set Initial Risk</h3>
                                    <p className="mt-2 text-sm text-zinc-400">{trade?.symbol ?? 'Trade'}</p>
                                </div>
                                <button type="button" aria-label="Close initial risk dialog" onClick={() => setRiskEditorOpen(false)} className="text-zinc-400 hover:text-white"><XIcon className="h-5 w-5" /></button>
                            </div>
                            <div className="px-5 py-6">
                                <p className="text-sm text-zinc-400">Required to calculate R-Multiple metrics.</p>
                                <div className="mt-4 flex gap-1 rounded-md border border-zinc-700 p-1">
                                    <button type="button" onClick={() => { setRiskMethod('stopPrice'); setRiskSaveError(''); }} className={`h-9 flex-1 rounded-md text-sm font-semibold ${riskMethod === 'stopPrice' ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-300'}`}>Stop Price</button>
                                    <button type="button" onClick={() => { setRiskMethod('amount'); setRiskSaveError(''); }} className={`h-9 flex-1 rounded-md text-sm font-semibold ${riskMethod === 'amount' ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-300'}`}>Risk Amount ($)</button>
                                </div>
                                <label htmlFor="trade-details-risk-input" className="mt-6 block text-sm font-semibold text-zinc-300">{riskMethod === 'amount' ? 'Total Risk Amount (USD)' : 'Stop Price'}</label>
                                <input id="trade-details-risk-input" type="number" step={riskMethod === 'amount' ? '0.01' : '0.0001'} autoFocus value={riskMethod === 'amount' ? riskAmountInput : riskStopInput} onChange={(event) => { riskMethod === 'amount' ? setRiskAmountInput(event.target.value) : setRiskStopInput(event.target.value); setRiskSaveError(''); }} placeholder="0.00" className="mt-3 h-10 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 text-sm text-white outline-none focus:border-blue-500" />
                                {!riskInputBlank && riskMethod === 'amount' && !riskAmountValid && <p className="mt-2 text-xs text-red-400">Enter an amount greater than 0.</p>}
                                {!riskInputBlank && riskMethod === 'stopPrice' && riskStopPreview && <p className={`mt-2 text-xs ${riskStopPreview.ok ? 'text-emerald-400' : 'text-red-400'}`}>{riskStopPreview.ok ? `Calculated Initial Risk: ${fmtMoney(riskStopPreview.totalRisk)}` : riskStopPreview.reason}</p>}
                                {riskSaveError && <p role="alert" className="mt-2 text-xs text-red-400">{riskSaveError}</p>}
                                <button type="button" disabled={!riskCanSave || riskSaving} onClick={saveRiskFromDetails} className="mt-6 h-10 w-full rounded-md bg-blue-600 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-45">{riskSaving ? 'Saving…' : riskInputBlank ? 'Save without risk' : 'Save & Calculate'}</button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

// --- HELPER FUNCTIONS FOR TRADE DETAILS (Added per instructions) ---

function normalizeTradeIdToLocalId(tradeId: string | number | undefined) {
    if (tradeId == null) return null;
    const s = String(tradeId);
    // supports: "tt-1295394523" -> 1295394523
    const m = s.match(/(\d+)/);
    return m ? Number(m[1]) : null;
}

function getLocalTradeRow(tradeId: string | number | undefined) {
    const localId = normalizeTradeIdToLocalId(tradeId);
    // Safety check for SSR/environment
    if (typeof localStorage === 'undefined') return null;

    const list = JSON.parse(readPrivateTradingJson('trades-cache', 'local', ['tt_trades']) || "[]") as any[];

    if (!Array.isArray(list) || list.length === 0) return null;

    // match by numeric id OR exact string id OR tradeId field
    return (
        list.find((r) => (localId != null && Number(r?.id) === localId)) ||
        list.find((r) => String(r?.id) === String(tradeId)) ||
        list.find((r) => String(r?.tradeId) === String(tradeId)) ||
        null
    );
}

function asArray<T = any>(v: any): T[] | null {
    return Array.isArray(v) ? v : null;
}

// Returns the array to pass into <TradeReplayChart executions={...} />
function deriveExecutionsForChart(trade: any) {
    if (!trade) return [];

    const toChartTimeMs = (value: unknown): number | null => {
        if (value == null) return null;
        if (value instanceof Date) {
            const ms = value.getTime();
            return Number.isFinite(ms) ? ms : null;
        }

        if (typeof value === 'number' && Number.isFinite(value)) {
            if (value <= 0) return null;
            return value > 1_000_000_000_000 ? Math.floor(value) : Math.floor(value * 1000);
        }

        const raw = String(value).trim();
        if (!raw) return null;

        const asNum = Number(raw);
        if (Number.isFinite(asNum) && asNum > 0) {
            return asNum > 1_000_000_000_000 ? Math.floor(asNum) : Math.floor(asNum * 1000);
        }

        const parsed = Date.parse(raw);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const normalizeSide = (input: any): 'buy' | 'sell' => {
        const raw = String(input?.side ?? input?.action ?? input?.type ?? input?.direction ?? '').trim().toLowerCase();
        if (raw.includes('sell') || raw.includes('short') || raw === 's' || raw === '-') return 'sell';
        if (raw.includes('buy') || raw.includes('long') || raw === 'b' || raw === '+') return 'buy';
        const qty = Number(input?.qty ?? input?.quantity ?? input?.volume ?? input?.shares ?? 0);
        return Number.isFinite(qty) && qty < 0 ? 'sell' : 'buy';
    };

    const toQty = (input: any): number => {
        const qty = Number(input?.qty ?? input?.quantity ?? input?.volume ?? input?.shares ?? 0);
        return Number.isFinite(qty) && qty !== 0 ? Math.abs(qty) : 1;
    };

    const toPrice = (input: any): number => {
        const price = Number(input?.price ?? input?.avgPrice ?? input?.fillPrice ?? input?.entryPrice ?? input?.exitPrice ?? 0);
        return Number.isFinite(price) ? price : 0;
    };

    const mapExecutions = (list: any[], tradeLike: any) => {
        const sample = list[0] ?? tradeLike ?? {};
        const multiplierSymbol = String(sample?.symbol ?? tradeLike?.symbol ?? '');
        const multiplierAssetClass = String(sample?.assetClass ?? tradeLike?.assetClass ?? tradeLike?.market ?? '').toUpperCase();
        const multiplier = multiplierAssetClass.includes('FUT')
            ? getContractMultiplier(multiplierSymbol, 'FUTURES', sample?.instrument ?? tradeLike?.instrument)
            : 1;

        let resolvedRows: ReturnType<typeof resolveExecutionRows> = [];
        try {
            resolvedRows = resolveExecutionRows(list as any[], { multiplier });
        } catch {
            resolvedRows = [];
        }

        return list.map((x: any, index: number) => {
            const resolved = resolvedRows[index];
            const time = toChartTimeMs(x.time ?? x.tsUtcMs ?? x.timestamp ?? x.executedAt ?? x.date ?? x.openTimestampMs);
            const explicitCommissionAndFees = Number(x.commissionAndFees ?? x.commissionsAndFees);
            const commission = Number.isFinite(Number(x.commission)) ? Number(x.commission) : 0;
            const fees = Number.isFinite(Number(x.fees ?? x.fee)) ? Number(x.fees ?? x.fee) : 0;
            return {
                time,
                timestamp: time ?? undefined,
                openTimestampMs: time,
                price: toPrice(x),
                side: (resolved?.side ?? normalizeSide(x)) as 'buy' | 'sell',
                qty: toQty(x),
                volume: Number(x.volume ?? x.qty ?? x.quantity ?? x.shares),
                commission,
                fees,
                commissionAndFees: Number.isFinite(explicitCommissionAndFees) ? explicitCommissionAndFees : (commission + fees),
                rowRealizedNet: Number.isFinite(Number(x.rowRealizedNet)) ? Number(x.rowRealizedNet) : resolved?.rowRealizedNet,
                rowRealizedGross: Number.isFinite(Number(x.rowRealizedGross)) ? Number(x.rowRealizedGross) : resolved?.rowRealizedGross,
                rowFees: Number.isFinite(Number(x.rowFees)) ? Number(x.rowFees) : resolved?.rowFees,
                action: x.action ?? resolved?.rowAction,
                symbol: x.symbol ?? tradeLike?.symbol,
                assetClass: x.assetClass ?? tradeLike?.assetClass,
                instrument: x.instrument ?? tradeLike?.instrument,
                positionAfter: Number.isFinite(Number(x.positionAfter)) ? Number(x.positionAfter) : resolved?.runningPosition,
                closedQty: Number.isFinite(Number(x.closedQty)) ? Number(x.closedQty) : resolved?.rowResolvedQty,
                rowResolvedQty: Number.isFinite(Number(x.rowResolvedQty)) ? Number(x.rowResolvedQty) : resolved?.rowResolvedQty,
            };
        }).filter((x: any) => Number.isFinite(Number(x.time)) && Number(x.time) > 0);
    };

    // 1) prefer direct arrays on the trade object
    const direct = [trade?.executions, trade?.fills, trade?.executionsList, trade?.fillEvents]
        .map((value) => asArray(value))
        .find((value) => value != null && value.length > 0);

    if (direct) {
        return mapExecutions(direct, trade);
    }

    // 2) check localStorage aggregated row
    const row = getLocalTradeRow(trade?.id);
    const fromLocal = [row?.executions, row?.fills, row?.executionsList, row?.fillEvents]
        .map((value) => asArray(value))
        .find((value) => value != null && value.length > 0);

    if (fromLocal) {
        return mapExecutions(fromLocal, trade);
    }

    // 3) Display-only marker fallback for legacy trades. These markers never feed financial metrics.
    const entryTime = trade?.entryTime || trade?.openTime || trade?.entryTimestamp || trade?.timestamp; // Added timestamp fallback
    const exitTime = trade?.exitTime || trade?.closeTime || trade?.exitTimestamp;

    const entryPrice = trade?.entryPrice || trade?.avgEntryPrice || trade?.price; // Added price fallback
    const exitPrice = trade?.exitPrice || trade?.avgExitPrice;

    if (entryTime && entryPrice) {
        const sideStr = (trade?.side || trade?.direction || "").toUpperCase();
        const isLong = sideStr.includes("LONG") || sideStr.includes("BUY") || sideStr === 'L' || Number(trade.pnl) >= 0;

        const entry = {
            time: toChartTimeMs(entryTime),
            price: Number(entryPrice),
            side: isLong ? "buy" : "sell",
            qty: Math.abs(trade?.qty ?? trade?.size ?? trade?.volume ?? 1)
        };

        // Only create exit if we have valid exit data
        const exit = (exitTime && exitPrice) ? {
            time: toChartTimeMs(exitTime),
            price: Number(exitPrice),
            side: isLong ? "sell" : "buy",
            qty: Math.abs(trade?.qty ?? trade?.size ?? trade?.volume ?? 1)
        } : null;

        const normalized = (exit ? [entry, exit] : [entry])
            .filter((x) => Number.isFinite(Number(x.time)) && Number(x.time) > 0);

        return normalized;
    }

    return [];
}

// --- MAIN APP: Trade Details Page (wired from TradesPage/App.tsx) ---
// NOTE: This is intentionally a thin adapter so the rich UI in this file can be
// rendered as a page for a selected `Trade` from the main app.
export function TradeDetailsPage({ trade, onBack }: { trade: Trade | null; onBack: () => void }) {
    const displayTimeZone = useDisplayTimezone();
    const [editOpen, setEditOpen] = React.useState(false);
    const [editError, setEditError] = React.useState('');
    const [editAccounts, setEditAccounts] = React.useState<Array<{ id: string; accountName: string }>>([]);
    const [editAccountsLoading, setEditAccountsLoading] = React.useState(false);
    const [editedTrade, setEditedTrade] = React.useState<Trade | null>(null);
    const currentTrade = editedTrade && String((editedTrade as any).persistedTradeId || (editedTrade as any).storageId || (editedTrade as any).id || '')
        === String((trade as any)?.persistedTradeId || (trade as any)?.storageId || (trade as any)?.id || '') ? editedTrade : trade;
    const openTradeEditor = React.useCallback(() => {
        const row = currentTrade as any;
        if (!row || isDemoTrade(row) || !['manual', 'custom'].includes(String(row.sourceType || row.source || '').toLowerCase()) && !row.isCustom) {
            setEditError('Only manually entered trades can be edited here.');
            return;
        }
        if (!/^manual-[0-9a-f-]{36}$/i.test(String(row.persistedTradeId || ''))) {
            setEditError('This manual trade is not saved to the server and cannot be edited safely.');
            return;
        }
        if (Number(row.execution_count ?? row.executionCount ?? 0) > 2 || (Array.isArray(row.executions) && row.executions.length > 2)) {
            setEditError('This trade has multiple executions. Aggregate editing is disabled to preserve its execution history.');
            return;
        }
        setEditError('');
        setEditOpen(true);
        setEditAccountsLoading(true);
        void listCsvSourceAccounts().then((accounts) => {
            setEditAccounts(accounts.filter((account) => account.status === 'active').map(({ id, accountName }) => ({ id, accountName })));
        }).catch((error) => setEditError(error instanceof Error ? error.message : 'Trading accounts could not be loaded.'))
            .finally(() => setEditAccountsLoading(false));
    }, [currentTrade]);
    const initialEditValues = React.useMemo<Partial<ManualEntryPayload> | undefined>(() => {
        if (!currentTrade) return undefined;
        const row = currentTrade as any;
        const manual = row.manualEntry || {};
        const zone = manual.timeZone || displayTimeZone;
        const opened = Number(row.openTimestampMs);
        const closed = Number(row.closeTimestampMs);
        const openLocal = Number.isFinite(opened) && opened > 0 ? formatDateTimeInputInTimezone(opened, zone) : '';
        const closeLocal = Number.isFinite(closed) && closed > 0 ? formatDateTimeInputInTimezone(closed, zone) : '';
        return {
            accountId: row.sourceAccountId, date: manual.date || openLocal.slice(0, 10),
            time: manual.time || (manual.time === null ? '' : openLocal.slice(11)),
            exitDate: manual.exitDate || closeLocal.slice(0, 10),
            exitTime: manual.exitTime || (manual.exitTime === null ? '' : closeLocal.slice(11)),
            timeZone: zone, status: row.status === 'open' ? 'open' : 'closed',
            side: row.side === 'Short' ? 'Short' : 'Long', symbol: row.symbol,
            assetType: manual.assetType || 'stocks', quantity: Number(row.quantity ?? row.volume),
            entryPrice: row.entryPrice, exitPrice: row.exitPrice,
            grossPnl: row.grossPnl, grossPnlSource: manual.grossPnlSource || 'calculated',
            netPnl: row.netPnl ?? row.pnl, netPnlSource: manual.netPnlSource || 'calculated',
            commissions: manual.commissions ?? row.fees ?? 0, otherFees: manual.otherFees ?? 0,
            contractMultiplier: manual.contractMultiplier ?? undefined,
            optionRight: manual.option?.right, strike: manual.option?.strike,
            expiration: manual.option?.expiration, notes: row.notes || '', tags: row.tags || [],
        };
    }, [currentTrade, displayTimeZone]);
    const saveEditedTrade = React.useCallback(async (payload: ManualEntryPayload) => {
        const persistedId = String((currentTrade as any)?.persistedTradeId || '');
        const tradeId = /^manual-[0-9a-f-]{36}$/i.test(persistedId) ? persistedId : '';
        if (!tradeId) throw new Error('This manual trade is not saved to the server and cannot be edited safely.');
        if (!payload.accountId) throw new Error('Select a trading account.');
        await updateManualTrade(tradeId, { ...payload, accountId: payload.accountId });
        const refreshed = await loadPersistedCsvTrades({ forceRefresh: true });
        const updated = refreshed.find((item: any) => item.persistedTradeId === tradeId);
        if (!updated) throw new Error('The trade was saved, but its refreshed details could not be loaded. Refresh this page.');
        const matches = (row: any) => String(row?.persistedTradeId || '') === tradeId || String(row?.storageId || '') === `persisted-${tradeId}`;
        try {
            const local = loadImportedTradesAuth();
            if (local.some(matches)) saveImportedTradesAuth(local.map((row: any) => matches(row) ? { ...row, ...updated } : row));
        } catch {}
        try { removePrivateTradingValue('trades-cache'); } catch {}
        try { removePrivateTradingValue(`trade-note-html:${String((currentTrade as any)?.storageId || '')}`, 'session'); } catch {}
        setEditedTrade(updated as Trade);
        writePrivateTradingJson('last-view-trade', JSON.stringify(updated), 'session');
    }, [currentTrade]);
    const demoTradesOverride = useDemoTradesOverride();
    const demoTradesActive = Boolean(demoTradesOverride?.enabled && Array.isArray(demoTradesOverride.trades) && demoTradesOverride.trades.length > 0);
    const demoTrades = React.useMemo<Trade[]>(
        () => (demoTradesActive ? (demoTradesOverride?.trades as Trade[]) : []),
        [demoTradesActive, demoTradesOverride?.trades]
    );
    const selectedTradeIsDemo = isDemoTrade(trade);
    const recentTrades = React.useMemo(() => {
        if (demoTradesActive) return demoTrades.slice(0, 5);
        try {
            const raw = readPrivateTradingJson('trades-cache', 'local', ['tt_trades']);
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) return [];
            return parsed.slice(-5).reverse() as Trade[];
        } catch {
            return [];
        }
    }, [demoTrades, demoTradesActive]);

    const formatTradeLabel = React.useCallback((item: Trade): string => {
        const symbol = item.symbol || 'N/A';
        const ts = (item as any)?.openTimestampMs ?? (item as any)?.timestamp ?? (item as any)?.entryTime ?? (item as any)?.openTime ?? (item as any)?.date;
        const dateStr = ts ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: displayTimeZone }).format(new Date(ts)) : 'N/A';
        const pnl = typeof item.pnl === 'number' ? `${item.pnl > 0 ? '+' : ''}${item.pnl.toFixed(2)}` : '—';
        return `${symbol} • ${dateStr} • P/L: ${pnl}`;
    }, [displayTimeZone]);

    const missingTradeContent = !trade ? (
            <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
                <div className="max-w-2xl mx-auto">
                    <h1 className="text-2xl font-bold text-zinc-900 dark:text-white">Trade Details</h1>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-2">
                        Trade context was not found. This can happen if you refreshed the page or navigated directly.
                    </p>

                    {/* Recent Trades Recovery List */}
                    {recentTrades.length > 0 && (
                        <div className="mt-6">
                            <p className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">Recent Trades</p>
                            <div className="grid gap-2 mt-3">
                                {recentTrades.map((t, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        onClick={() => {
                                            if (typeof (window as any).__TT_viewTrade === 'function') {
                                                (window as any).__TT_viewTrade(t);
                                            }
                                        }}
                                        className="w-full text-left px-3 py-2 rounded-md border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors text-sm text-zinc-700 dark:text-zinc-300"
                                    >
                                        {formatTradeLabel(t)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Browse All Trades CTA */}
                    <div className="mt-6 flex gap-3">
                        <button
                            type="button"
                            onClick={onBack}
                            className="flex-1 px-4 h-9 rounded-md text-sm font-medium bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white transition-colors"
                        >
                            Browse All Trades
                        </button>
                    </div>
                </div>
            </div>
        ) : null;

    // Keep the existing TradeDetails layout intact; just focus calculations to the clicked trade's symbol.
    const initialDate = React.useMemo(() => {
        const source = (trade as any)?.openTimestampMs ?? (trade as any)?.timestamp ?? (trade as any)?.entryTime ?? (trade as any)?.openTime ?? (trade as any)?.date;
        const d = new Date(source as any);
        return Number.isFinite(d.getTime()) ? d : new Date();
    }, [trade]);
    const [currentDate, setCurrentDate] = React.useState<Date>(initialDate);

    const [tradeList, setTradeList] = React.useState<Trade[]>([]);
    const loadTradeList = React.useCallback(() => {
        if (demoTradesActive) {
            setTradeList(demoTrades);
            return;
        }
        try {
            const raw = readPrivateTradingJson('trades-cache', 'local', ['tt_trades']);
            if (!raw) {
                setTradeList([]);
                return;
            }
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                setTradeList(parsed as Trade[]);
            } else {
                setTradeList([]);
            }
        } catch {
            setTradeList([]);
        }
    }, [demoTrades, demoTradesActive]);
    const tradeIdKey = React.useMemo(() => String((trade as any)?.storageId ?? (trade as any)?.id ?? ''), [trade]);
    const [riskEdit, setRiskEdit] = React.useState<{ tradeKey: string; amount: number | null; method: InitialRiskMethod | null; stopPrice: number | null } | null>(null);
    const displayedTrade = React.useMemo(() => {
        if (!currentTrade || riskEdit?.tradeKey !== tradeIdKey) return currentTrade;
        return {
            ...currentTrade,
            initialRisk: riskEdit.amount,
            initialRiskAmount: riskEdit.amount,
            initial_risk: riskEdit.amount,
            riskAmount: riskEdit.amount,
            initialRiskUnset: riskEdit.amount == null,
            initialRiskMethod: riskEdit.method,
            initialStopPrice: riskEdit.stopPrice,
        } as Trade;
    }, [currentTrade, riskEdit, tradeIdKey]);
    const saveInitialRiskForDetails = React.useCallback(async (amount: number | null, method: InitialRiskMethod | null, stopPrice: number | null): Promise<void> => {
        const persistedId = String((currentTrade as any)?.persistedTradeId || '');
        if (!currentTrade || !persistedId || selectedTradeIsDemo) throw new Error('Initial Risk can only be saved for a server-backed trade.');
        await updateTradeInitialRisk(persistedId, { amount, method, stopPrice });
        setRiskEdit({ tradeKey: tradeIdKey, amount, method, stopPrice });
        setEditedTrade({
            ...currentTrade,
            initialRisk: amount,
            initialRiskAmount: amount,
            initial_risk: amount,
            riskAmount: amount,
            initialRiskUnset: amount == null,
            initialRiskMethod: method,
            initialStopPrice: stopPrice,
        } as Trade);
        void loadPersistedCsvTrades({ forceRefresh: true }).then((refreshed) => {
            const updated = refreshed.find((item: any) => item.persistedTradeId === persistedId);
            if (updated) setEditedTrade(updated as Trade);
        }).catch(() => undefined);
    }, [currentTrade, selectedTradeIsDemo, tradeIdKey]);
    React.useEffect(() => {
        loadTradeList();
    }, [loadTradeList, tradeIdKey]);
    const tradeInsightComparableTrades = React.useMemo(() => {
        if (!selectedTradeIsDemo) return tradeList;
        const demoTenantId = String((trade as any)?.demoTenantId ?? '').trim() === 'warrior-trading'
            ? 'warrior-trading'
            : 'trade-proof';
        const byId = new Map<string, Trade>();
        for (const candidate of [
            ...generateDashboardDemoTrades(demoTenantId, initialDate),
            ...demoTrades.filter((candidate: any) => String(candidate?.demoTenantId ?? demoTenantId) === demoTenantId),
        ] as Trade[]) {
            const key = String((candidate as any)?.id ?? (candidate as any)?.storageId ?? (candidate as any)?.tradeId ?? `${candidate.symbol}-${(candidate as any)?.timestamp}`);
            if (key) byId.set(key, candidate);
        }
        return Array.from(byId.values());
    }, [demoTrades, initialDate, selectedTradeIsDemo, trade, tradeList]);

    // `sideMenuActive` removed — previously used to detect sidebar presence but never read.

    // --- REPLACED LOGIC START ---
    // Use the new helpers to derive executions for the chart and execution table seeding
    const forcedExecutions = React.useMemo(() => {
        // cast to any to satisfy the complex return type expected by JournalEntryPage
        return deriveExecutionsForChart(trade) as { time: number; price: number; side: 'buy' | 'sell'; qty: number }[];
    }, [trade]);

    const chartExecutionSourceMessage = React.useMemo(() => {
        if (!trade) return null;

        const raw: any = trade;
        const directExecutions =
            asArray(raw?.executions) ||
            asArray(raw?.fills) ||
            asArray(raw?.executionsList) ||
            asArray(raw?.fillEvents);

        const localRow = getLocalTradeRow(raw?.id);
        const localExecutions =
            asArray(localRow?.executions) ||
            asArray(localRow?.fills) ||
            asArray(localRow?.executionsList) ||
            asArray(localRow?.fillEvents);

        if (directExecutions || localExecutions) return null;

        const hasFallbackMarkerInputs = Boolean(
            (raw?.entryTime || raw?.openTime || raw?.entryTimestamp || raw?.timestamp) &&
            (raw?.entryPrice || raw?.avgEntryPrice || raw?.price),
        );

        if (hasFallbackMarkerInputs) {
            return 'Chart markers are synthesized from entry/exit fields.';
        }

        return forcedExecutions.length ? null : 'No execution markers were available; the chart is seeded from the trade row.';
    }, [forcedExecutions.length, trade]);
    // --- REPLACED LOGIC END ---

    // Trade notes (HTML in editor, plain text persisted to trades list)
    const plainToHtml = React.useCallback((plain: string) => {
        const escapeHtml = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
        const trimmed = String(plain ?? '');
        if (!trimmed) return '';
        return `<p>${escapeHtml(trimmed).replace(/\n/g, '<br/>')}</p>`;
    }, []);
    const htmlToPlain = (html: string) => {
        try {
            const div = document.createElement('div');
            div.innerHTML = html;
            return (div.textContent || '').trimEnd();
        } catch {
            return String(html ?? '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trimEnd();
        }
    };

    const getLatestTradePlainNotes = React.useCallback((): string => {
        if (editedTrade && String((editedTrade as any).storageId ?? (editedTrade as any).id ?? '') === tradeIdKey) {
            return String((editedTrade as any).notes ?? '');
        }
        try {
            const imported = loadImportedTradesAuth();
            if (Array.isArray(imported) && imported.length > 0) {
                const found = imported.find((r: any) => String(r?.storageId ?? r?.id ?? '') === tradeIdKey);
                if (found) return String(found?.notes ?? '');
            }
        } catch {}
        return String((trade as any)?.notes ?? '');
    }, [tradeIdKey, trade, editedTrade]);

    const getLatestTradeNotesHtml = React.useCallback((): string => {
        if (!tradeIdKey) return plainToHtml(getLatestTradePlainNotes());
        try {
            const cachedHtml = readPrivateTradingJson(`trade-note-html:${tradeIdKey}`, 'session', [`tt_trade_notes_html_${tradeIdKey}`]);
            if (cachedHtml != null) return String(JSON.parse(cachedHtml));
        } catch {}
        return plainToHtml(getLatestTradePlainNotes());
    }, [tradeIdKey, getLatestTradePlainNotes, plainToHtml]);

    const [tradeNotesHtml, setTradeNotesHtml] = React.useState<string>(() => {
        return getLatestTradeNotesHtml();
    });

    // When the user navigates to a different trade, refresh editor state.
    React.useEffect(() => {
        setTradeNotesHtml(getLatestTradeNotesHtml());
    }, [tradeIdKey, getLatestTradeNotesHtml]);

    const saveTimerRef = React.useRef<number | null>(null);
    const persistTradeNotes = React.useCallback((html: string) => {
        if (!tradeIdKey) return;
        const plain = htmlToPlain(html);

        if (selectedTradeIsDemo) {
            try {
                writePrivateTradingJson(`trade-note-html:${tradeIdKey}`, JSON.stringify(html), 'session');
                const rawLast = readPrivateTradingJson('last-view-trade', 'session', ['tt_last_view_trade']);
                if (rawLast) {
                    const parsed = JSON.parse(rawLast);
                    if (parsed && (String(parsed.id) === tradeIdKey || String(parsed.storageId) === tradeIdKey)) {
                        parsed.notes = plain;
                        writePrivateTradingJson('last-view-trade', JSON.stringify(parsed), 'session');
                    }
                }
            } catch {}
            return;
        }

        // persist plain notes to TradesPage UI cache (tt_trades)
        try {
            const raw = readPrivateTradingJson('trades-cache', 'local', ['tt_trades']);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    const next = parsed.map((t: any) => {
                        const matches = String(t?.id) === tradeIdKey || String(t?.storageId) === tradeIdKey;
                        return matches ? { ...t, notes: plain } : t;
                    });
                    writePrivateTradingJson('trades-cache', JSON.stringify(next));
                }
            }
        } catch {}

        // ALSO update the authoritative imported-trades storage so other components refresh immediately
        try {
            const imported = loadImportedTradesAuth();
            if (Array.isArray(imported) && imported.length > 0) {
                const updated = imported.map((r: any) => {
                    if (String(r?.id) === tradeIdKey || String(r?.storageId) === tradeIdKey) {
                        return { ...r, notes: plain };
                    }
                    return r;
                });
                saveImportedTradesAuth(updated);
                // clear UI cache so TradesPage will re-hydrate from authoritative storage
                try { removePrivateTradingValue('trades-cache'); } catch {}
            }
        } catch {}

        // keep fallback in sessionStorage for TradeDetails reloads
        try {
            writePrivateTradingJson(`trade-note-html:${tradeIdKey}`, JSON.stringify(html), 'session');
        } catch {}

        // update last-viewed trade snapshot
        try {
            const rawLast = readPrivateTradingJson('last-view-trade', 'session', ['tt_last_view_trade']);
            if (rawLast) {
                const parsed = JSON.parse(rawLast);
                if (parsed && (String(parsed.id) === tradeIdKey || String(parsed.storageId) === tradeIdKey)) {
                    parsed.notes = plain;
                    writePrivateTradingJson('last-view-trade', JSON.stringify(parsed), 'session');
                }
            }
        } catch {}
    }, [selectedTradeIsDemo, tradeIdKey]);

    const handleTradeNotesChange = React.useCallback((html: string) => {
        setTradeNotesHtml(html);
        try {
            if (tradeIdKey) writePrivateTradingJson(`trade-note-html:${tradeIdKey}`, JSON.stringify(html), 'session');
        } catch {}
        if (saveTimerRef.current) {
            window.clearTimeout(saveTimerRef.current);
        }
        saveTimerRef.current = window.setTimeout(() => {
            persistTradeNotes(html);
        }, 300);
    }, [persistTradeNotes, tradeIdKey]);

    React.useEffect(() => {
        return () => {
            if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
        };
    }, []);

    const normalizeTags = React.useCallback((value: unknown): string[] => {
        if (!Array.isArray(value)) return [];
        const cleaned = value
            .map(v => String(v ?? '').trim())
            .filter(Boolean);
        return Array.from(new Set(cleaned));
    }, []);

    const getLatestTradeTags = React.useCallback((): string[] => {
        if (editedTrade && String((editedTrade as any).storageId ?? (editedTrade as any).id ?? '') === tradeIdKey) {
            return normalizeTags((editedTrade as any).tags);
        }
        try {
            const imported = loadImportedTradesAuth();
            if (Array.isArray(imported) && imported.length > 0) {
                const found = imported.find((r: any) => String(r?.storageId ?? r?.id ?? '') === tradeIdKey);
                if (found) return normalizeTags(found?.tags);
            }
        } catch {}
        return normalizeTags((trade as any)?.tags);
    }, [tradeIdKey, trade, editedTrade, normalizeTags]);

    const [tradeTags, setTradeTags] = React.useState<string[]>(() => {
        return getLatestTradeTags();
    });

    // Refresh tags when trade changes
    React.useEffect(() => {
        setTradeTags(getLatestTradeTags());
    }, [tradeIdKey, getLatestTradeTags]);

    const tagsSaveTimerRef = React.useRef<number | null>(null);
    const persistTradeTags = React.useCallback((tags: string[]) => {
        if (!tradeIdKey) return;
        const normalized = normalizeTags(tags);

        if (selectedTradeIsDemo) {
            try {
                writePrivateTradingJson(`trade-tags:${tradeIdKey}`, JSON.stringify(normalized), 'session');
                const rawLast = readPrivateTradingJson('last-view-trade', 'session', ['tt_last_view_trade']);
                if (rawLast) {
                    const parsed = JSON.parse(rawLast);
                    if (parsed && (String(parsed.id) === tradeIdKey || String(parsed.storageId) === tradeIdKey)) {
                        parsed.tags = normalized;
                        writePrivateTradingJson('last-view-trade', JSON.stringify(parsed), 'session');
                    }
                }
            } catch {}
            return;
        }

        try {
            const raw = readPrivateTradingJson('trades-cache', 'local', ['tt_trades']);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    const next = parsed.map((t: any) => {
                        const matches = String(t?.id) === tradeIdKey || String(t?.storageId) === tradeIdKey;
                        if (!matches) return t;
                        return { ...t, tags: normalized };
                    });
                    writePrivateTradingJson('trades-cache', JSON.stringify(next));
                }
            }
        } catch {}

        try {
            const imported = loadImportedTradesAuth();
            if (Array.isArray(imported) && imported.length > 0) {
                const updated = imported.map((r: any) => {
                    if (String(r?.id) === tradeIdKey || String(r?.storageId) === tradeIdKey) {
                        return { ...r, tags: normalized };
                    }
                    return r;
                });
                saveImportedTradesAuth(updated);
                try { removePrivateTradingValue('trades-cache'); } catch {}
            }
        } catch {}

        try {
            writePrivateTradingJson(`trade-tags:${tradeIdKey}`, JSON.stringify(normalized), 'session');
        } catch {}

        try {
            const rawLast = readPrivateTradingJson('last-view-trade', 'session', ['tt_last_view_trade']);
            if (rawLast) {
                const parsed = JSON.parse(rawLast);
                if (parsed && (String(parsed.id) === tradeIdKey || String(parsed.storageId) === tradeIdKey)) {
                    parsed.tags = normalized;
                    writePrivateTradingJson('last-view-trade', JSON.stringify(parsed), 'session');
                }
            }
        } catch {}
    }, [selectedTradeIsDemo, tradeIdKey, normalizeTags]);

    const queuePersistTradeTags = React.useCallback((nextTags: string[]) => {
        if (tagsSaveTimerRef.current) {
            window.clearTimeout(tagsSaveTimerRef.current);
        }
        tagsSaveTimerRef.current = window.setTimeout(() => {
            persistTradeTags(nextTags);
        }, 300);
    }, [persistTradeTags]);

    const updateTradeTags = React.useCallback((updater: (prev: string[]) => string[]) => {
        setTradeTags(prev => {
            const next = normalizeTags(updater(prev));
            queuePersistTradeTags(next);
            return next;
        });
    }, [normalizeTags, queuePersistTradeTags]);

    const handleAddTradeTag = React.useCallback((tag: string) => {
        const cleaned = String(tag ?? '').trim();
        if (!cleaned) return;
        updateTradeTags(prev => (prev.includes(cleaned) ? prev : [...prev, cleaned]));
    }, [updateTradeTags]);

    const handleRemoveTradeTag = React.useCallback((tag: string) => {
        const cleaned = String(tag ?? '').trim();
        if (!cleaned) return;
        updateTradeTags(prev => prev.filter(t => t !== cleaned));
    }, [updateTradeTags]);

    React.useEffect(() => {
        return () => {
            if (tagsSaveTimerRef.current) window.clearTimeout(tagsSaveTimerRef.current);
        };
    }, []);

    // Seed the local TradeDetails data store so the full page can render the clicked trade.
    // IMPORTANT: seed *executions* (entry/exit) so the executions table + chart markers make sense.
    React.useEffect(() => {
        if (!trade) return;

        const raw: any = trade;
        const toNumber = (value: unknown) => {
            const n = typeof value === 'number' ? value : Number(value);
            return Number.isFinite(n) ? n : undefined;
        };

        const normalizeExecToImported = (exec: { time: number; price: number; side: 'buy' | 'sell'; qty: number }, i: number): ImportedTradeRecord => {
            const ts = exec.time;
            const qtyAbs = Math.abs(exec.qty || 1);
            const signedQty = exec.side === 'sell' ? -qtyAbs : qtyAbs;

            return {
                storageId: `tt-exec-${String(raw.id ?? raw.tradeId ?? raw.trade_id ?? ts)}-${i}`,
                importBatchId: 'tt-selected',
                importedAt: Date.now(),
                openTimestampMs: ts,
                closeTimestampMs: null,
                timestamp: ts,
                symbol: raw.symbol,
                // for the executions table logic in this file, volume sign matters
                volume: signedQty,
                shares: qtyAbs,
                side: exec.side === 'buy' ? 'Long' : 'Short',
                pnl: 0,
                fees: 0,
                commission: 0,
                durationMinutes: 0,
                notes: raw.notes || '',
            } as ImportedTradeRecord;
        };

        const seed = (forcedExecutions || [])
            .filter(e => Number.isFinite(e.time) && Number.isFinite(e.price) && e.price > 0)
            .sort((a, b) => a.time - b.time)
            .map(normalizeExecToImported);

        // If we only got 1 marker (fallback) or none, still seed the main trade row.
        // IMPORTANT: use the trade's own date/timestamp so dayTrades filtering works.
        const tradeTs = (() => {
            const t = Number(raw.openTimestampMs ?? raw.timestamp ?? raw.entryTime ?? raw.openTime ?? raw.createdAt ?? Date.now());
            return Number.isFinite(t) ? t : Date.now();
        })();

        saveImportedTrades(seed.length ? seed : [
            {
                storageId: `tt-${raw.id ?? Date.now()}`,
                importBatchId: 'tt-selected',
                importedAt: Date.now(),
                openTimestampMs: tradeTs,
                closeTimestampMs: normalizeTimestampToMs(raw.closeTimestampMs ?? raw.exitTime ?? raw.closeTime),
                timestamp: tradeTs,
                symbol: raw.symbol,
                volume: Number.isFinite(Number(raw.volume)) ? Number(raw.volume) : 0,
                shares: Number.isFinite(Number(raw.volume)) ? Number(raw.volume) : 0,
                side: raw.side || (Number(raw.pnl) >= 0 ? 'Long' : 'Short'),
                pnl: Number(raw.pnl) || 0,
                fees: toNumber(raw.fees) ?? 0,
                commission: toNumber(raw.commission) ?? 0,
                durationMinutes: toNumber(raw.durationMinutes) ?? toNumber(raw.duration) ?? 0,
                notes: raw.notes || '',
            } as ImportedTradeRecord,
        ], { append: false });
    }, [trade, forcedExecutions]);

    // If the user clicks a different trade row, refresh the day shown.
    const _currentTradeSymbol = trade?.symbol ?? null;
    const _currentTradeId = (trade as any)?.id ?? null;
    React.useEffect(() => {
        setCurrentDate(initialDate);
    }, [initialDate, _currentTradeSymbol, _currentTradeId]);

    const currentTradeIndex = React.useMemo(() => {
        if (!trade || tradeList.length === 0) return -1;
        const currentId = String((trade as any)?.id ?? '');
        let idx = -1;
        if (currentId) {
            idx = tradeList.findIndex((t: any) => String(t?.id ?? '') === currentId);
        }
        if (idx >= 0) return idx;

        // Fallback matching if ID is missing
        const curTs = Number((trade as any).openTimestampMs ?? (trade as any).timestamp ?? 0);
        const curSym = String((trade as any)?.symbol ?? '').toUpperCase();
        return tradeList.findIndex((t: any) => {
            const tSym = String(t?.symbol ?? '').toUpperCase();
            const tTs = Number((t as any).openTimestampMs ?? (t as any).timestamp ?? 0);
            return tSym === curSym && Number.isFinite(curTs) && Number.isFinite(tTs) && tTs === curTs;
        });
    }, [trade, tradeList]);

    const prevTrade = React.useMemo(() => {
        if (currentTradeIndex > 0) return tradeList[currentTradeIndex - 1];
        return null;
    }, [tradeList, currentTradeIndex]);

    const nextTrade = React.useMemo(() => {
        if (currentTradeIndex >= 0 && currentTradeIndex < tradeList.length - 1) return tradeList[currentTradeIndex + 1];
        return null;
    }, [tradeList, currentTradeIndex]);

    const navigateToTrade = React.useCallback((next: Trade) => {
        if (!next) return;
        try { writePrivateTradingJson('last-view-trade', JSON.stringify(next), 'session'); } catch {}
        try {
            const view = (window as any).__TT_viewTrade;
            if (typeof view === 'function') {
                view(next);
                return;
            }
        } catch {}
        try {
            const nav = (window as any).__TT_navigate;
            if (typeof nav === 'function') nav('Trade Details');
        } catch {}
    }, []);

    if (missingTradeContent) return missingTradeContent;

    return (
        <div className="relative w-full max-w-none min-h-screen min-w-0">
            <div className="w-full max-w-none min-w-0">
                <JournalEntryPage
                    date={currentDate}
                    onNavigateDate={setCurrentDate}
                    focusSymbol={trade?.symbol}
                    trade={displayedTrade}
                    onSaveInitialRisk={saveInitialRiskForDetails}
                    onEditTrade={openTradeEditor}
                    tradeNotes={{ value: tradeNotesHtml, onChange: handleTradeNotesChange }}
                    tradeTags={{ value: tradeTags, onAdd: handleAddTradeTag, onRemove: handleRemoveTradeTag }}
                    onNavigateTrade={navigateToTrade}
                    prevTrade={prevTrade}
                    nextTrade={nextTrade}
                    comparableTrades={tradeInsightComparableTrades}
                    forcedExecutions={forcedExecutions}
                    chartExecutionSourceMessage={chartExecutionSourceMessage}
                />
                {editError && !editOpen && <p role="alert" className="mx-5 mt-3 text-sm text-red-400">{editError}</p>}
                {editOpen && <CreateCustomEntryModal
                    open title="Edit Trade" onClose={() => setEditOpen(false)} onSave={saveEditedTrade}
                    initialValues={initialEditValues} requireAccount={/^manual-[0-9a-f-]{36}$/i.test(String((currentTrade as any)?.persistedTradeId || ''))}
                    accounts={editAccounts} accountsLoading={editAccountsLoading} accountsError={editError}
                    timeZone={initialEditValues?.timeZone}
                />}
            </div>
        </div>
    );
}

export default TradeDetailsPage;
